/**
 * Cliente de la API — mismo origen (`OD-0026 D-06`).
 *
 * La API vive en `/api/v1/**` del MISMO origen que esta aplicación, que es lo
 * que hace funcionar su protección CSRF: `Origin` exacto, `Content-Type:
 * application/json` y `Sec-Fetch-Site: same-origin`. Por eso no hay ninguna
 * URL de API configurable: apuntar a otro host rompería esa protección.
 *
 * La cookie de sesión es `__Host-...`, `HttpOnly` y `SameSite=Strict`: este
 * código NUNCA la lee ni la escribe. Sólo manda `credentials: "same-origin"`.
 *
 * `ADR-0004`: nada de esto se cachea ni se guarda en el dispositivo.
 */

/**
 * Estado HTTP de `VALIDATION_FAILED` (`apps/api/.../error-handler.ts`): 422, no
 * 400. Las pantallas comparaban con 400 y los rechazos de la API (contraseña
 * corta, enlace no válido) acababan en el error genérico. Se nombra aquí para
 * que no vuelva a escribirse un número a mano.
 */
export const ESTADO_VALIDACION = 422;

export type ErrorApi = {
  readonly code: string;
  readonly message: string;
  readonly action: string | null;
  readonly requestId: string;
  /** Motivo legible por máquina (p. ej. MFA_REQUIRED) y errores por campo. */
  readonly motivo: string | null;
  readonly campos: Readonly<Record<string, string>>;
  /** Rechazos por línea de documento: `lineas["0"].quantity` (TDS-CORE Parte 2 §2.3). */
  readonly lineas: Readonly<Record<string, Readonly<Record<string, string>>>>;
  /** Plazas en un QUOTA_EXCEEDED, o id del cliente existente en un duplicado. */
  readonly detalles: Readonly<Record<string, unknown>>;
};

export type Sesion = {
  readonly organizationId: string;
  readonly userId: string;
  /**
   * El rol en la empresa activa. Solo pinta: la franja «Estás en … como
   * asesoría» (`OD-0063` D-05). Lo que se puede hacer lo dicen las
   * capabilities, y lo vuelve a decidir la API en cada petición.
   */
  readonly rol: string | null;
  readonly capabilities: readonly string[];
  readonly requestId: string;
};

export type RespuestaLogin =
  | { readonly tipo: "AUTENTICADO" }
  | { readonly tipo: "MFA"; readonly challenge: string; readonly expiresAt: string };

export type Resultado<T> =
  | { readonly ok: true; readonly datos: T }
  | { readonly ok: false; readonly estado: number; readonly error: ErrorApi | null };

/** `null` si el cuerpo no es JSON: un HTML de error no debe romper la pantalla. */
async function cuerpoJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function comoError(valor: unknown): ErrorApi | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.code !== "string" || typeof v.message !== "string") return null;
  const details = typeof v.details === "object" && v.details !== null ? (v.details as Record<string, unknown>) : {};
  const campos: Record<string, string> = {};
  if (typeof details.fields === "object" && details.fields !== null) {
    for (const [k, val] of Object.entries(details.fields as Record<string, unknown>)) {
      if (typeof val === "string") campos[k] = val;
    }
  }
  const lineas: Record<string, Record<string, string>> = {};
  if (typeof details.lines === "object" && details.lines !== null) {
    for (const [i, campos] of Object.entries(details.lines as Record<string, unknown>)) {
      if (typeof campos !== "object" || campos === null) continue;
      const porCampo: Record<string, string> = {};
      for (const [k, val] of Object.entries(campos as Record<string, unknown>)) {
        if (typeof val === "string") porCampo[k] = val;
      }
      lineas[i] = porCampo;
    }
  }
  return {
    code: v.code,
    message: v.message,
    action: typeof v.action === "string" ? v.action : null,
    requestId: typeof v.requestId === "string" ? v.requestId : "",
    motivo: typeof details.reason === "string" ? details.reason : null,
    campos,
    lineas,
    detalles: details,
  };
}

/** `URS §106`: 500 KB. Se avisa aquí ANTES de subir, y la API lo vuelve a comprobar. */
export const LOGO_MAX_BYTES = 500 * 1024;

export type Logo = {
  readonly fileId: string;
  /** `OD-0044`: `null` = todavía sin comprobar, que no es lo mismo que `OK`. */
  readonly integridad: "OK" | "HASH_DISTINTO" | "NO_ESTA" | null;
  readonly originalName: string;
  readonly contentType: string;
  /** Ya escrito por la API («488 KB»): la web no calcula. */
  readonly tamanoLegible: string;
  readonly uploadedAt: string;
};

export type EstadoLogo = {
  readonly logo: Logo | null;
  readonly incluidoEnElPlan: boolean;
  /** `false` cuando el despliegue no tiene almacén: no se ofrece subir nada. */
  readonly disponible: boolean;
  readonly espacioLibre: number;
};

function comoLogo(v: unknown): Logo | null {
  if (typeof v !== "object" || v === null) return null;
  const l = v as Record<string, unknown>;
  if (typeof l.fileId !== "string" || typeof l.contentType !== "string") return null;
  const integridad = l.integridad;
  return {
    fileId: l.fileId,
    integridad:
      integridad === "OK" || integridad === "HASH_DISTINTO" || integridad === "NO_ESTA" ? integridad : null,
    originalName: typeof l.originalName === "string" ? l.originalName : "",
    contentType: l.contentType,
    tamanoLegible: typeof l.tamanoLegible === "string" ? l.tamanoLegible : "",
    uploadedAt: typeof l.uploadedAt === "string" ? l.uploadedAt : "",
  };
}

/** `logo: null` es un resultado válido —no hay logotipo—, distinto de un error. */
function comoEstadoLogo(cuerpo: unknown): EstadoLogo | null {
  if (typeof cuerpo !== "object" || cuerpo === null || !("logo" in cuerpo)) return null;
  const v = cuerpo as Record<string, unknown>;
  return {
    logo: comoLogo(v.logo),
    incluidoEnElPlan: v.incluidoEnElPlan === true,
    // Falla cerrado: si la API no lo afirma, no se ofrece subir.
    disponible: v.disponible === true,
    espacioLibre: typeof v.espacioLibre === "number" ? v.espacioLibre : 0,
  };
}

/** `OD-0036`: la exportación para la gestoría. Lo que la pantalla necesita antes de ofrecerla. */
export type EstadoExportacion = {
  readonly incluidoEnElPlan: boolean;
  readonly maxFacturasPorZip: number;
  readonly diasMaximosDeRango: number;
  /** Los trimestres que se ofrecen, ya calculados por la API: la web no decide fechas fiscales. */
  readonly trimestres: readonly string[];
  /** `OD-0054 D-15`: si el rol ve compras. Sin él, no se ofrece el listado de recibidas. */
  readonly veCompras: boolean;
  /**
   * `OD-0063` D-10e: las cotas del paquete de recibidas con adjuntos, o `null`
   * si la API no las da (una API anterior): entonces no se ofrece el paquete.
   */
  readonly paqueteDeRecibidas: { readonly maxCompras: number; readonly maxMb: number } | null;
  /** `OD-0063` D-10f: si se ofrecen los libros para ContaSOL. Falla cerrado. */
  readonly contasol: boolean;
};

/** `OD-0063` D-10f: lo que la gestoría elige para las cuentas de ContaSOL. La API lo valida. */
export type OpcionesContasol = {
  readonly digitos: string;
  readonly prefijoClientes: string;
  readonly prefijoProveedores: string;
  readonly primerCodigo: string;
};

function comoEstadoExportacion(cuerpo: unknown): EstadoExportacion | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  if (typeof v.maxFacturasPorZip !== "number" || typeof v.diasMaximosDeRango !== "number" || !Array.isArray(v.trimestres)) return null;
  return {
    // Falla cerrado: si la API no lo afirma, la exportación no se ofrece.
    incluidoEnElPlan: v.incluidoEnElPlan === true,
    maxFacturasPorZip: v.maxFacturasPorZip,
    diasMaximosDeRango: v.diasMaximosDeRango,
    trimestres: v.trimestres.filter((x): x is string => typeof x === "string"),
    // Falla cerrado: una API que no lo dice no ofrece las compras.
    veCompras: v.veCompras === true,
    paqueteDeRecibidas:
      typeof v.maxComprasPorZip === "number" && typeof v.maxMbDeAdjuntosPorZip === "number"
        ? { maxCompras: v.maxComprasPorZip, maxMb: v.maxMbDeAdjuntosPorZip }
        : null,
    contasol: v.contasol === true,
  };
}

export type PeriodoExportacion = { readonly trimestre: string } | { readonly desde: string; readonly hasta: string };

/** Los tres formatos del listado (`OD-0036`, revisión 2026-10-18). El ZIP lleva dentro el elegido. */
export type FormatoListado = "xlsx" | "csv" | "aeat";

/** `OD-0054-ENMIENDA-01`: las recibidas, en Excel o CSV. El libro de la AEAT de recibidas aún no existe (D-14). */
export type FormatoRecibidas = "xlsx" | "csv";

const RUTA_DEL_LISTADO: Readonly<Record<FormatoListado, string>> = {
  csv: "/api/v1/accountant-export/facturas.csv",
  xlsx: "/api/v1/accountant-export/facturas.xlsx",
  aeat: "/api/v1/accountant-export/libro-expedidas.xlsx",
};

/**
 * El nombre que puso la API: el completo de `filename*` (RFC 6266), que es el
 * que lleva acentos —el libro de la AEAT lleva la razón social—, o el ASCII de
 * `filename` si no viene.
 */
function nombreDeDescarga(cabecera: string | null, respaldo: string): string {
  const completo = (cabecera ?? "").match(/filename\*=UTF-8''([^;]+)/)?.[1];
  if (completo !== undefined) {
    try {
      return decodeURIComponent(completo);
    } catch {
      // Mal codificado: se cae al ASCII, que siempre viene.
    }
  }
  return (cabecera ?? "").match(/filename="([^"]+)"/)?.[1] ?? respaldo;
}

/** Un fichero descargado: los bytes y el nombre que puso la API. */
export type FicheroDescargado = { readonly contenido: Blob; readonly nombre: string };

/**
 * El nombre del fichero, limpio para viajar en una cabecera HTTP.
 *
 * Una cabecera no admite saltos de línea ni bytes fuera de ASCII: un nombre con
 * un acento o, peor, con un `\r\n` dentro, es una petición malformada o una
 * inyección de cabeceras. Se queda lo imprimible y punto; el nombre es una
 * comodidad para que el usuario reconozca su fichero, no un identificador.
 */
function nombreSeguro(nombre: string): string {
  const limpio = [...nombre].filter((ch) => ch >= " " && ch <= "~").join("");
  return (limpio === "" ? "logotipo" : limpio).slice(0, 200);
}

async function pedir<T>(
  ruta: string,
  init: RequestInit,
  interpretar: (cuerpo: unknown) => T | null,
): Promise<Resultado<T>> {
  let res: Response;
  try {
    res = await fetch(ruta, {
      ...init,
      credentials: "same-origin",
      headers: { "content-type": "application/json", accept: "application/json", ...init.headers },
    });
  } catch {
    // Sin red, o la petición no llegó a salir.
    return { ok: false, estado: 0, error: null };
  }

  const cuerpo = await cuerpoJson(res);
  if (!res.ok) return { ok: false, estado: res.status, error: comoError(cuerpo) };

  const datos = interpretar(cuerpo);
  if (datos === null) return { ok: false, estado: res.status, error: null };
  return { ok: true, datos };
}

function comoSesion(cuerpo: unknown): Sesion | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  if (typeof v.organizationId !== "string" || typeof v.userId !== "string") return null;
  const caps = Array.isArray(v.capabilities) ? v.capabilities.filter((c): c is string => typeof c === "string") : [];
  return {
    organizationId: v.organizationId,
    userId: v.userId,
    rol: typeof v.role === "string" ? v.role : null,
    capabilities: caps,
    requestId: typeof v.requestId === "string" ? v.requestId : "",
  };
}

function comoLogin(cuerpo: unknown): RespuestaLogin | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  if (v.status === "AUTHENTICATED") return { tipo: "AUTENTICADO" };
  if (v.status === "MFA_REQUIRED" && typeof v.challenge === "string") {
    return { tipo: "MFA", challenge: v.challenge, expiresAt: typeof v.expiresAt === "string" ? v.expiresAt : "" };
  }
  return null;
}

/** Éxito sólo si el cuerpo trae EXACTAMENTE el estado esperado: un 200 con otra cosa no se da por bueno. */
function estadoEs(esperado: string): (cuerpo: unknown) => true | null {
  return (cuerpo) =>
    typeof cuerpo === "object" && cuerpo !== null && (cuerpo as Record<string, unknown>).status === esperado
      ? true
      : null;
}

export type EstadoMfa = {
  readonly totp: "NONE" | "PENDING" | "ACTIVE";
  readonly codigosRestantes: number;
};

function comoEstadoMfa(cuerpo: unknown): EstadoMfa | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  if (v.totp !== "NONE" && v.totp !== "PENDING" && v.totp !== "ACTIVE") return null;
  return { totp: v.totp, codigosRestantes: typeof v.recoveryCodesRemaining === "number" ? v.recoveryCodesRemaining : 0 };
}

function comoAltaMfa(cuerpo: unknown): { readonly secreto: string; readonly uri: string } | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  return typeof v.secret === "string" && typeof v.uri === "string" && v.uri.startsWith("otpauth://")
    ? { secreto: v.secret, uri: v.uri }
    : null;
}

function comoCodigos(cuerpo: unknown): readonly string[] | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  if (v.status !== "ACTIVE" || !Array.isArray(v.recoveryCodes)) return null;
  const codigos = v.recoveryCodes.filter((c): c is string => typeof c === "string");
  return codigos.length === v.recoveryCodes.length ? codigos : null;
}

export type TipoIdEmisor = "NIF_ES_PERSON" | "NIE_ES" | "NIF_ES_ENTITY";

export type Emisor = {
  readonly legalName: string;
  readonly taxIdType: TipoIdEmisor;
  readonly taxIdValue: string;
  readonly taxIdNormalized: string;
  readonly taxIdControlWarning: boolean;
  readonly addressLine: string;
  readonly postalCode: string;
  readonly city: string;
  readonly province: string;
  readonly activityNature: NaturalezaActividad;
  /** OD-0035: null = sin responder. Sin responder no se puede emitir. */
  readonly siiStatus: EstadoSii;
  /** OD-0063 D-10c: la actividad del libro registro. Los tres null = sin indicar. */
  readonly iaeActivityType: CodigoActividadIae | null;
  readonly iaeActivitySubtype: string | null;
  readonly iaeEpigraph: string | null;
  // Datos que salen en los documentos (no fiscales: no piden reconfirmar).
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly paymentMethod: FormaPago | null;
  /** Normalizado por la API: "ES9121000418450200051332". */
  readonly iban: string | null;
  readonly documentFooter: string | null;
  readonly confirmedAt: string | null;
  /** DC-02 A: ya hay facturas emitidas con este NIF; no se puede cambiar. */
  readonly taxIdLocked: boolean;
};

/** Q-04 (RRSIF art. 13): declaración responsable firmada de una versión. */
export type DeclaracionSif = {
  readonly version: string;
  readonly signedAt: string;
  readonly signedPlace: string;
  readonly url: string;
};

/** "Acerca de": sistema, productor, versión y declaraciones (GET /api/v1/sif, público). */
export type AcercaDeSif = {
  readonly productor: string;
  readonly nifProductor: string | null;
  readonly sistema: string;
  readonly idSistema: string;
  readonly version: string | null;
  readonly declaracion: DeclaracionSif | null;
  readonly declaraciones: readonly DeclaracionSif[];
};

function comoDeclaracion(valor: unknown): DeclaracionSif | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.version !== "string" || typeof v.signedAt !== "string" || typeof v.signedPlace !== "string") return null;
  // Sólo enlaces https: la URL la pinta la pantalla.
  if (typeof v.url !== "string" || !v.url.startsWith("https://")) return null;
  return { version: v.version, signedAt: v.signedAt, signedPlace: v.signedPlace, url: v.url };
}

function comoAcercaDe(cuerpo: unknown): AcercaDeSif | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = cuerpo as Record<string, unknown>;
  const productor = v.producer as Record<string, unknown> | undefined;
  const sistema = v.system as Record<string, unknown> | undefined;
  if (typeof productor?.name !== "string" || typeof sistema?.name !== "string" || typeof sistema.id !== "string") return null;
  const lista = Array.isArray(v.declarations) ? v.declarations.map(comoDeclaracion) : [];
  return {
    productor: productor.name,
    nifProductor: typeof productor.nif === "string" ? productor.nif : null,
    sistema: sistema.name,
    idSistema: sistema.id,
    version: typeof sistema.version === "string" ? sistema.version : null,
    declaracion: comoDeclaracion(v.declaration),
    declaraciones: lista.filter((d): d is DeclaracionSif => d !== null),
  };
}

export type FormaPago = "TRANSFERENCIA" | "DOMICILIACION" | "TARJETA" | "EFECTIVO" | "BIZUM" | "OTRO";
const FORMAS_PAGO: readonly string[] = ["TRANSFERENCIA", "DOMICILIACION", "TARJETA", "EFECTIVO", "BIZUM", "OTRO"];

/** TDS-CORE Parte 4 §3.1: lo minimo que ve el operador de cada pendiente. */
export type PendienteOperador = {
  readonly organizationId: string;
  readonly legalEntityId: string;
  readonly legalName: string;
  readonly taxId: string;
  readonly route: string | null;
  readonly declaredAt: string | null;
};

function comoPendientes(cuerpo: unknown): readonly PendienteOperador[] | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const lista = (cuerpo as Record<string, unknown>).pending;
  if (!Array.isArray(lista)) return null;
  const salida: PendienteOperador[] = [];
  for (const x of lista) {
    if (typeof x !== "object" || x === null) return null;
    const v = x as Record<string, unknown>;
    if (typeof v.organizationId !== "string" || typeof v.legalEntityId !== "string" || typeof v.legalName !== "string") return null;
    salida.push({
      organizationId: v.organizationId,
      legalEntityId: v.legalEntityId,
      legalName: v.legalName,
      taxId: typeof v.taxId === "string" ? v.taxId : "",
      route: typeof v.route === "string" ? v.route : null,
      declaredAt: typeof v.declaredAt === "string" ? v.declaredAt : null,
    });
  }
  return salida;
}

/** TDS-CORE Parte 4 §4: lo que decide si se puede emitir. */
export type EstadoAutorizacion =
  | "NOT_STARTED"
  | "PENDING_SIGNATURE"
  | "PENDING_VALIDATION"
  | "ACTIVE"
  | "REJECTED"
  | "REVOKED"
  | "NIF_CHANGED";

export type MotivoRechazoAutorizacion =
  | "NOT_FOUND"
  | "GRANTOR_MISMATCH"
  | "WRONG_PROCEDURE"
  | "WRONG_REPRESENTATIVE"
  | "NOT_IN_FORCE"
  | "OTHER";

export type Autorizacion = {
  readonly estado: EstadoAutorizacion;
  readonly validadaEl: string | null;
  readonly motivoRechazo: MotivoRechazoAutorizacion | null;
  /** A quien se apodera en la Sede: el fabricante (TDS-CORE P4 §1). */
  readonly apoderado: { readonly nombre: string; readonly nif: string };
  /**
   * `false` mientras la empresa no tenga datos fiscales. Sin ellos NO se puede
   * autorizar —no hay a quién representar— y la API responde
   * `LEGAL_ENTITY_MISSING`. La puerta lo mira ANTES de ofrecer los botones.
   */
  readonly datosFiscalesListos: boolean;
};

const ESTADOS_AUTORIZACION: readonly string[] = [
  "NOT_STARTED", "PENDING_SIGNATURE", "PENDING_VALIDATION", "ACTIVE", "REJECTED", "REVOKED", "NIF_CHANGED",
];
const MOTIVOS_RECHAZO: readonly string[] = [
  "NOT_FOUND", "GRANTOR_MISMATCH", "WRONG_PROCEDURE", "WRONG_REPRESENTATIVE", "NOT_IN_FORCE", "OTHER",
];

function comoAutorizacion(cuerpo: unknown): Autorizacion | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const r = (cuerpo as Record<string, unknown>).representation as Record<string, unknown> | undefined;
  if (r === undefined || typeof r.state !== "string" || !ESTADOS_AUTORIZACION.includes(r.state)) return null;
  const rep = r.representative as Record<string, unknown> | undefined;
  if (typeof rep?.name !== "string" || typeof rep.taxId !== "string") return null;
  return {
    estado: r.state as EstadoAutorizacion,
    validadaEl: typeof r.validatedAt === "string" ? r.validatedAt : null,
    motivoRechazo:
      typeof r.rejectionReason === "string" && MOTIVOS_RECHAZO.includes(r.rejectionReason)
        ? (r.rejectionReason as MotivoRechazoAutorizacion)
        : null,
    apoderado: { nombre: rep.name, nif: rep.taxId },
    // Si la API no lo dice (versión anterior), se da por bueno: el fallo se
    // vería al pulsar, como antes, en vez de bloquear una cuenta que sí puede.
    datosFiscalesListos: r.issuerReady !== false,
  };
}

/** OD-0035 A: ¿lleva los libros de IVA en el SII? Dato declarado. */
export type EstadoSii = "NOT_SII" | "SII" | null;

/** OD-0063 D-10c: el «Código» de actividad del libro registro de la AEAT (AEAT-REF-027, validación 16). */
export type CodigoActividadIae = "A" | "B" | "C" | "D";

/** OD-0029 D-03: dato declarado que solo alimenta la SUGERENCIA de retencion. */
export type NaturalezaActividad = "PROFESIONAL" | "EMPRESARIAL" | "NO_INDICADA";

export type DatosEmisor = Pick<
  Emisor,
  | "legalName"
  | "taxIdType"
  | "taxIdValue"
  | "addressLine"
  | "postalCode"
  | "city"
  | "province"
  | "activityNature"
  | "siiStatus"
  | "iaeActivityType"
  | "iaeActivitySubtype"
  | "iaeEpigraph"
  | "contactEmail"
  | "contactPhone"
  | "paymentMethod"
  | "iban"
  | "documentFooter"
>;

function comoEmisor(valor: unknown): Emisor | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  const textos = ["legalName", "taxIdValue", "taxIdNormalized", "addressLine", "postalCode", "city", "province"];
  if (!textos.every((k) => typeof v[k] === "string")) return null;
  if (v.taxIdType !== "NIF_ES_PERSON" && v.taxIdType !== "NIE_ES" && v.taxIdType !== "NIF_ES_ENTITY") return null;
  return {
    legalName: v.legalName as string,
    taxIdType: v.taxIdType,
    taxIdValue: v.taxIdValue as string,
    taxIdNormalized: v.taxIdNormalized as string,
    taxIdControlWarning: v.taxIdControlWarning === true,
    addressLine: v.addressLine as string,
    postalCode: v.postalCode as string,
    city: v.city as string,
    province: v.province as string,
    activityNature:
      v.activityNature === "PROFESIONAL" || v.activityNature === "EMPRESARIAL" ? v.activityNature : "NO_INDICADA",
    siiStatus: v.siiStatus === "SII" || v.siiStatus === "NOT_SII" ? v.siiStatus : null,
    iaeActivityType:
      v.iaeActivityType === "A" || v.iaeActivityType === "B" || v.iaeActivityType === "C" || v.iaeActivityType === "D"
        ? v.iaeActivityType
        : null,
    iaeActivitySubtype: typeof v.iaeActivitySubtype === "string" ? v.iaeActivitySubtype : null,
    iaeEpigraph: typeof v.iaeEpigraph === "string" ? v.iaeEpigraph : null,
    contactEmail: typeof v.contactEmail === "string" ? v.contactEmail : null,
    contactPhone: typeof v.contactPhone === "string" ? v.contactPhone : null,
    paymentMethod: typeof v.paymentMethod === "string" && FORMAS_PAGO.includes(v.paymentMethod) ? (v.paymentMethod as FormaPago) : null,
    iban: typeof v.iban === "string" ? v.iban : null,
    documentFooter: typeof v.documentFooter === "string" ? v.documentFooter : null,
    confirmedAt: typeof v.confirmedAt === "string" ? v.confirmedAt : null,
    taxIdLocked: v.taxIdLocked === true,
  };
}

/** `{ legalEntity: null }` es un resultado válido (todavía no hay emisor), distinto de un error. */
function comoRespuestaEmisor(cuerpo: unknown): { readonly emisor: Emisor | null } | null {
  if (typeof cuerpo !== "object" || cuerpo === null || !("legalEntity" in cuerpo)) return null;
  const le = (cuerpo as Record<string, unknown>).legalEntity;
  if (le === null) return { emisor: null };
  const e = comoEmisor(le);
  return e === null ? null : { emisor: e };
}

export type TipoId = "NIF_ES_PERSON" | "NIE_ES" | "NIF_ES_ENTITY" | "VAT_EU" | "FOREIGN_ID" | "OTHER";

export type Cliente = {
  readonly id: string;
  readonly displayName: string;
  readonly legalName: string | null;
  readonly taxIdType: TipoId | null;
  readonly taxIdValue: string | null;
  readonly taxIdNormalized: string | null;
  readonly taxIdControlWarning: boolean;
  readonly country: string;
  readonly addressLine: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
  readonly province: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly notes: string | null;
  readonly status: "ACTIVE" | "ARCHIVED";
  readonly archiveReason: string | null;
  readonly deletable: boolean;
  /** OD-0029 D-03: pagador obligado a retener (art. 76 RIRPF), declarado. */
  readonly withholdingObligation: ObligacionRetener;
  /**
   * `OD-0062` E-03: días que suele tardar en pagar, de 0 (al contado) a 365.
   * `null` = sin plazo: el vencimiento propuesto sigue la regla de `D-07`.
   */
  readonly paymentTermsDays: number | null;
};

export type ObligacionRetener = "SI" | "NO" | "DESCONOCIDO";

/** Lo que se envía al crear o editar. Nunca estado ni motivo de archivo (TDS-CORE R-C5). */
export type DatosCliente = Omit<Cliente, "id" | "taxIdNormalized" | "taxIdControlWarning" | "status" | "archiveReason" | "deletable" | "paymentTermsDays"> & {
  /** `OD-0062` E-03: lo escrito en «Otro» va como texto y lo convierte la API. */
  readonly paymentTermsDays: number | string | null;
};

export type Plazas = {
  /** `null` = sin límite numérico (`RC-0009`). */
  readonly total: number | null;
  readonly used: number;
  readonly available: number | null;
  readonly releasable: number;
};

const TEXTOS_CLIENTE = ["legalName", "taxIdValue", "taxIdNormalized", "addressLine", "postalCode", "city", "province", "email", "phone", "notes"];

function comoCliente(valor: unknown): Cliente | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.id !== "string" || typeof v.displayName !== "string" || typeof v.country !== "string") return null;
  if (v.status !== "ACTIVE" && v.status !== "ARCHIVED") return null;
  if (!TEXTOS_CLIENTE.every((k) => v[k] === null || typeof v[k] === "string")) return null;
  const obligacion = v.withholdingObligation;
  return {
    ...(v as unknown as Cliente),
    withholdingObligation: obligacion === "SI" || obligacion === "NO" ? obligacion : "DESCONOCIDO",
    paymentTermsDays: typeof v.paymentTermsDays === "number" ? v.paymentTermsDays : null,
  };
}

function comoUnCliente(cuerpo: unknown): Cliente | null {
  return typeof cuerpo === "object" && cuerpo !== null ? comoCliente((cuerpo as Record<string, unknown>).customer) : null;
}

function comoListaClientes(cuerpo: unknown): readonly Cliente[] | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const lista = (cuerpo as Record<string, unknown>).customers;
  if (!Array.isArray(lista)) return null;
  const clientes = lista.map(comoCliente);
  return clientes.every((c) => c !== null) ? (clientes as Cliente[]) : null;
}

/** Una página de clientes y el cursor de la siguiente (`null` en la última). */
type PaginaClientes = { readonly clientes: readonly Cliente[]; readonly siguiente: string | null };

function comoPaginaClientes(cuerpo: unknown): PaginaClientes | null {
  const clientes = comoListaClientes(cuerpo);
  if (clientes === null) return null;
  const siguiente = (cuerpo as Record<string, unknown>).nextCursor;
  if (siguiente !== null && siguiente !== undefined && typeof siguiente !== "string") return null;
  return { clientes, siguiente: siguiente ?? null };
}

/**
 * Tope de páginas al listar clientes: 200 de 50 son 10.000 clientes. Existe
 * para que una API que devolviera siempre el mismo cursor no deje la pantalla
 * pidiendo para siempre, no como límite de producto.
 */
const MAX_PAGINAS_CLIENTES = 200;

function comoPlazas(cuerpo: unknown): Plazas | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const s = (cuerpo as Record<string, unknown>).slots as Record<string, unknown> | undefined;
  if (s === undefined || s === null) return null;
  // `total` y `available` pueden venir a `null` en un plan sin límite: eso NO
  // invalida la respuesta. Rechazarla entera dejaba la pantalla sin plazas, sin
  // avisos y sin nombre de plan, y en silencio.
  const obligatorios = ["used", "releasable"];
  const opcionales = ["total", "available"];
  const ok =
    obligatorios.every((k) => typeof s[k] === "number") &&
    opcionales.every((k) => typeof s[k] === "number" || s[k] === null);
  return ok ? (s as unknown as Plazas) : null;
}

/** TDS-CORE Parte 2 §1. Importes y porcentajes SIEMPRE como texto ("45,1234"): nunca `number`. */
export type TratamientoIva = "SUJETA_NO_EXENTA" | "SUJETA_EXENTA" | "NO_SUJETA";

export type Producto = {
  readonly id: string;
  readonly code: string;
  readonly description: string;
  readonly unitPrice: string;
  readonly vatTreatment: TratamientoIva;
  readonly vatRate: string | null;
  readonly vatCause: string | null;
  readonly withholdingDefault: boolean;
  readonly active: boolean;
};

export type DatosProducto = Omit<Producto, "id" | "active">;

export type CatalogoIva = {
  readonly id: string;
  readonly vatRates: readonly string[];
  readonly exemptionCauses: readonly string[];
  readonly nonSubjectCauses: readonly string[];
};

const TRATAMIENTOS_IVA: readonly string[] = ["SUJETA_NO_EXENTA", "SUJETA_EXENTA", "NO_SUJETA"];
const textoONulo = (x: unknown): boolean => x === null || typeof x === "string";

function comoProducto(valor: unknown): Producto | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.id !== "string" || typeof v.code !== "string" || typeof v.description !== "string") return null;
  if (typeof v.unitPrice !== "string" || !TRATAMIENTOS_IVA.includes(v.vatTreatment as string)) return null;
  if (!textoONulo(v.vatRate) || !textoONulo(v.vatCause)) return null;
  if (typeof v.withholdingDefault !== "boolean" || typeof v.active !== "boolean") return null;
  return v as unknown as Producto;
}

function comoUnProducto(cuerpo: unknown): Producto | null {
  return typeof cuerpo === "object" && cuerpo !== null ? comoProducto((cuerpo as Record<string, unknown>).product) : null;
}

function comoListaProductos(cuerpo: unknown): readonly Producto[] | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const lista = (cuerpo as Record<string, unknown>).products;
  if (!Array.isArray(lista)) return null;
  const productos = lista.map(comoProducto);
  return productos.every((p) => p !== null) ? (productos as Producto[]) : null;
}

function comoCatalogo(cuerpo: unknown): CatalogoIva | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const c = (cuerpo as Record<string, unknown>).catalog as Record<string, unknown> | null | undefined;
  if (c === null || c === undefined || typeof c.id !== "string") return null;
  const textos = (x: unknown): boolean => Array.isArray(x) && x.every((e) => typeof e === "string");
  return textos(c.vatRates) && textos(c.exemptionCauses) && textos(c.nonSubjectCauses) ? (c as unknown as CatalogoIva) : null;
}

/**
 * CRM V1 de Business — `OD-0048` sobre `RC-0016`. El importe estimado es
 * TEXTO ("3000,50") e informativo: no lleva IVA y esta web no lo suma; el total
 * de lo abierto lo da la API.
 */
export type EstadoOportunidad = "OPEN" | "WON" | "LOST";

/** Las siete etapas (`OD-0048-ENMIENDA-01`): cinco abiertas y las dos de cierre. */
export type EtapaAbierta = "IDENTIFIED" | "QUALIFIED" | "QUOTE_REQUESTED" | "QUOTE_SENT" | "QUOTE_ACCEPTED";
export type EtapaOportunidad = EtapaAbierta | "WON" | "LOST";
export const ETAPAS_ABIERTAS: readonly EtapaAbierta[] = ["IDENTIFIED", "QUALIFIED", "QUOTE_REQUESTED", "QUOTE_SENT", "QUOTE_ACCEPTED"];
const ETAPAS: readonly string[] = [...ETAPAS_ABIERTAS, "WON", "LOST"];

/** OD-0058 D-02: las cinco preguntas, en el orden de la ficha. */
export type PreguntaCualificacion = "budget" | "authority" | "timing" | "competition" | "trust";
export const PREGUNTAS_CUALIFICACION: readonly PreguntaCualificacion[] = ["budget", "authority", "timing", "competition", "trust"];
export type RespuestaCualificacion = "YES" | "NO" | "UNKNOWN";
export const RESPUESTAS_CUALIFICACION: readonly RespuestaCualificacion[] = ["YES", "NO", "UNKNOWN"];
export type Cualificacion = Readonly<Record<PreguntaCualificacion, RespuestaCualificacion | null>>;
/**
 * De dónde sale el porcentaje: el cálculo, la base de la etapa (sin cualificar,
 * `OD-0058-ENMIENDA-02` G-03), el usuario o el cierre (100 / 0).
 */
export type OrigenProbabilidad = "CALCULATED" | "STAGE" | "MANUAL" | "CLOSED";
const ORIGENES: readonly string[] = ["CALCULATED", "STAGE", "MANUAL", "CLOSED"];

export type Oportunidad = {
  readonly id: string;
  readonly title: string;
  readonly customer: { readonly id: string; readonly displayName: string; readonly provisional: boolean } | null;
  readonly estimatedAmount: string | null;
  readonly expectedCloseDate: string | null;
  readonly notes: string | null;
  readonly status: EstadoOportunidad;
  /** La etapa que se enseña; con presupuesto enlazado, la dice el presupuesto. */
  readonly stage: EtapaOportunidad;
  /** Adonde vuelve "Reabrir". */
  readonly openStage: EtapaAbierta;
  readonly quote: { readonly id: string; readonly status: string | null } | null;
  /** OD-0058. Una API anterior no la manda: queda todo sin responder. */
  readonly qualification: Cualificacion;
  /** Entero 0-100 que calcula la API. `null` solo si la API no lo manda. */
  readonly probability: number | null;
  readonly probabilitySource: OrigenProbabilidad | null;
  /** ENMIENDA-02 G-06: la base de la etapa de la que parte el cálculo. */
  readonly stageProbability: number | null;
  /** G-08: lo que da el cálculo sin el ajuste a mano; adonde vuelve «Volver al cálculo». */
  readonly calculatedProbability: number | null;
  /** G-02: de 0 a 100 sin mirar la etapa; `null` con menos de tres respuestas. */
  readonly qualificationLevel: number | null;
  /** G-02: la etapa a la que José propone pasarla. */
  readonly suggestedStage: "QUALIFIED" | null;
  /**
   * OD-0058-ENMIENDA-01 E-03 y ENMIENDA-02 G-07: la pregunta que más sube el
   * porcentaje, desde cuánto y hasta cuánto, calculado por la API. `null` si
   * ninguna lo sube o la API es anterior.
   */
  readonly lever: { readonly question: PreguntaCualificacion; readonly from: number | null; readonly probability: number } | null;
  /** OD-0048-ENMIENDA-02 F-01: por qué se perdió; `null` sin motivo o si no está perdida. */
  readonly lostReason: MotivoPerdida | null;
  readonly lostNote: string | null;
  readonly closedAt: string | null;
};

/** OD-0048-ENMIENDA-02 F-01: los cinco motivos, en el orden de la pantalla. */
export const MOTIVOS_PERDIDA = ["PRICE", "COMPETITOR", "POSTPONED", "NO_REPLY", "OTHER"] as const;
export type MotivoPerdida = (typeof MOTIVOS_PERDIDA)[number];

export type DatosOportunidad = {
  readonly customerId: string;
  readonly title: string;
  readonly estimatedAmount: string | null;
  readonly expectedCloseDate: string | null;
  readonly notes: string | null;
};

/** Cuántas y cuánto suman. El importe lo suma la API; aquí solo se enseña. */
export type CifraOportunidades = { readonly cuantas: number; readonly total: string };

export type ListaOportunidades = {
  readonly oportunidades: readonly Oportunidad[];
  /** Suma de lo abierto, calculada por la API. */
  readonly totalAbierto: string;
  readonly abiertas: number;
  /** Una columna del tablero por etapa abierta, en orden (vacío si la API es anterior). */
  readonly etapas: ReadonlyArray<CifraOportunidades & { readonly etapa: EtapaAbierta }>;
  /**
   * OD-0058 D-05: lo abierto ponderado, sumado por la API. `porEtapa`: cuántas
   * cuentan por la base de su etapa, sin cualificar (G-09). `null` si la API es
   * anterior.
   */
  readonly prevision: { readonly total: string; readonly cuenta: number; readonly porEtapa: number } | null;
  /** E-06: ids de las abiertas a las que José recomienda llamar primero, en orden. */
  readonly recomendadas: readonly string[];
  readonly ganadas: CifraOportunidades | null;
  readonly perdidas: CifraOportunidades | null;
};

function comoCifra(v: unknown): CifraOportunidades | null {
  if (typeof v !== "object" || v === null) return null;
  const c = v as Record<string, unknown>;
  return typeof c.count === "number" && typeof c.total === "string" ? { cuantas: c.count, total: c.total } : null;
}

const ESTADOS_OPORTUNIDAD: readonly string[] = ["OPEN", "WON", "LOST"];

function comoOportunidad(valor: unknown): Oportunidad | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.id !== "string" || typeof v.title !== "string" || !ESTADOS_OPORTUNIDAD.includes(v.status as string)) return null;
  if (!textoONulo(v.estimatedAmount) || !textoONulo(v.expectedCloseDate) || !textoONulo(v.notes) || !textoONulo(v.closedAt)) return null;
  if (!ETAPAS.includes(v.stage as string) || !ETAPAS.includes(v.openStage as string)) return null;
  const q = v.quote as Record<string, unknown> | null | undefined;
  if (q !== null && (typeof q !== "object" || typeof q.id !== "string" || !textoONulo(q.status))) return null;
  const c = v.customer as Record<string, unknown> | null | undefined;
  if (c !== null && (typeof c !== "object" || typeof c.id !== "string" || typeof c.displayName !== "string")) return null;
  const qual = typeof v.qualification === "object" && v.qualification !== null ? (v.qualification as Record<string, unknown>) : {};
  const qualification = Object.fromEntries(
    PREGUNTAS_CUALIFICACION.map((p) => [p, (RESPUESTAS_CUALIFICACION as readonly unknown[]).includes(qual[p]) ? qual[p] : null]),
  ) as Cualificacion;
  const entero = (x: unknown): number | null => (typeof x === "number" && Number.isInteger(x) ? x : null);
  const probability = entero(v.probability);
  const origen = ORIGENES.includes(v.probabilitySource as string) ? (v.probabilitySource as OrigenProbabilidad) : null;
  const l = typeof v.lever === "object" && v.lever !== null ? (v.lever as Record<string, unknown>) : null;
  const lever =
    l !== null && (PREGUNTAS_CUALIFICACION as readonly unknown[]).includes(l.question) && entero(l.probability) !== null
      ? { question: l.question as PreguntaCualificacion, from: entero(l.from), probability: entero(l.probability) ?? 0 }
      : null;
  // Una API anterior no los manda: sin motivo.
  const lostReason = (MOTIVOS_PERDIDA as readonly unknown[]).includes(v.lostReason) ? (v.lostReason as MotivoPerdida) : null;
  const lostNote = typeof v.lostNote === "string" ? v.lostNote : null;
  return {
    ...(v as unknown as Oportunidad),
    qualification,
    probability,
    probabilitySource: probability === null ? null : origen,
    // Una API anterior no los manda: sin base, sin nivel y sin propuesta.
    stageProbability: entero(v.stageProbability),
    calculatedProbability: entero(v.calculatedProbability),
    qualificationLevel: entero(v.qualificationLevel),
    suggestedStage: v.suggestedStage === "QUALIFIED" ? "QUALIFIED" : null,
    lever,
    lostReason,
    lostNote,
  };
}

/**
 * Una oportunidad y lo que la acompaña. `historial` (OD-0048-ENMIENDA-02 F-04)
 * solo viene en la ficha: la última OTRA ganada del mismo cliente.
 */
export type UnaOportunidad = {
  readonly oportunidad: Oportunidad;
  readonly ofertas: readonly string[];
  readonly historial: { readonly ganada: string } | null;
};

function comoUnaOportunidad(cuerpo: unknown): UnaOportunidad | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const b = cuerpo as Record<string, unknown>;
  const o = comoOportunidad(b.opportunity);
  if (o === null) return null;
  const ofertas = Array.isArray(b.wonOffers) ? b.wonOffers.filter((x): x is string => typeof x === "string") : [];
  const h = typeof b.customerHistory === "object" && b.customerHistory !== null ? (b.customerHistory as Record<string, unknown>) : null;
  const historial = h !== null && typeof h.wonTitle === "string" ? { ganada: h.wonTitle } : null;
  return { oportunidad: o, ofertas, historial };
}

function comoListaOportunidades(cuerpo: unknown): ListaOportunidades | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const b = cuerpo as Record<string, unknown>;
  if (!Array.isArray(b.opportunities) || typeof b.openTotal !== "string" || typeof b.openCount !== "number") return null;
  const lista = b.opportunities.map(comoOportunidad);
  if (!lista.every((o) => o !== null)) return null;
  const etapas = (Array.isArray(b.stages) ? b.stages : []).flatMap((x) => {
    const c = comoCifra(x);
    const etapa = (x as Record<string, unknown>).stage;
    return c !== null && (ETAPAS_ABIERTAS as readonly unknown[]).includes(etapa) ? [{ ...c, etapa: etapa as EtapaAbierta }] : [];
  });
  return {
    oportunidades: lista as Oportunidad[],
    totalAbierto: b.openTotal,
    abiertas: b.openCount,
    etapas,
    prevision:
      typeof b.forecast === "object" && b.forecast !== null
        ? (() => {
            const f = b.forecast as Record<string, unknown>;
            return typeof f.total === "string" && typeof f.counted === "number"
              ? { total: f.total, cuenta: f.counted, porEtapa: typeof f.byStage === "number" ? f.byStage : 0 }
              : null;
          })()
        : null,
    recomendadas: Array.isArray(b.recommended) ? b.recommended.filter((x): x is string => typeof x === "string") : [],
    ganadas: comoCifra(b.won),
    perdidas: comoCifra(b.lost),
  };
}

/**
 * Tareas y actividades del CRM de Business — `OD-0050` sobre `RC-0017`. Una
 * tarea y una llamada son lo mismo con distinto tipo: algo con fecha que se
 * marca como hecho. Agrupar por "vencidas / hoy / próximas" lo hace la
 * pantalla con el día LOCAL; la API solo ordena por fecha.
 */
export type TipoActividad = "TASK" | "CALL" | "MEETING" | "EMAIL" | "NOTE";
export const TIPOS_ACTIVIDAD: readonly TipoActividad[] = ["TASK", "CALL", "MEETING", "EMAIL", "NOTE"];

export type Actividad = {
  readonly id: string;
  readonly kind: TipoActividad;
  readonly title: string;
  readonly notes: string | null;
  readonly dueDate: string | null;
  readonly dueTime: string | null;
  readonly important: boolean;
  readonly customer: { readonly id: string; readonly displayName: string } | null;
  readonly opportunity: { readonly id: string; readonly title: string } | null;
  /** `OD-0050-ENMIENDA-01`: el número es `null` en una factura en borrador. */
  readonly quote: DocumentoEnlazado | null;
  readonly invoice: DocumentoEnlazado | null;
  readonly status: "PENDING" | "DONE";
  readonly doneAt: string | null;
  /** `OD-0062` E-09: al hacerla, la API crea la siguiente. Solo con fecha. */
  readonly repeat: RepeticionActividad | null;
};

export type RepeticionActividad = "WEEKLY" | "MONTHLY";
export const REPETICIONES_ACTIVIDAD: readonly RepeticionActividad[] = ["WEEKLY", "MONTHLY"];

export type DocumentoEnlazado = { readonly id: string; readonly number: string | null; readonly status: string };

/** La ficha desde la que se mira: cliente, oportunidad, presupuesto o factura. */
export type FiltroActividades = {
  readonly clienteId?: string | null;
  readonly oportunidadId?: string | null;
  readonly presupuestoId?: string | null;
  readonly facturaId?: string | null;
};

export type DatosActividad = {
  readonly kind: TipoActividad;
  readonly title: string;
  readonly notes: string | null;
  readonly dueDate: string | null;
  readonly dueTime: string | null;
  readonly important: boolean;
  readonly customerId: string | null;
  readonly opportunityId: string | null;
  readonly quoteId: string | null;
  readonly invoiceId: string | null;
  readonly repeat: RepeticionActividad | null;
};

/** E-07: las señales de la pantalla de José (`POST /api/v1/crm/jose/signals`). */
export type SenalJose =
  | { readonly type: "FEEDBACK"; readonly subject: string; readonly value: "UP" | "DOWN" }
  | { readonly type: "TIP_NEXT"; readonly subject: string }
  | { readonly type: "RECOMMENDATION_OPENED"; readonly value: "1" | "2" | "3" };

export type ListaActividades = {
  readonly actividades: readonly Actividad[];
  readonly pendientes: number;
  /** La lista general de hechas se corta (los últimos 90 días, como mucho 100). */
  readonly cortada: boolean;
};

function comoActividad(valor: unknown): Actividad | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.id !== "string" || typeof v.title !== "string" || !(TIPOS_ACTIVIDAD as readonly unknown[]).includes(v.kind)) return null;
  if (v.status !== "PENDING" && v.status !== "DONE") return null;
  if (!textoONulo(v.notes) || !textoONulo(v.dueDate) || !textoONulo(v.dueTime) || !textoONulo(v.doneAt) || typeof v.important !== "boolean") return null;
  const c = v.customer as Record<string, unknown> | null | undefined;
  if (c !== null && (typeof c !== "object" || typeof c.id !== "string" || typeof c.displayName !== "string")) return null;
  const o = v.opportunity as Record<string, unknown> | null | undefined;
  if (o !== null && (typeof o !== "object" || typeof o.id !== "string" || typeof o.title !== "string")) return null;
  for (const d of [v.quote, v.invoice] as Array<Record<string, unknown> | null | undefined>) {
    if (d === null) continue;
    if (typeof d !== "object" || typeof d.id !== "string" || !textoONulo(d.number) || typeof d.status !== "string") return null;
  }
  const repeat = (REPETICIONES_ACTIVIDAD as readonly unknown[]).includes(v.repeat) ? (v.repeat as RepeticionActividad) : null;
  return { ...(v as unknown as Actividad), repeat };
}

function comoUnaActividad(cuerpo: unknown): Actividad | null {
  return typeof cuerpo === "object" && cuerpo !== null ? comoActividad((cuerpo as Record<string, unknown>).activity) : null;
}

/** Marcar como hecha devuelve también la siguiente, si se repite y nació ahora (E-09). */
export type ActividadMarcada = { readonly actividad: Actividad; readonly siguiente: Actividad | null };

function comoActividadMarcada(cuerpo: unknown): ActividadMarcada | null {
  const actividad = comoUnaActividad(cuerpo);
  if (actividad === null) return null;
  const n = (cuerpo as Record<string, unknown>).next;
  return { actividad, siguiente: n === null || n === undefined ? null : comoActividad(n) };
}

function comoListaActividades(cuerpo: unknown): ListaActividades | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const b = cuerpo as Record<string, unknown>;
  if (!Array.isArray(b.activities) || typeof b.pendingCount !== "number") return null;
  const lista = b.activities.map(comoActividad);
  if (!lista.every((a) => a !== null)) return null;
  return { actividades: lista as Actividad[], pendientes: b.pendingCount, cortada: b.truncated === true };
}

/**
 * Registro de cobros de Business (`OD-0040`, `ADR-0023`). Importes SIEMPRE
 * texto: lo cobrado y lo pendiente los suma la API (`D-08`), no esta web.
 */
export type MetodoCobro = "TRANSFERENCIA" | "TARJETA" | "EFECTIVO" | "DOMICILIACION" | "BIZUM" | "OTRO";
export const METODOS_COBRO: readonly MetodoCobro[] = ["TRANSFERENCIA", "TARJETA", "EFECTIVO", "DOMICILIACION", "BIZUM", "OTRO"];

/** `OD-0002`: el estado de PAGO, independiente del documental. */
export type EstadoPago = "UNPAID" | "PARTIALLY_PAID" | "PAID";

/** Por qué no se puede registrar un cobro en una factura. */
export type BloqueoCobro = "NOT_ISSUED" | "INVOICE_VOIDED" | "RECTIFICATIVE" | "NOTHING_TO_COLLECT" | "TOTALS_UNREADABLE" | "FULLY_PAID";

export type Cobro = {
  readonly id: string;
  readonly amount: string;
  readonly paidOn: string;
  readonly method: MetodoCobro;
  readonly note: string | null;
  readonly status: "POSTED" | "VOIDED";
  readonly registeredAt: string;
  readonly voidedAt: string | null;
  readonly voidReason: string | null;
};

export type CuentaCobros = {
  readonly invoiceId: string;
  readonly amountToCollect: string | null;
  readonly collected: string;
  readonly pending: string;
  readonly paymentStatus: EstadoPago | null;
  readonly blockedBy: BloqueoCobro | null;
};

export type FichaCobros = { readonly cobros: readonly Cobro[]; readonly cuenta: CuentaCobros };

export type ResumenCobros = {
  readonly desde: string;
  readonly hasta: string;
  readonly cobrado: string;
  readonly pendiente: string;
  readonly facturasPendientes: number;
  readonly porCliente: ReadonlyArray<{ readonly customerId: string; readonly displayName: string; readonly pending: string; readonly invoices: number }>;
  readonly facturas: readonly CuentaCobros[];
};

export type DatosCobro = { readonly amount: string; readonly paidOn: string; readonly method: MetodoCobro; readonly note: string | null };

const ESTADOS_PAGO: readonly unknown[] = ["UNPAID", "PARTIALLY_PAID", "PAID"];
const BLOQUEOS_COBRO: readonly unknown[] = ["NOT_ISSUED", "INVOICE_VOIDED", "RECTIFICATIVE", "NOTHING_TO_COLLECT", "TOTALS_UNREADABLE", "FULLY_PAID"];

function comoCobro(valor: unknown): Cobro | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.id !== "string" || typeof v.amount !== "string" || typeof v.paidOn !== "string" || typeof v.registeredAt !== "string") return null;
  if (!(METODOS_COBRO as readonly unknown[]).includes(v.method) || (v.status !== "POSTED" && v.status !== "VOIDED")) return null;
  if (!textoONulo(v.note) || !textoONulo(v.voidedAt) || !textoONulo(v.voidReason)) return null;
  return v as unknown as Cobro;
}

function comoCuentaCobros(valor: unknown): CuentaCobros | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.invoiceId !== "string" || !textoONulo(v.amountToCollect) || typeof v.collected !== "string" || typeof v.pending !== "string") return null;
  if (v.paymentStatus !== null && !ESTADOS_PAGO.includes(v.paymentStatus)) return null;
  if (v.blockedBy !== null && !BLOQUEOS_COBRO.includes(v.blockedBy)) return null;
  return v as unknown as CuentaCobros;
}

/** `OD-0067` D-02: lo que el cliente escribe para su gestoría de una factura o compra. */
export type NotaParaLaAsesoria = {
  readonly nota: string | null;
  readonly etiqueta: string | null;
  /** `null` si nunca se ha escrito. */
  readonly actualizadaEn: string | null;
  /** Lo decide la API por el rol: quien no puede, la ve sin poder cambiarla. */
  readonly puedeEscribir: boolean;
};

/** `OD-0067` D-05: un mensaje de la conversación con la gestoría, tal como lo ve quien mira. */
export type MensajeDeLaConversacion = {
  readonly id: string;
  readonly lado: "CLIENT" | "ADVISOR";
  readonly propio: boolean;
  /** El correo de quien lo escribió; `null` si es tuyo, o si eres la gestoría y es de la empresa. */
  readonly autor: string | null;
  /** `null` si está retirado. */
  readonly texto: string | null;
  readonly retirado: boolean;
  readonly documento: { readonly tipo: "factura" | "compra"; readonly id: string; readonly etiqueta: string } | null;
  readonly enviadoEn: string;
};

export type ConversacionConLaAsesoria = {
  /** De qué lado está quien mira: lo decide la API por el rol. */
  readonly lado: "CLIENT" | "ADVISOR";
  readonly asesoria: string | null;
  readonly mensajes: readonly MensajeDeLaConversacion[];
  readonly noLeidos: number;
  /** D-06: a quién va «Mandar por correo». `null` si no hay a quién. */
  readonly destinatario: string | null;
};

const LADOS: readonly string[] = ["CLIENT", "ADVISOR"];

function comoMensajeDeLaConversacion(valor: unknown): MensajeDeLaConversacion | null {
  const m = objeto(valor);
  if (m === null || typeof m.id !== "string" || !LADOS.includes(m.lado as string) || typeof m.propio !== "boolean") return null;
  if (!textoONulo(m.autor) || !textoONulo(m.texto) || typeof m.retirado !== "boolean" || typeof m.enviadoEn !== "string") return null;
  const d = objeto(m.documento);
  const documento =
    d !== null && (d.tipo === "factura" || d.tipo === "compra") && typeof d.id === "string" && typeof d.etiqueta === "string"
      ? { tipo: d.tipo as "factura" | "compra", id: d.id, etiqueta: d.etiqueta }
      : null;
  if (m.documento !== null && documento === null) return null;
  return {
    id: m.id,
    lado: m.lado as "CLIENT" | "ADVISOR",
    propio: m.propio,
    autor: (m.autor as string | null) ?? null,
    texto: (m.texto as string | null) ?? null,
    retirado: m.retirado,
    documento,
    enviadoEn: m.enviadoEn,
  };
}

function comoConversacion(cuerpo: unknown): ConversacionConLaAsesoria | null {
  const o = objeto(cuerpo);
  if (o === null || !LADOS.includes(o.lado as string) || !textoONulo(o.asesoria) || typeof o.noLeidos !== "number" || !textoONulo(o.destinatario)) return null;
  if (!Array.isArray(o.mensajes)) return null;
  const mensajes = o.mensajes.map(comoMensajeDeLaConversacion);
  if (!mensajes.every((m) => m !== null)) return null;
  return {
    lado: o.lado as "CLIENT" | "ADVISOR",
    asesoria: (o.asesoria as string | null) ?? null,
    mensajes: mensajes as MensajeDeLaConversacion[],
    noLeidos: o.noLeidos,
    destinatario: (o.destinatario as string | null) ?? null,
  };
}

function comoNoLeidos(cuerpo: unknown): { readonly noLeidos: number } | null {
  const o = objeto(cuerpo);
  return o !== null && typeof o.noLeidos === "number" ? { noLeidos: o.noLeidos } : null;
}

/**
 * `OD-0067` D-05: la conversación. `null` es la de la empresa ACTIVA; con una
 * organización, la gestoría desde su panel (`ADR-0025`, ampliación
 * 2026-10-28). Las mismas respuestas por las dos puertas.
 */
export function apiConversacion(organizationId: string | null) {
  const base = organizationId === null ? "/api/v1/asesoria/conversacion" : `/api/v1/asesoria/clientes/${tramo(organizationId)}/conversacion`;
  return {
    ver: (): Promise<Resultado<ConversacionConLaAsesoria>> => pedir(base, { method: "GET" }, comoConversacion),
    enviar: (
      texto: string,
      documento: { readonly tipo: "factura" | "compra"; readonly id: string } | null,
    ): Promise<Resultado<ConversacionConLaAsesoria>> =>
      pedir(`${base}/mensajes`, { method: "POST", body: JSON.stringify({ texto, documento }) }, comoConversacion),
    retirar: (id: string): Promise<Resultado<ConversacionConLaAsesoria>> =>
      pedir(`${base}/mensajes/${encodeURIComponent(id)}/retirar`, { method: "POST", body: "{}" }, comoConversacion),
    marcarLeida: (): Promise<Resultado<{ readonly noLeidos: number }>> => pedir(`${base}/leida`, { method: "POST", body: "{}" }, comoNoLeidos),
  };
}

/** El aviso de Inicio (D-05): sin plan, sin rol o sin gestoría, la API no contesta 200 y no se avisa. */
export const noLeidosDeLaConversacion = (): Promise<Resultado<{ readonly noLeidos: number }>> =>
  pedir("/api/v1/asesoria/conversacion/no-leidos", { method: "GET" }, comoNoLeidos);

/** `OD-0067` D-03. */
export type ComentarioDelTrimestre = {
  readonly trimestre: string;
  readonly comentario: string | null;
  readonly actualizadoEn: string | null;
  readonly puedeEscribir: boolean;
};

function comoNotaParaLaAsesoria(cuerpo: unknown): NotaParaLaAsesoria | null {
  const o = objeto(cuerpo);
  const n = objeto(o?.nota);
  if (o === null || n === null || !textoONulo(n.nota) || !textoONulo(n.etiqueta) || !textoONulo(o.actualizadaEn) || typeof o.puedeEscribir !== "boolean") return null;
  return {
    nota: (n.nota as string | null) ?? null,
    etiqueta: (n.etiqueta as string | null) ?? null,
    actualizadaEn: (o.actualizadaEn as string | null) ?? null,
    puedeEscribir: o.puedeEscribir,
  };
}

function comoComentarioDelTrimestre(cuerpo: unknown): ComentarioDelTrimestre | null {
  const o = objeto(cuerpo);
  if (o === null || typeof o.trimestre !== "string" || !textoONulo(o.comentario) || !textoONulo(o.actualizadoEn) || typeof o.puedeEscribir !== "boolean") return null;
  return {
    trimestre: o.trimestre,
    comentario: (o.comentario as string | null) ?? null,
    actualizadoEn: (o.actualizadoEn as string | null) ?? null,
    puedeEscribir: o.puedeEscribir,
  };
}

function comoFichaCobros(cuerpo: unknown): FichaCobros | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const b = cuerpo as Record<string, unknown>;
  const cuenta = comoCuentaCobros(b.summary);
  if (!Array.isArray(b.payments) || cuenta === null) return null;
  const cobros = b.payments.map(comoCobro);
  return cobros.every((c) => c !== null) ? { cobros: cobros as Cobro[], cuenta } : null;
}

/** El alta y la anulación devuelven el cobro y la cuenta nueva: la ficha se pinta con ella. */
function comoCuentaDeUnCobro(cuerpo: unknown): CuentaCobros | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const b = cuerpo as Record<string, unknown>;
  return comoCobro(b.payment) === null ? null : comoCuentaCobros(b.summary);
}

function comoResumenCobros(cuerpo: unknown): ResumenCobros | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const b = cuerpo as Record<string, unknown>;
  const periodo = b.period as Record<string, unknown> | null | undefined;
  if (typeof periodo !== "object" || periodo === null || typeof periodo.from !== "string" || typeof periodo.to !== "string") return null;
  if (typeof b.collected !== "string" || typeof b.pending !== "string" || typeof b.pendingInvoices !== "number") return null;
  if (!Array.isArray(b.pendingByCustomer) || !Array.isArray(b.invoices)) return null;
  const facturas = b.invoices.map(comoCuentaCobros);
  if (!facturas.every((f) => f !== null)) return null;
  const porCliente = b.pendingByCustomer.filter(
    (c): c is ResumenCobros["porCliente"][number] =>
      typeof c === "object" &&
      c !== null &&
      typeof (c as Record<string, unknown>).customerId === "string" &&
      typeof (c as Record<string, unknown>).displayName === "string" &&
      typeof (c as Record<string, unknown>).pending === "string" &&
      typeof (c as Record<string, unknown>).invoices === "number",
  );
  return {
    desde: periodo.from,
    hasta: periodo.to,
    cobrado: b.collected,
    pendiente: b.pending,
    facturasPendientes: b.pendingInvoices,
    porCliente,
    facturas: facturas as CuentaCobros[],
  };
}

/** `OD-0051 D-03`: los tramos de antigüedad, en el orden en que se pintan. */
export const TRAMOS_ANTIGUEDAD = ["NOT_DUE", "DAYS_1_30", "DAYS_31_60", "DAYS_61_90", "DAYS_OVER_90", "NO_DUE_DATE"] as const;
export type TramoAntiguedad = (typeof TRAMOS_ANTIGUEDAD)[number];

/** `OD-0051 D-04`. */
export const FILTROS_PENDIENTES = ["PENDING", "OVERDUE", "PARTIAL"] as const;
export type FiltroPendientes = (typeof FILTROS_PENDIENTES)[number];

export type FiltrosPendientes = {
  readonly estado: FiltroPendientes;
  readonly cliente: string | null;
  readonly desde: string | null;
  readonly hasta: string | null;
};

export type FacturaPendiente = {
  readonly invoiceId: string;
  readonly number: string | null;
  readonly customerId: string;
  readonly customerName: string;
  readonly issueDate: string | null;
  readonly dueDate: string | null;
  readonly amountToCollect: string;
  readonly collected: string;
  readonly pending: string;
  readonly paymentStatus: EstadoPago;
  readonly bucket: TramoAntiguedad;
  readonly daysOverdue: number | null;
  /** `OD-0062` E-02: días desde la emisión, para las que no tienen vencimiento. */
  readonly daysSinceIssue: number | null;
};

/** Todo sumado por la API (`OD-0040 D-08`): esta web no suma dinero. */
export type CobrosPendientes = {
  readonly aFecha: string;
  readonly pendiente: string;
  readonly facturas: number;
  readonly tramos: ReadonlyArray<{ readonly bucket: TramoAntiguedad; readonly pending: string; readonly invoices: number }>;
  readonly clientes: ReadonlyArray<{ readonly customerId: string; readonly displayName: string }>;
  readonly filas: readonly FacturaPendiente[];
  /** `OD-0062` E-02: lo que hay sin vencimiento emitido hace más de `dias` días. `null` si no llega. */
  readonly sinVencimientoAntiguas: { readonly dias: number; readonly pendiente: string; readonly facturas: number } | null;
};

const TRAMOS: readonly unknown[] = TRAMOS_ANTIGUEDAD;

function comoFacturaPendiente(valor: unknown): FacturaPendiente | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.invoiceId !== "string" || typeof v.customerId !== "string" || typeof v.customerName !== "string") return null;
  if (!textoONulo(v.number) || !textoONulo(v.issueDate) || !textoONulo(v.dueDate)) return null;
  if (typeof v.amountToCollect !== "string" || typeof v.collected !== "string" || typeof v.pending !== "string") return null;
  if (!ESTADOS_PAGO.includes(v.paymentStatus) || !TRAMOS.includes(v.bucket)) return null;
  if (v.daysOverdue !== null && typeof v.daysOverdue !== "number") return null;
  return { ...(v as unknown as FacturaPendiente), daysSinceIssue: typeof v.daysSinceIssue === "number" ? v.daysSinceIssue : null };
}

function comoCobrosPendientes(cuerpo: unknown): CobrosPendientes | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const b = cuerpo as Record<string, unknown>;
  if (typeof b.asOf !== "string" || typeof b.pending !== "string" || typeof b.invoices !== "number") return null;
  if (!Array.isArray(b.buckets) || !Array.isArray(b.customers) || !Array.isArray(b.rows)) return null;
  const tramos = b.buckets.filter(
    (x): x is CobrosPendientes["tramos"][number] =>
      typeof x === "object" &&
      x !== null &&
      TRAMOS.includes((x as Record<string, unknown>).bucket) &&
      typeof (x as Record<string, unknown>).pending === "string" &&
      typeof (x as Record<string, unknown>).invoices === "number",
  );
  const clientes = b.customers.filter(
    (x): x is CobrosPendientes["clientes"][number] =>
      typeof x === "object" &&
      x !== null &&
      typeof (x as Record<string, unknown>).customerId === "string" &&
      typeof (x as Record<string, unknown>).displayName === "string",
  );
  const filas = b.rows.map(comoFacturaPendiente);
  if (!filas.every((f) => f !== null)) return null;
  const s = objeto(b.noDueDateOverThreshold);
  const sinVencimientoAntiguas =
    s !== null && typeof s.days === "number" && typeof s.pending === "string" && typeof s.invoices === "number"
      ? { dias: s.days, pendiente: s.pending, facturas: s.invoices }
      : null;
  return { aFecha: b.asOf, pendiente: b.pending, facturas: b.invoices, tramos, clientes, filas: filas as FacturaPendiente[], sinVencimientoAntiguas };
}

/** Los filtros de Cobros pendientes en la consulta: los mismos para la lista y para el Excel. */
function consultaPendientes(f: FiltrosPendientes): string {
  const q = new URLSearchParams({ estado: f.estado });
  if (f.cliente !== null) q.set("cliente", f.cliente);
  if (f.desde !== null) q.set("desde", f.desde);
  if (f.hasta !== null) q.set("hasta", f.hasta);
  return q.toString();
}

/**
 * Informes fiscales de Business (`OD-0041`, ACCEPTED). Todo calculado por la API:
 * aquí no se suma ni un céntimo, y ninguna cifra es «a ingresar».
 */
export type ObligacionFiscal =
  | {
      readonly publicado: true;
      readonly modelo: string;
      readonly periodo: string;
      readonly desde: string;
      readonly hasta: string;
      readonly domiciliacionHasta: string | null;
      readonly estado: "ABIERTO" | "PROXIMO" | "TERMINADO";
      readonly diasRestantes: number;
      readonly proximidad: "NORMAL" | "CERCA" | "INMINENTE";
      readonly fuente: { readonly ref: string; readonly titulo: string; readonly url: string };
    }
  | {
      readonly publicado: false;
      readonly modelo: string;
      readonly periodo: string;
      readonly calendario: number;
      readonly plazoGeneral: string;
    };

export type FacturaDelInforme = {
  readonly invoiceId: string;
  readonly numero: string;
  readonly fechaExpedicion: string;
  readonly fechaOperacion: string | null;
  readonly clase: "ORDINARIA" | "RECTIFICATIVA_DIFERENCIAS" | "RECTIFICATIVA_SUSTITUCION";
  readonly base: string;
  readonly cuota: string;
  readonly retencion: string;
  readonly avisos: readonly string[];
};

/** OD-0056: una compra anotada que forma el IVA soportado del trimestre. */
export type CompraDelInformeFiscal = {
  readonly purchaseId: string;
  readonly registro: string;
  readonly proveedor: string;
  readonly numeroDelProveedor: string;
  readonly fechaExpedicion: string;
  readonly fechaAnotacion: string;
  readonly base: string;
  readonly cuota: string;
  readonly avisos: readonly string[];
};

export type CompraExcluidaDelInforme = {
  readonly purchaseId: string;
  readonly registro: string;
  readonly proveedor: string;
  readonly fechaAnotacion: string;
  readonly motivo: string;
};

/**
 * OD-0056: el IVA de las compras ANOTADAS. No es el deducible, y sin compras
 * no hay cifra: hay un motivo.
 */
export type IvaSoportadoDelInforme =
  | {
      readonly disponible: true;
      readonly base: string;
      readonly soportado: string;
      readonly porTipo: ReadonlyArray<{ readonly tratamiento: string; readonly tipo: string | null; readonly base: string; readonly cuota: string | null }>;
      readonly compras: readonly CompraDelInformeFiscal[];
      readonly excluidas: readonly CompraExcluidaDelInforme[];
    }
  | {
      readonly disponible: false;
      readonly motivo: string;
      readonly compras: readonly string[];
      readonly excluidas: readonly CompraExcluidaDelInforme[];
    };

export type InformeFiscal = {
  readonly aFecha: string;
  readonly trimestre: { readonly etiqueta: string; readonly desde: string; readonly hasta: string; readonly enCurso: boolean };
  readonly trimestres: readonly string[];
  readonly informe:
    | {
        readonly calculado: true;
        readonly iva: {
          readonly base: string;
          readonly repercutido: string;
          readonly facturas: number;
          readonly porTipo: ReadonlyArray<{
            readonly tratamiento: string;
            readonly tipo: string | null;
            readonly causa: string | null;
            readonly base: string;
            readonly cuota: string | null;
          }>;
        };
        readonly retenciones: {
          readonly importe: string;
          readonly facturas: number;
          readonly porTipo: ReadonlyArray<{ readonly tipo: string; readonly base: string; readonly importe: string; readonly facturas: number }>;
        };
        readonly facturas: readonly FacturaDelInforme[];
        readonly excluidas: ReadonlyArray<{ readonly invoiceId: string; readonly numero: string; readonly fechaExpedicion: string; readonly motivo: string }>;
      }
    | {
        readonly calculado: false;
        readonly motivo: string;
        readonly facturas: readonly string[];
        readonly excluidas: ReadonlyArray<{ readonly invoiceId: string; readonly numero: string; readonly fechaExpedicion: string; readonly motivo: string }>;
      };
  readonly ivaSoportado: IvaSoportadoDelInforme;
  readonly declaracion: readonly ObligacionFiscal[];
  readonly calendario: {
    readonly comprobadoEl: string;
    readonly obligado: "PERSONA_FISICA" | "ENTIDAD" | null;
    readonly proximas: readonly ObligacionFiscal[];
  };
};

function esObligacion(valor: unknown): valor is ObligacionFiscal {
  if (typeof valor !== "object" || valor === null) return false;
  const v = valor as Record<string, unknown>;
  if (typeof v.modelo !== "string" || typeof v.periodo !== "string") return false;
  if (v.publicado === false) return typeof v.calendario === "number" && typeof v.plazoGeneral === "string";
  return (
    v.publicado === true &&
    typeof v.desde === "string" &&
    typeof v.hasta === "string" &&
    textoONulo(v.domiciliacionHasta) &&
    typeof v.diasRestantes === "number" &&
    ["ABIERTO", "PROXIMO", "TERMINADO"].includes(v.estado as string) &&
    ["NORMAL", "CERCA", "INMINENTE"].includes(v.proximidad as string) &&
    typeof v.fuente === "object" &&
    v.fuente !== null &&
    typeof (v.fuente as Record<string, unknown>).url === "string"
  );
}

function comoInformeFiscal(cuerpo: unknown): InformeFiscal | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const b = cuerpo as Record<string, unknown>;
  if (typeof b.aFecha !== "string" || !Array.isArray(b.trimestres) || !Array.isArray(b.declaracion)) return null;
  const t = b.trimestre as Record<string, unknown> | null;
  if (typeof t !== "object" || t === null || typeof t.etiqueta !== "string" || typeof t.enCurso !== "boolean") return null;
  const i = b.informe as Record<string, unknown> | null;
  if (typeof i !== "object" || i === null || typeof i.calculado !== "boolean" || !Array.isArray(i.excluidas)) return null;
  if (i.calculado) {
    const iva = i.iva as Record<string, unknown> | null;
    const ret = i.retenciones as Record<string, unknown> | null;
    if (typeof iva !== "object" || iva === null || typeof iva.repercutido !== "string" || !Array.isArray(iva.porTipo)) return null;
    if (typeof ret !== "object" || ret === null || typeof ret.importe !== "string" || !Array.isArray(ret.porTipo)) return null;
    if (!Array.isArray(i.facturas)) return null;
  } else if (typeof i.motivo !== "string" || !Array.isArray(i.facturas)) return null;
  const s = b.ivaSoportado as Record<string, unknown> | null;
  if (typeof s !== "object" || s === null || typeof s.disponible !== "boolean" || !Array.isArray(s.compras) || !Array.isArray(s.excluidas)) return null;
  if (s.disponible ? typeof s.soportado !== "string" || !Array.isArray(s.porTipo) : typeof s.motivo !== "string") return null;
  const c = b.calendario as Record<string, unknown> | null;
  if (typeof c !== "object" || c === null || !Array.isArray(c.proximas)) return null;
  if (!b.declaracion.every(esObligacion) || !c.proximas.every(esObligacion)) return null;
  return b as unknown as InformeFiscal;
}

/** TDS-CORE Parte 2 §2. Igual que en productos: importes SIEMPRE texto. */
export type EstadoPresupuesto = "DRAFT" | "SENT" | "ACCEPTED" | "REJECTED" | "EXPIRED" | "CANCELLED";

export type DescuentoLinea =
  | { readonly kind: "NONE" }
  | { readonly kind: "AMOUNT" | "PERCENT"; readonly value: string };

export type LineaPresupuesto = {
  readonly productId: string | null;
  readonly description: string;
  /** Texto con 3 decimales. */
  readonly quantity: string;
  /** Texto con 4 decimales, sin IVA. */
  readonly unitPrice: string;
  readonly discount: DescuentoLinea;
  readonly vatTreatment: TratamientoIva;
  readonly vatRate: string | null;
  readonly vatCause: string | null;
  readonly withholdingApplies: boolean;
};

export type DesgloseIva = {
  readonly treatment: TratamientoIva;
  readonly rate: string | null;
  readonly cause: string | null;
  readonly base: string;
  readonly vatAmount: string | null;
};

export type TotalesDocumento = {
  readonly lines: ReadonlyArray<{ readonly gross: string; readonly discount: string; readonly base: string }>;
  readonly breakdown: readonly DesgloseIva[];
  readonly baseTotal: string;
  readonly vatTotal: string;
  /** TOTAL FACTURA: nunca resta la retencion (AEAT-REF-012). */
  readonly invoiceTotal: string;
  readonly withholdingRate: string | null;
  readonly withholdingBase: string;
  readonly withholdingAmount: string;
  /**
   * Suplidos (`OD-0052`): sólo si la factura los tiene. Ausente = ninguno, y
   * así llegan las facturas de antes y los presupuestos.
   */
  readonly disbursementsTotal?: string;
  /**
   * Anticipos descontados (`OD-0053 D-03`): sólo en la factura FINAL de un
   * presupuesto con anticipos. La base de arriba ya es la neta.
   */
  readonly advanceDeductionsTotal?: string;
  /** TOTAL A COBRAR = TOTAL FACTURA menos la retencion, mas los suplidos. */
  readonly amountToCollect: string;
};

/** Un suplido tal como lo devuelve la API: el importe, texto con coma. */
export type Suplido = {
  readonly description: string;
  readonly amount: string;
  readonly paidTo: string | null;
  readonly reference: string | null;
};

/**
 * Un suplido mientras se escribe. La casilla viaja con él: sin ella marcada
 * la API no lo acepta (`OD-0052 D-01`).
 */
export type SuplidoEditable = {
  readonly description: string;
  readonly amount: string;
  readonly paidTo: string;
  readonly reference: string;
  readonly onBehalfConfirmed: boolean;
};

export type DatosFiscalesCopia = {
  readonly legalName: string;
  readonly taxIdNormalized: string;
  readonly addressLine: string;
  readonly postalCode: string;
  readonly city: string;
  readonly province: string;
  // Opcionales: las copias congeladas antes del bloque 1 no los tienen.
  readonly contactEmail?: string | null;
  readonly contactPhone?: string | null;
  readonly paymentMethod?: string | null;
  readonly iban?: string | null;
  readonly documentFooter?: string | null;
};

/** Panel: recuentos y sumas calculados en el SERVIDOR (la web no suma importes). */
export type ResumenPresupuestos = {
  readonly byStatus: Readonly<Record<EstadoPresupuesto, number>>;
  /** `OD-0062` E-08: enviados que caducan en los próximos 7 días (lo decide la API). */
  readonly expiringSoon: number;
  readonly month: {
    readonly resetsOn: string;
    readonly sentCount: number;
    readonly sentTotal: string;
    readonly acceptedCount: number;
    readonly acceptedTotal: string;
  };
  readonly recent: readonly ResumenPresupuesto[];
};

function comoResumen(cuerpo: unknown): ResumenPresupuestos | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const s = (cuerpo as Record<string, unknown>).summary as Record<string, unknown> | undefined;
  if (s === undefined || s === null || typeof s.byStatus !== "object" || typeof s.month !== "object" || !Array.isArray(s.recent)) return null;
  const m = s.month as Record<string, unknown>;
  if (typeof m.sentTotal !== "string" || typeof m.acceptedTotal !== "string") return null;
  return { ...(s as unknown as ResumenPresupuestos), expiringSoon: typeof s.expiringSoon === "number" ? s.expiringSoon : 0 };
}

/**
 * `OD-0062` E-08 y E-06: dos filtros de la lista que no son estados. Los dos
 * los decide la API: «caducan pronto» con la fecha de Madrid, y «pendientes de
 * facturar» con lo que ya está facturado.
 */
export const CADUCAN_PRONTO = "EXPIRING_SOON" as const;
export const PENDIENTES_FACTURAR = "PENDIENTES_FACTURAR" as const;
export type FiltroListaPresupuestos = EstadoPresupuesto | typeof CADUCAN_PRONTO;

/** `OD-0062` E-07: los totales de lo que se está escribiendo, sin guardar. */
export type CalculoPresupuesto = {
  readonly totales: TotalesDocumento | null;
  /** Hay líneas con retención y falta elegir el tipo: sin total hasta decidirlo. */
  readonly pendienteRetencion: boolean;
};

function comoCalculoPresupuesto(cuerpo: unknown): CalculoPresupuesto | null {
  const b = objeto(cuerpo);
  if (b === null || typeof b.pendingWithholdingDecision !== "boolean") return null;
  const tot = b.totals === null ? null : objeto(b.totals);
  if (tot === null && b.totals !== null) return null;
  if (tot !== null && (typeof tot.invoiceTotal !== "string" || typeof tot.amountToCollect !== "string" || !Array.isArray(tot.breakdown))) return null;
  return { totales: tot as unknown as TotalesDocumento | null, pendienteRetencion: b.pendingWithholdingDecision };
}

export type ClientePresupuesto = {
  readonly displayName: string;
  readonly legalName: string | null;
  readonly taxIdNormalized: string | null;
  readonly addressLine: string | null;
  readonly postalCode: string | null;
  readonly city: string | null;
  readonly province: string | null;
};

export type ResumenPresupuesto = {
  readonly id: string;
  readonly number: string;
  readonly status: EstadoPresupuesto;
  readonly customerId: string;
  readonly customerName: string | null;
  readonly issueDate: string;
  readonly validUntil: string | null;
  readonly invoiceTotal: string | null;
  readonly amountToCollect: string | null;
};

export type Presupuesto = ResumenPresupuesto & {
  readonly customer: ClientePresupuesto | null;
  readonly issuer: DatosFiscalesCopia | null;
  readonly frozen: boolean;
  readonly notes: string | null;
  readonly withholdingRate: string | null;
  readonly withholdingReason: string | null;
  readonly withholdingSuggestion: { readonly suggestedRate: string | null } | null;
  readonly pendingWithholdingDecision: boolean;
  readonly lines: readonly LineaPresupuesto[];
  readonly totals: TotalesDocumento | null;
  readonly sentAt: string | null;
  readonly cancelReason: string | null;
};

/** Lo que se envia al crear o editar: la API decide numero, estado y totales. */
export type DatosPresupuesto = {
  readonly customerId: string;
  readonly validUntil: string | null;
  readonly notes: string | null;
  readonly withholdingRate: string | null;
  readonly withholdingReason: string | null;
  readonly lines: readonly LineaPresupuesto[];
};

export type UsoCuota = {
  /**
   * `null` = sin límite numérico (`RC-0009`, planes de pago). No es lo mismo
   * que cero, y no es «ilimitado sin freno»: el freno es el límite de
   * peticiones, no el de unidades.
   */
  readonly limit: number | null;
  readonly used: number;
  /** `null` cuando no hay tope: no hay restante que contar. */
  readonly available: number | null;
  readonly warningLevel: 80 | 90 | 100 | null;
};

export type CuotasPresupuestos = {
  /** Versión concreta del plan ("FREE_V1"...); la da la API con las cuotas. */
  readonly planVersion?: string;
  readonly products: UsoCuota;
  readonly quotesCreatedThisMonth: UsoCuota & { readonly resetsOn: string };
  readonly quotesActive: UsoCuota;
};

const ESTADOS_PRESUPUESTO: readonly string[] = ["DRAFT", "SENT", "ACCEPTED", "REJECTED", "EXPIRED", "CANCELLED"];

// ---------------------------------------------------------------- facturas (TDS-CORE Parte 3)

export type EstadoFactura = "DRAFT" | "DISCARDED" | "ISSUED" | "VOIDED";
const ESTADOS_FACTURA: readonly string[] = ["DRAFT", "DISCARDED", "ISSUED", "VOIDED"];

export type EstadoDocumental = "DRAFT" | "DISCARDED" | "ISSUED" | "VOIDED" | "RECTIFIED";

export type ResumenFactura = {
  readonly id: string;
  /** null mientras es borrador: el número lo pone la emisión. */
  readonly number: string | null;
  readonly status: EstadoFactura;
  /**
   * `URS §28`: el estado DOCUMENTAL. `RECTIFIED` no es una columna —la
   * original no se toca al rectificarla—, sino "emitida y ya rectificada".
   */
  readonly documentState: EstadoDocumental;
  readonly customerId: string;
  readonly customerName: string | null;
  readonly issueDate: string | null;
  readonly invoiceTotal: string | null;
  readonly amountToCollect: string | null;
  readonly sourceQuoteId: string | null;
  readonly updatedAt: string;
};

export type Factura = ResumenFactura & {
  readonly customer: ClientePresupuesto | null;
  readonly frozen: boolean;
  readonly description: string | null;
  /** DR-09: texto propuesto por la API a partir de las líneas. */
  readonly suggestedDescription: string | null;
  readonly operationDate: string | null;
  readonly dueDate: string | null;
  readonly notes: string | null;
  readonly withholdingRate: string | null;
  readonly withholdingReason: string | null;
  readonly withholdingSuggestion: { readonly suggestedRate: string | null } | null;
  readonly pendingWithholdingDecision: boolean;
  readonly lines: readonly LineaPresupuesto[];
  /**
   * Suplidos (`OD-0052`). Opcional porque la web y el Worker se despliegan por
   * separado: una API anterior no lo manda, y entonces no hay ninguno.
   */
  readonly disbursements?: readonly Suplido[];
  /**
   * `OD-0053`: si esta factura es un ANTICIPO, de qué presupuesto y por qué
   * porcentaje ("30,00"). Ausente o `null` en cualquier otra, y en una API
   * anterior.
   */
  readonly advance?: { readonly quoteId: string; readonly quoteNumber: string | null; readonly percent: string } | null;
  /** Lo que una factura FINAL descuenta de cada anticipo (`D-03`). Ausente = nada. */
  readonly advanceDeductions?: readonly AnticipoDescontado[];
  readonly totals: TotalesDocumento | null;
  readonly sourceQuoteNumber: string | null;
  readonly discardedAt: string | null;
  /** URL del QR tributario; la genera la API al emitir (DQ-06). */
  readonly qrUrl: string | null;
  /**
   * Qué fue del registro en la cola de remisión a la AEAT (Orden art. 16.4).
   * `null` en un borrador, o cuando el registro no está en la cola — que no
   * es «enviado», es «no se sabe».
   */
  readonly aeatSubmission: EnvioRegistro | null;
  /**
   * Y lo mismo del registro de ANULACIÓN, que es otro registro. `null` si la
   * factura no está anulada. Son dos preguntas distintas: si la AEAT tiene la
   * factura, y si ya sabe que está anulada.
   */
  readonly voidSubmission: EnvioRegistro | null;
  readonly issuedAt: string | null;
  /** Anulación (TDS-FISCAL Parte 4 §3): cuándo y por qué. */
  readonly voidedAt: string | null;
  readonly voidReason: string | null;
  /**
   * Rectificativa (`TDS-FISCAL` Parte 4 §2): a qué factura rectifica, con qué
   * motivo y en qué modalidad. `null` en una factura ordinaria.
   */
  readonly rectification: {
    readonly rectifiesInvoiceId: string;
    readonly rectifiesNumber: string | null;
    readonly rootInvoiceId: string;
    readonly kind: string;
    readonly type: string;
  } | null;
  /** Las rectificativas EMITIDAS de esta factura (`URS §28`). */
  readonly rectifiedBy: ReadonlyArray<{ readonly id: string; readonly number: string | null }>;
};

/** Un descuento de la factura final: la base de un grupo de un anticipo. */
export type AnticipoDescontado = {
  readonly advanceInvoiceId: string;
  readonly advanceNumber: string | null;
  readonly advanceIssueDate: string | null;
  readonly treatment: string;
  readonly rate: string | null;
  readonly cause: string | null;
  readonly withholdingApplies: boolean;
  readonly base: string;
};

/**
 * Lo que la lista de presupuestos dice de su facturación, sin entrar en
 * cada ficha: la factura que nació del presupuesto y cuánto llevan facturado
 * sus anticipos emitidos. La API sólo manda los que tienen algo.
 */
export type FacturacionPresupuesto = {
  readonly quoteId: string;
  readonly invoiceId: string | null;
  readonly invoiceNumber: string | null;
  readonly invoiceStatus: string | null;
  /** "30,00"; null si no hay ningún anticipo emitido. */
  readonly percentAdvanced: string | null;
  /** Los números de los anticipos emitidos. Una API anterior no lo manda. */
  readonly advanceNumbers?: readonly string[];
  readonly advanceDraftPending: boolean;
};

/**
 * Los anticipos de un presupuesto y lo que se puede hacer ahora
 * (`OD-0053 D-07`, pieza 4). Lo decide la API: la pantalla no suma
 * porcentajes ni compara planes.
 */
export type AnticiposPresupuesto = {
  readonly quoteId: string;
  /** El plan incluye los anticipos. */
  readonly included: boolean;
  readonly advances: ReadonlyArray<{
    readonly invoiceId: string;
    readonly number: string | null;
    readonly status: EstadoFactura;
    readonly percent: string;
    readonly baseTotal: string | null;
    readonly amountToCollect: string | null;
    readonly operationDate: string | null;
    readonly issueDate: string | null;
    readonly createdAt: string;
  }>;
  readonly percentInvoiced: string;
  readonly percentPending: string;
  readonly finalInvoiceId: string | null;
  readonly canCreateAdvance: boolean;
  readonly canInvoiceRest: boolean;
  readonly restBlockedReason: string | null;
};

/** «Facturar un anticipo»: el porcentaje O lo cobrado, y el día del cobro. */
export type DatosAnticipo =
  | { readonly percent: string; readonly operationDate: string }
  | { readonly amount: string; readonly operationDate: string };

/** Lista L2 sin R5, que queda fuera de V1 (`DR-04`). */
export type MotivoRectificativa = "R1" | "R2" | "R3" | "R4";
/** Lista L3: `I` por diferencias, `S` por sustitución. */
export type ModalidadRectificativa = "I" | "S";

/** Lo que se envía al crear o editar un borrador: la API decide estado y totales. */
export type DatosFactura = {
  readonly customerId: string;
  readonly description: string | null;
  readonly operationDate: string | null;
  readonly dueDate: string | null;
  readonly notes: string | null;
  readonly withholdingRate: string | null;
  readonly withholdingReason: string | null;
  readonly lines: readonly LineaPresupuesto[];
  readonly disbursements: readonly SuplidoEditable[];
};

/** Orden art. 16.4: lo que queda por enviar a la AEAT, y si hubo incidencia. */
export type EstadoRemision = {
  readonly pending: number;
  readonly rejected: number;
  readonly incident: boolean;
};

/**
 * Facturas emitidas: cupo del mes y del año, y si se ha emitido alguna vez.
 *
 * `null` cuando la API no lo manda. No es lo mismo que "nunca ha emitido": la
 * web y el Worker se despliegan por separado, y decirle "te falta emitir tu
 * primera factura" a quien ya emitió sería inventarse un estado.
 */
export type Emitidas = { readonly month: UsoCuota; readonly year: UsoCuota; readonly ever: boolean };

export type UsoFacturas = {
  readonly draftsActive: UsoCuota;
  readonly remision: EstadoRemision;
  readonly issued: Emitidas | null;
  /**
   * Si el plan permite guardar suplidos (`OD-0052 D-06 B`). Lo decide la API:
   * la pantalla no compara nombres de plan (`URS §105`). Una API anterior no
   * lo manda, y se lee como `false`.
   */
  readonly disbursementsIncluded: boolean;
  /** Si el plan permite anticipos (`OD-0053 D-08 B`). Una API anterior: `false`. */
  readonly advanceInvoicesIncluded: boolean;
  /**
   * Los borradores de ANTICIPO sin emitir, para el aviso de Inicio
   * (`D-07`, pieza 5). Una API anterior: ninguno.
   */
  readonly pendingAdvanceDrafts: { readonly count: number; readonly oldestCreatedAt: string | null };
};

/** Qué fue de un registro en la cola de remisión a la AEAT. */
export type EnvioRegistro = {
  readonly status: "PENDING" | "SENT" | "REJECTED";
  readonly aeatState: string | null;
  readonly csv: string | null;
};

/** Control previo de la emisión (TDS-CORE Parte 3 §3): qué falta para emitir. */
export type MotivoControlPrevio = {
  readonly code: string;
  readonly limit?: number;
  readonly used?: number;
  readonly error?: string;
  readonly representation?: string;
};

export type ControlPrevio = {
  readonly outcome: "ALLOW_ISSUE" | "BLOCKED";
  readonly reasons: readonly MotivoControlPrevio[];
};

function comoControlPrevio(cuerpo: unknown): ControlPrevio | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const p = (cuerpo as Record<string, unknown>).preflight as Record<string, unknown> | undefined;
  if (p === undefined || (p.outcome !== "ALLOW_ISSUE" && p.outcome !== "BLOCKED") || !Array.isArray(p.reasons)) return null;
  return p.reasons.every((r) => typeof (r as Record<string, unknown>)?.code === "string") ? (p as unknown as ControlPrevio) : null;
}

function comoFactura(valor: unknown): Factura | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.id !== "string" || !ESTADOS_FACTURA.includes(v.status as string)) return null;
  if (typeof v.customerId !== "string" || !Array.isArray(v.lines) || typeof v.frozen !== "boolean") return null;
  return v as unknown as Factura;
}

function comoUnaFactura(cuerpo: unknown): Factura | null {
  return typeof cuerpo === "object" && cuerpo !== null ? comoFactura((cuerpo as Record<string, unknown>).invoice) : null;
}

/** Lo que impide anular. Su «todo bien» se llama ALLOW_VOID, no ALLOW_ISSUE. */
export type ControlAnulacion = {
  readonly outcome: "ALLOW_VOID" | "BLOCKED";
  readonly reasons: readonly MotivoControlPrevio[];
};

function comoControlAnulacion(cuerpo: unknown): ControlAnulacion | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const p = (cuerpo as Record<string, unknown>).voidPreflight as Record<string, unknown> | undefined;
  if (p === undefined || (p.outcome !== "ALLOW_VOID" && p.outcome !== "BLOCKED") || !Array.isArray(p.reasons)) return null;
  return p.reasons.every((r) => typeof (r as Record<string, unknown>)?.code === "string") ? (p as unknown as ControlAnulacion) : null;
}

function comoListaFacturas(cuerpo: unknown): readonly ResumenFactura[] | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const lista = (cuerpo as Record<string, unknown>).invoices;
  if (!Array.isArray(lista)) return null;
  const ok = lista.every((f) => {
    const v = f as Record<string, unknown>;
    return typeof v?.id === "string" && ESTADOS_FACTURA.includes(v?.status as string);
  });
  return ok ? (lista as ResumenFactura[]) : null;
}

/**
 * Un uso de cuota válido. `limit` y `available` son `number | null`: `null` es
 * «sin límite numérico», y llegó con `RC-0009` cuando los planes de pago
 * dejaron de tener tope de unidades.
 *
 * Esto exigía `number` en los tres campos, así que la primera organización que
 * llegó a un plan de pago hizo que se rechazara la respuesta ENTERA: ni plazas,
 * ni contadores, ni nombre del plan, y sin ningún error a la vista. El único
 * cliente que veía el producto roto era el que había pagado.
 */
function esUsoCuota(v: unknown): boolean {
  const c = v as Record<string, unknown> | undefined;
  if (typeof c?.used !== "number") return false;
  return (typeof c.limit === "number" || c.limit === null) && (typeof c.available === "number" || c.available === null);
}

function comoUsoFacturas(cuerpo: unknown): UsoFacturas | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const u = (cuerpo as Record<string, unknown>).usage as Record<string, unknown> | undefined;
  const d = u?.draftsActive as Record<string, unknown> | undefined;
  if (!esUsoCuota(d)) return null;
  const r = u?.remision as Record<string, unknown> | undefined;
  const em = u?.issued as Record<string, unknown> | undefined;
  const cuota = (v: unknown): UsoCuota | null => (esUsoCuota(v) ? (v as unknown as UsoCuota) : null);
  const mes = cuota(em?.month);
  const anio = cuota(em?.year);
  const pa = u?.pendingAdvanceDrafts as Record<string, unknown> | undefined;
  return {
    ...(u as unknown as UsoFacturas),
    // Una API antigua no trae remision: se lee como "nada pendiente", no como
    // un aviso inventado.
    remision: {
      pending: typeof r?.pending === "number" ? r.pending : 0,
      rejected: typeof r?.rejected === "number" ? r.rejected : 0,
      incident: r?.incident === true,
    },
    // Y si no trae las emitidas, se queda en "no se sabe" (null), nunca en cero.
    issued: mes !== null && anio !== null && typeof em?.ever === "boolean" ? { month: mes, year: anio, ever: em.ever } : null,
    disbursementsIncluded: u?.disbursementsIncluded === true,
    advanceInvoicesIncluded: u?.advanceInvoicesIncluded === true,
    pendingAdvanceDrafts: {
      count: typeof pa?.count === "number" ? pa.count : 0,
      oldestCreatedAt: typeof pa?.oldestCreatedAt === "string" ? pa.oldestCreatedAt : null,
    },
  };
}

/** Lo facturado de cada presupuesto, y los aceptados que faltan por facturar. */
export type FacturacionPresupuestos = {
  readonly quotes: readonly FacturacionPresupuesto[];
  readonly pendingQuoteIds: readonly string[];
};

function comoFacturacionPresupuestos(cuerpo: unknown): FacturacionPresupuestos | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const { quotes: lista, pendingQuoteIds: pendientes } = cuerpo as Record<string, unknown>;
  if (!Array.isArray(lista) || !Array.isArray(pendientes)) return null;
  const ok = lista.every((q) => {
    const v = q as Record<string, unknown>;
    return typeof v?.quoteId === "string" && typeof v?.advanceDraftPending === "boolean";
  });
  if (!ok || !pendientes.every((p) => typeof p === "string")) return null;
  return { quotes: lista as FacturacionPresupuesto[], pendingQuoteIds: pendientes as string[] };
}

function comoAnticiposPresupuesto(cuerpo: unknown): AnticiposPresupuesto | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const v = (cuerpo as Record<string, unknown>).quoteAdvances as Record<string, unknown> | undefined;
  if (v === undefined || typeof v.quoteId !== "string" || !Array.isArray(v.advances)) return null;
  if (typeof v.canCreateAdvance !== "boolean" || typeof v.canInvoiceRest !== "boolean" || typeof v.percentPending !== "string") return null;
  return v as unknown as AnticiposPresupuesto;
}

function comoPresupuesto(valor: unknown): Presupuesto | null {
  if (typeof valor !== "object" || valor === null) return null;
  const v = valor as Record<string, unknown>;
  if (typeof v.id !== "string" || typeof v.number !== "string" || !ESTADOS_PRESUPUESTO.includes(v.status as string)) return null;
  if (typeof v.customerId !== "string" || typeof v.issueDate !== "string" || !Array.isArray(v.lines)) return null;
  if (typeof v.frozen !== "boolean") return null;
  return v as unknown as Presupuesto;
}

function comoUnPresupuesto(cuerpo: unknown): Presupuesto | null {
  return typeof cuerpo === "object" && cuerpo !== null ? comoPresupuesto((cuerpo as Record<string, unknown>).quote) : null;
}

function comoListaPresupuestos(cuerpo: unknown): readonly ResumenPresupuesto[] | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const lista = (cuerpo as Record<string, unknown>).quotes;
  if (!Array.isArray(lista)) return null;
  const ok = lista.every((q) => {
    const v = q as Record<string, unknown>;
    return typeof v?.id === "string" && typeof v?.number === "string" && ESTADOS_PRESUPUESTO.includes(v?.status as string);
  });
  return ok ? (lista as ResumenPresupuesto[]) : null;
}

function comoCuotas(cuerpo: unknown): CuotasPresupuestos | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const u = (cuerpo as Record<string, unknown>).usage as Record<string, unknown> | undefined;
  if (u === undefined || u === null) return null;
  return esUsoCuota(u.products) && esUsoCuota(u.quotesCreatedThisMonth) && esUsoCuota(u.quotesActive)
    ? (u as unknown as CuotasPresupuestos)
    : null;
}

// ------------------------------------------- plan de pago (OD-0014 A-10/A-11)

export type PlanDePago = "FACTURA_PLUS" | "BUSINESS";

/** Los estados locales de `OD-0014` D-02. Los escribe el webhook, no esta app. */
export type EstadoLocalSuscripcion = "ACTIVE" | "PAST_DUE" | "GRACE_EXPIRED" | "CANCELED" | "ENDED";
const ESTADOS_SUSCRIPCION: readonly string[] = ["ACTIVE", "PAST_DUE", "GRACE_EXPIRED", "CANCELED", "ENDED"];

export type Suscripcion = {
  readonly state: EstadoLocalSuscripcion;
  readonly currentPeriodEnd: string | null;
  readonly updatedAt: string;
};

/**
 * `suscripcion: null` = no hay ninguna registrada: quien está en Free.
 *
 * `upgrades` son los planes a los que se puede subir, **decididos por el
 * servidor** (`URS §105`): esta aplicación no compara nombres de plan ni sabe
 * cuál está por encima de cuál. Pinta los botones que le llegan y manda de
 * vuelta el mismo identificador. Vacío = no hay nada que ofrecer.
 *
 * `contratables` es su pareja para quien NO tiene suscripción viva: los planes
 * que tiene sentido ofrecer en un Checkout nuevo. Es lo que el panel pone a la
 * vista como «Pasar a…»; el gate sigue siendo el `POST` de contratar.
 */
export type EstadoSuscripcion = {
  readonly suscripcion: Suscripcion | null;
  readonly upgrades: readonly PlanDePago[];
  readonly contratables: readonly PlanDePago[];
  /**
   * `OD-0064` D-07: la prueba de Business EN CURSO, o `null`. `endsAt` es la
   * medianoche de Madrid en que empieza Free; `diasRestantes` lo cuenta el
   * servidor, con su reloj. Una API anterior no lo manda: `null`.
   */
  readonly prueba: PruebaDeBusiness | null;
};

/** `clientesFree`: lo que admite el Free al que baja. `null` si no se sabe: entonces no se dice nada de elegir. */
export type PruebaDeBusiness = {
  readonly endsAt: string;
  /** El último día de Business, `AAAA-MM-DD` en Madrid, ya calculado por el servidor. */
  readonly ultimoDia: string;
  readonly diasRestantes: number;
  readonly clientesFree: number | null;
};

function planesDePago(v: unknown): readonly PlanDePago[] {
  return (Array.isArray(v) ? v : []).filter((p): p is PlanDePago => p === "FACTURA_PLUS" || p === "BUSINESS");
}

function comoPrueba(v: unknown): PruebaDeBusiness | null {
  if (typeof v !== "object" || v === null) return null;
  const p = v as Record<string, unknown>;
  if (typeof p.endsAt !== "string" || typeof p.ultimoDia !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(p.ultimoDia)) return null;
  if (typeof p.diasRestantes !== "number" || !Number.isInteger(p.diasRestantes)) return null;
  return {
    endsAt: p.endsAt,
    ultimoDia: p.ultimoDia,
    diasRestantes: p.diasRestantes,
    clientesFree: typeof p.clientesFree === "number" && Number.isInteger(p.clientesFree) ? p.clientesFree : null,
  };
}

function comoEstadoSuscripcion(cuerpo: unknown): EstadoSuscripcion | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const s = (cuerpo as Record<string, unknown>).subscription;
  const upgrades = planesDePago((cuerpo as Record<string, unknown>).upgrades);
  const contratables = planesDePago((cuerpo as Record<string, unknown>).contratables);
  const prueba = comoPrueba((cuerpo as Record<string, unknown>).prueba);
  if (s === null || s === undefined) return { suscripcion: null, upgrades, contratables, prueba };
  if (typeof s !== "object") return null;
  const v = s as Record<string, unknown>;
  if (typeof v.state !== "string" || !ESTADOS_SUSCRIPCION.includes(v.state)) return null;
  return {
    upgrades,
    contratables,
    prueba,
    suscripcion: {
      state: v.state as EstadoLocalSuscripcion,
      currentPeriodEnd: typeof v.currentPeriodEnd === "string" ? v.currentPeriodEnd : null,
      updatedAt: typeof v.updatedAt === "string" ? v.updatedAt : "",
    },
  };
}

/**
 * A dónde se deja salir al navegador.
 *
 * Contratar y gestionar el pago terminan en una redirección FUERA del
 * producto, y es el único sitio donde esta aplicación manda a nadie a otro
 * dominio. La URL la compone Stripe y la API ya comprueba que sea `https`,
 * pero se vuelve a comprobar aquí: si algún día la respuesta trajera otra
 * cosa, esto sería un redirector abierto con la marca de Faktelio delante.
 */
/**
 * `D3-b`: los datos fiscales del suscriptor.
 *
 * `territorioIva` **no es un país**: dentro de España hay tres territorios con
 * impuesto distinto, y `OD-0029` `D-01` sólo soporta Península y Baleares. El
 * país del domicilio va aparte y **no localiza nada** — la Decisión 3 de la
 * asesoría lo dice con esas palabras.
 */
export const TERRITORIOS_IVA = [
  "ES_PENINSULA_BALEARES",
  "ES_CANARIAS",
  "ES_CEUTA_MELILLA",
  "OTRO_UE",
  "FUERA_UE",
] as const;
export type TerritorioIva = (typeof TERRITORIOS_IVA)[number];

export const CONDICIONES_DESTINATARIO = ["EMPRESARIO", "PARTICULAR"] as const;
export type CondicionDestinatario = (typeof CONDICIONES_DESTINATARIO)[number];

/** Los SEIS tipos: un suscriptor extranjero se puede registrar, aunque todavía no se le pueda facturar. */
export const TIPOS_ID_SUSCRIPTOR = ["NIF_ES_PERSON", "NIE_ES", "NIF_ES_ENTITY", "VAT_EU", "FOREIGN_ID", "OTHER"] as const;
export type TipoIdSuscriptor = (typeof TIPOS_ID_SUSCRIPTOR)[number];

export type DatosFiscalesSuscriptor = {
  readonly territorioIva: TerritorioIva;
  readonly condicion: CondicionDestinatario;
  readonly razonSocial: string;
  readonly taxCountry: string;
  readonly taxIdType: TipoIdSuscriptor;
  readonly taxIdValue: string;
  readonly addressLine: string;
  readonly postalCode: string;
  readonly city: string;
  readonly province: string | null;
  readonly country: string;
};

/**
 * Lo que la API contesta. **`puedeContratar` lo decide el servidor**: esta
 * aplicación no sabe qué territorios deja fuera `OD-0029` `D-01`, y no debe
 * aprenderlo — un segundo criterio en el cliente es un criterio que se
 * desincroniza.
 */
export type EstadoDatosFiscales = {
  readonly perfil: DatosFiscalesSuscriptor | null;
  readonly puedeContratar: boolean;
  readonly motivo: string | null;
};

function comoEstadoDatosFiscales(cuerpo: unknown): EstadoDatosFiscales | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const c = cuerpo as Record<string, unknown>;
  const puedeContratar = c.puedeContratar === true;
  const motivo = typeof c.motivo === "string" ? c.motivo : null;
  const p = c.perfil;
  if (p === null || p === undefined) return { perfil: null, puedeContratar, motivo };
  if (typeof p !== "object") return null;
  const v = p as Record<string, unknown>;
  const texto = (x: unknown): string => (typeof x === "string" ? x : "");
  if (!TERRITORIOS_IVA.includes(texto(v.territorioIva) as TerritorioIva)) return null;
  if (!CONDICIONES_DESTINATARIO.includes(texto(v.condicion) as CondicionDestinatario)) return null;
  if (!TIPOS_ID_SUSCRIPTOR.includes(texto(v.taxIdType) as TipoIdSuscriptor)) return null;
  return {
    puedeContratar,
    motivo,
    perfil: {
      territorioIva: texto(v.territorioIva) as TerritorioIva,
      condicion: texto(v.condicion) as CondicionDestinatario,
      razonSocial: texto(v.razonSocial),
      taxCountry: texto(v.taxCountry),
      taxIdType: texto(v.taxIdType) as TipoIdSuscriptor,
      taxIdValue: texto(v.taxIdValue),
      addressLine: texto(v.addressLine),
      postalCode: texto(v.postalCode),
      city: texto(v.city),
      province: typeof v.province === "string" ? v.province : null,
      country: texto(v.country),
    },
  };
}

/**
 * La bajada a Free (`TDS-FREE-DOWNGRADE` §14). Todo lo decide el SERVIDOR: la
 * fecha, el límite de clientes, si ya está congelada y el orden de los
 * candidatos, que es el mismo que usaría la selección automática. Esta
 * aplicación solo enseña y manda de vuelta los ids elegidos.
 *
 * `bajada: null` = no hay ninguna programada. `ultima` es la última ya hecha,
 * para los avisos de después (OD-FREE-002 §19.4 y §19.5).
 */
export type CandidatoDeBajada = {
  readonly customerId: string;
  readonly displayName: string;
  readonly lastActivityAt: string | null;
};

export type VistaDeBajada = {
  readonly bajada: {
    readonly effectiveAt: string;
    readonly congelada: boolean;
    readonly limite: number | null;
    readonly activos: number;
    readonly sobran: boolean;
    readonly seleccion: readonly string[];
    readonly candidatos: readonly CandidatoDeBajada[];
  } | null;
  readonly ultima: {
    readonly committedAt: string | null;
    readonly selectionMode: string | null;
    readonly limite: number | null;
    readonly restauracion: "NINGUNA" | "PENDIENTE" | "HECHA" | "BLOQUEADA";
  } | null;
};

const RESTAURACIONES: readonly string[] = ["NINGUNA", "PENDIENTE", "HECHA", "BLOQUEADA"];

function soloTextos(v: unknown): readonly string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function comoVistaDeBajada(cuerpo: unknown): VistaDeBajada | null {
  if (typeof cuerpo !== "object" || cuerpo === null) return null;
  const c = cuerpo as Record<string, unknown>;
  let bajada: VistaDeBajada["bajada"] = null;
  if (typeof c.bajada === "object" && c.bajada !== null) {
    const b = c.bajada as Record<string, unknown>;
    if (typeof b.effectiveAt !== "string") return null;
    const candidatos = (Array.isArray(b.candidatos) ? b.candidatos : []).flatMap((x): CandidatoDeBajada[] => {
      if (typeof x !== "object" || x === null) return [];
      const v = x as Record<string, unknown>;
      if (typeof v.customerId !== "string" || typeof v.displayName !== "string") return [];
      return [{ customerId: v.customerId, displayName: v.displayName, lastActivityAt: typeof v.lastActivityAt === "string" ? v.lastActivityAt : null }];
    });
    bajada = {
      effectiveAt: b.effectiveAt,
      // Ante la duda, congelada: no se invita a un cambio que el servidor va a rechazar.
      congelada: b.congelada !== false,
      limite: typeof b.limite === "number" ? b.limite : null,
      activos: typeof b.activos === "number" ? b.activos : candidatos.length,
      sobran: b.sobran === true,
      seleccion: soloTextos(b.seleccion),
      candidatos,
    };
  }
  let ultima: VistaDeBajada["ultima"] = null;
  if (typeof c.ultima === "object" && c.ultima !== null) {
    const u = c.ultima as Record<string, unknown>;
    ultima = {
      committedAt: typeof u.committedAt === "string" ? u.committedAt : null,
      selectionMode: typeof u.selectionMode === "string" ? u.selectionMode : null,
      limite: typeof u.limite === "number" ? u.limite : null,
      restauracion: typeof u.restauracion === "string" && RESTAURACIONES.includes(u.restauracion) ? (u.restauracion as "NINGUNA") : "NINGUNA",
    };
  }
  return { bajada, ultima };
}

const DESTINOS_DE_PAGO: readonly string[] = ["https://checkout.stripe.com/", "https://billing.stripe.com/"];

export function soloStripe(cuerpo: unknown): { readonly url: string } | null {
  const url = typeof cuerpo === "object" && cuerpo !== null ? (cuerpo as Record<string, unknown>).url : null;
  if (typeof url !== "string") return null;
  return DESTINOS_DE_PAGO.some((d) => url.startsWith(d)) ? { url } : null;
}

/**
 * El programa para gestorías (`OD-0063` D-01, D-03 y D-04). `programa` dice si
 * está encendido en este entorno (D-11 A: solo staging hasta que el abogado
 * revise las condiciones); apagado, la pantalla no se ofrece.
 */
export type AccesoDeAsesoria = {
  readonly programa: boolean;
  readonly incluidoEnElPlan: boolean;
  readonly acceso: null | {
    readonly asesoria: string | null;
    readonly desde: string;
    /** Los correos de quien de la gestoría lleva la empresa. */
    readonly personas: readonly string[];
  };
  /**
   * Las gestorías que entraron por la invitación que mandó la empresa, para
   * darles acceso con un clic (`OD-0063` D-04, enmienda del 2026-10-28). Solo
   * cuando la empresa no ha dado acceso a ninguna.
   */
  readonly invitaciones: readonly InvitacionAceptada[];
};

export type InvitacionAceptada = { readonly invitacion: string; readonly asesoria: string | null; readonly desde: string };

/** El enlace que el cliente manda a su gestoría con su propio correo. */
export type InvitacionCreada = { readonly token: string; readonly caduca: string; readonly empresa: string | null };

/** La gestoría entró por ese enlace: de qué cliente era, y si ya puede recibir acceso. */
export type InvitacionDeClienteAceptada = { readonly cliente: string | null; readonly perfilActivo: boolean };

/**
 * Qué gestoría trajo a la empresa (`OD-0063` D-08). `codigo` es el de esa
 * gestoría, para darle acceso desde la tarjeta del panel; `null` si está
 * suspendida.
 */
export type AtribucionDeAsesoriaWeb = {
  readonly programa: boolean;
  readonly atribucion: null | { readonly asesoria: string | null; readonly codigo: string | null; readonly desde: string };
};

export type PerfilDeAsesoriaWeb = {
  readonly codigo: string;
  readonly estado: "ACTIVE" | "SUSPENDED";
  readonly desde: string;
};

export type EstadoPerfilAsesoria = {
  readonly programa: boolean;
  /** La versión de las condiciones que se aceptan al activar el perfil. */
  readonly condiciones: string;
  readonly perfil: PerfilDeAsesoriaWeb | null;
  /** Los clientes que la invitaron y todavía no le han dado acceso. */
  readonly invitadaPor: readonly string[];
};

export type PersonaDeAsesoria = { readonly userId: string; readonly email: string | null };

export type ClienteDeAsesoria = {
  readonly organizationId: string;
  readonly nombre: string | null;
  readonly desde: string;
  /** `OD-0063` D-03: sin Business no hay acceso, y la fila sale sin datos. */
  readonly enBusiness: boolean;
  readonly personas: readonly PersonaDeAsesoria[];
};

export type ClientesDeAsesoria = {
  readonly perfil: PerfilDeAsesoriaWeb | null;
  readonly clientes: readonly ClienteDeAsesoria[];
  /** Las personas del equipo entre las que se reparte. */
  readonly equipo: readonly PersonaDeAsesoria[];
};

function comoPerfilAsesoria(v: unknown): PerfilDeAsesoriaWeb | null {
  const o = objeto(v);
  if (o === null || typeof o.codigo !== "string" || typeof o.desde !== "string") return null;
  if (o.estado !== "ACTIVE" && o.estado !== "SUSPENDED") return null;
  return { codigo: o.codigo, estado: o.estado, desde: o.desde };
}

function comoPersonas(v: unknown): PersonaDeAsesoria[] {
  return (Array.isArray(v) ? v : []).flatMap((p) => {
    const o = objeto(p);
    return o !== null && typeof o.userId === "string"
      ? [{ userId: o.userId, email: typeof o.email === "string" ? o.email : null }]
      : [];
  });
}

function comoAccesoAsesoria(cuerpo: unknown): AccesoDeAsesoria | null {
  const o = objeto(cuerpo);
  if (o === null || !("acceso" in o)) return null;
  const a = objeto(o.acceso);
  return {
    // Falla cerrado: si la API no lo afirma, no hay programa ni plan.
    programa: o.programa === true,
    incluidoEnElPlan: o.incluidoEnElPlan === true,
    acceso:
      a === null || typeof a.desde !== "string"
        ? null
        : {
            asesoria: typeof a.asesoria === "string" ? a.asesoria : null,
            desde: a.desde,
            personas: (Array.isArray(a.personas) ? a.personas : []).filter((x): x is string => typeof x === "string"),
          },
    invitaciones: (Array.isArray(o.invitaciones) ? o.invitaciones : []).flatMap((i) => {
      const x = objeto(i);
      return x !== null && typeof x.invitacion === "string" && typeof x.desde === "string"
        ? [{ invitacion: x.invitacion, asesoria: typeof x.asesoria === "string" ? x.asesoria : null, desde: x.desde }]
        : [];
    }),
  };
}

function comoAtribucionAsesoria(cuerpo: unknown): AtribucionDeAsesoriaWeb | null {
  const o = objeto(cuerpo);
  if (o === null || !("atribucion" in o)) return null;
  const a = objeto(o.atribucion);
  return {
    programa: o.programa === true,
    atribucion:
      a === null || typeof a.desde !== "string"
        ? null
        : {
            asesoria: typeof a.asesoria === "string" ? a.asesoria : null,
            codigo: typeof a.codigo === "string" ? a.codigo : null,
            desde: a.desde,
          },
  };
}

function comoEstadoPerfilAsesoria(cuerpo: unknown): EstadoPerfilAsesoria | null {
  const o = objeto(cuerpo);
  if (o === null || typeof o.condiciones !== "string" || !("perfil" in o)) return null;
  return {
    programa: o.programa === true,
    condiciones: o.condiciones,
    perfil: comoPerfilAsesoria(o.perfil),
    invitadaPor: (Array.isArray(o.invitadaPor) ? o.invitadaPor : []).filter((x): x is string => typeof x === "string"),
  };
}

function comoClientesAsesoria(cuerpo: unknown): ClientesDeAsesoria | null {
  const o = objeto(cuerpo);
  if (o === null || !Array.isArray(o.clientes)) return null;
  return {
    perfil: comoPerfilAsesoria(o.perfil),
    clientes: o.clientes.flatMap((c) => {
      const x = objeto(c);
      return x !== null && typeof x.organizationId === "string" && typeof x.desde === "string"
        ? [
            {
              organizationId: x.organizationId,
              nombre: typeof x.nombre === "string" ? x.nombre : null,
              desde: x.desde,
              enBusiness: x.enBusiness === true,
              personas: comoPersonas(x.personas),
            },
          ]
        : [];
    }),
    equipo: comoPersonas(o.equipo),
  };
}

/** Una respuesta que es un objeto, sin más: la acción ya dice lo que pasó con el estado HTTP. */
const hecho = (cuerpo: unknown): true | null => (objeto(cuerpo) === null ? null : true);

export const api = {
  sesion: (): Promise<Resultado<Sesion>> => pedir("/api/v1/me", { method: "GET" }, comoSesion),

  login: (email: string, password: string): Promise<Resultado<RespuestaLogin>> =>
    pedir("/api/v1/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }, comoLogin),

  loginMfa: (challenge: string, code: string): Promise<Resultado<RespuestaLogin>> =>
    pedir("/api/v1/auth/login/mfa", { method: "POST", body: JSON.stringify({ challenge, code }) }, comoLogin),

  /**
   * 202 idéntico exista o no la cuenta (`OD-0027 D-04`): la pantalla no puede distinguirlo, y no debe.
   * `origen` es la página del sitio de la que viene; solo se cuenta (`global/0021`).
   */
  registro: (email: string, password: string, origen: string | null = null): Promise<Resultado<true>> =>
    pedir(
      "/api/v1/auth/register",
      { method: "POST", body: JSON.stringify(origen === null ? { email, password } : { email, password, origen }) },
      estadoEs("ACCEPTED"),
    ),

  verificarEmail: (token: string): Promise<Resultado<true>> =>
    pedir("/api/v1/auth/verify-email", { method: "POST", body: JSON.stringify({ token }) }, estadoEs("VERIFIED")),

  /** 202 idéntico exista o no la cuenta (`OD-0027 D-04`). */
  pedirRecuperacion: (email: string): Promise<Resultado<true>> =>
    pedir("/api/v1/auth/password-recovery", { method: "POST", body: JSON.stringify({ email }) }, estadoEs("ACCEPTED")),

  /** El estado registrado de la suscripción. Exige OWNER: un 403 es la respuesta normal para quien no lo es. */
  suscripcion: (): Promise<Resultado<EstadoSuscripcion>> =>
    pedir("/api/v1/billing/subscription", { method: "GET" }, comoEstadoSuscripcion),

  /** Se manda el PLAN, nunca un precio ni un importe: el precio lo pone la configuración del servidor (A-11). */
  contratar: (plan: PlanDePago): Promise<Resultado<{ readonly url: string }>> =>
    pedir("/api/v1/billing/checkout", { method: "POST", body: JSON.stringify({ plan }) }, soloStripe),

  /**
   * Subir de plan teniendo ya suscripción. No devuelve URL: Stripe cobra la
   * diferencia y el plan lo sube el webhook, así que la pantalla se queda
   * esperando la confirmación igual que tras un pago.
   */
  cambiarPlan: (plan: PlanDePago): Promise<Resultado<true>> =>
    pedir("/api/v1/billing/subscription/plan", { method: "POST", body: JSON.stringify({ plan }) }, estadoEs("PROCESANDO")),

  /** Portal de Stripe: cambiar la tarjeta y cancelar (A-01). */
  portalDePago: (): Promise<Resultado<{ readonly url: string }>> =>
    pedir("/api/v1/billing/portal", { method: "POST" }, soloStripe),

  /** La bajada a Free programada, si hay. Exige OWNER, como la suscripción. */
  bajadaAFree: (): Promise<Resultado<VistaDeBajada>> => pedir("/api/v1/plan/downgrade", { method: "GET" }, comoVistaDeBajada),

  /**
   * Qué clientes se quedan operativos: la selección ENTERA, que sustituye a la
   * anterior. No archiva a nadie: lo hace el corte, en la fecha, con lo que
   * haya guardado entonces.
   */
  guardarSeleccionDeBajada: (customerIds: readonly string[]): Promise<Resultado<readonly string[]>> =>
    pedir("/api/v1/plan/downgrade/selection", { method: "PUT", body: JSON.stringify({ customerIds }) }, (cuerpo) =>
      typeof cuerpo === "object" && cuerpo !== null && Array.isArray((cuerpo as Record<string, unknown>).seleccion)
        ? soloTextos((cuerpo as Record<string, unknown>).seleccion)
        : null,
    ),

  /** `D3-b`: lo declarado, y si con eso se puede contratar. Lo decide el servidor. */
  datosFiscales: (): Promise<Resultado<EstadoDatosFiscales>> =>
    pedir("/api/v1/billing/datos-fiscales", { method: "GET" }, comoEstadoDatosFiscales),

  /**
   * Declararlos. Sin esto **no hay checkout**: la Decisión 3 de la asesoría
   * exige impedir formalizar una operación cuya localización fiscal esté
   * indeterminada, y aquí no se presume ninguna.
   */
  guardarDatosFiscales: (datos: DatosFiscalesSuscriptor): Promise<Resultado<{ readonly puedeContratar: boolean }>> =>
    pedir("/api/v1/billing/datos-fiscales", { method: "POST", body: JSON.stringify(datos) }, (cuerpo) => {
      if (typeof cuerpo !== "object" || cuerpo === null) return null;
      const c = cuerpo as Record<string, unknown>;
      return c.status === "GUARDADO" ? { puedeContratar: c.puedeContratar === true } : null;
    }),

  /** Cierra TODAS las sesiones del usuario (`OD-0027 D-05`). */
  completarRecuperacion: (token: string, password: string): Promise<Resultado<true>> =>
    pedir(
      "/api/v1/auth/password-recovery/complete",
      { method: "POST", body: JSON.stringify({ token, password }) },
      estadoEs("PASSWORD_CHANGED"),
    ),

  /**
   * `OD-0028`: 201 CREATED. La organización y el rol OWNER los decide la API, no el cliente.
   * `asesoria`: el código del enlace de una gestoría (`OD-0063` D-08). La API
   * decide si atribuye; el alta no depende de ello.
   */
  crearOrganizacion: (name: string, asesoria: string | null = null): Promise<Resultado<true>> =>
    pedir(
      "/api/v1/organizations",
      { method: "POST", body: JSON.stringify(asesoria === null ? { name } : { name, asesoria }) },
      estadoEs("CREATED"),
    ),

  /** TDS-CORE DC-09. El secreto y los códigos NUNCA se guardan en el dispositivo (ADR-0004). */
  estadoMfa: (): Promise<Resultado<EstadoMfa>> => pedir("/api/v1/account/mfa", { method: "GET" }, comoEstadoMfa),

  empezarMfa: (): Promise<Resultado<{ readonly secreto: string; readonly uri: string }>> =>
    pedir("/api/v1/account/mfa/totp/begin", { method: "POST", body: JSON.stringify({}) }, comoAltaMfa),

  /** Al confirmar, la API cierra todas las sesiones y renueva la cookie de ésta. */
  confirmarMfa: (code: string): Promise<Resultado<readonly string[]>> =>
    pedir("/api/v1/account/mfa/totp/confirm", { method: "POST", body: JSON.stringify({ code }) }, comoCodigos),

  /**
   * Herramientas internas (OD-0020 D-02). Para quien no es operador, la API
   * responde 404: esta pantalla no distingue ese caso de "no hay sesion".
   */
  pendientesInternos: (): Promise<Resultado<readonly PendienteOperador[]>> =>
    pedir("/api/v1/internal/tax-representations", { method: "GET" }, comoPendientes),

  revisarInterno: (
    organizationId: string,
    decision: "ACTIVE" | "REJECTED",
    reason?: MotivoRechazoAutorizacion,
  ): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/internal/tax-representations/${encodeURIComponent(organizationId)}/review`,
      { method: "POST", body: JSON.stringify(reason === undefined ? { decision } : { decision, reason }) },
      () => true as const,
    ),

  /** TDS-CORE Parte 4: autorizacion para que Faktelio remita a la AEAT. */
  autorizacionAeat: (): Promise<Resultado<Autorizacion>> =>
    pedir("/api/v1/tax-representation", { method: "GET" }, comoAutorizacion),

  /** Solo el OWNER. La ruta 3 (firma online) todavia no se admite. */
  elegirAutorizacion: (route: "APODERAMIENTO"): Promise<Resultado<Autorizacion>> =>
    pedir("/api/v1/tax-representation/route", { method: "POST", body: JSON.stringify({ route }) }, comoAutorizacion),

  /**
   * Manda los pasos del apoderamiento por correo (OD-0039): a uno mismo, para
   * hacerlo desde el movil, o a la gestoria, que suele hacer estos tramites.
   */
  enviarInstruccionesAutorizacion: (destino: "YO" | "GESTORIA", email: string): Promise<Resultado<{ readonly enviado: boolean }>> =>
    pedir(
      "/api/v1/tax-representation/instrucciones",
      { method: "POST", body: JSON.stringify({ destino, email }) },
      (cuerpo) => (typeof cuerpo === "object" && cuerpo !== null ? { enviado: (cuerpo as { enviado?: unknown }).enviado === true } : null),
    ),

  /** "Ya lo he hecho en la Sede": pasa a comprobacion, NO activa nada. */
  declararAutorizacion: (): Promise<Resultado<Autorizacion>> =>
    pedir("/api/v1/tax-representation/declare", { method: "POST", body: JSON.stringify({}) }, comoAutorizacion),

  retirarAutorizacion: (): Promise<Resultado<Autorizacion>> =>
    pedir("/api/v1/tax-representation/revoke", { method: "POST", body: JSON.stringify({}) }, comoAutorizacion),

  /** Q-04: público, sin sesión (también lo usa la página /declaracion-responsable). */
  acercaDe: (): Promise<Resultado<AcercaDeSif>> => pedir("/api/v1/sif", { method: "GET" }, comoAcercaDe),

  /** TDS-CORE §2. Editar y confirmar exigen OWNER y MFA: la API lo decide, no esta pantalla. */
  obtenerEmisor: (): Promise<Resultado<{ readonly emisor: Emisor | null }>> =>
    pedir("/api/v1/legal-entity", { method: "GET" }, comoRespuestaEmisor),

  guardarEmisor: (datos: DatosEmisor): Promise<Resultado<{ readonly emisor: Emisor | null }>> =>
    pedir("/api/v1/legal-entity", { method: "PUT", body: JSON.stringify(datos) }, comoRespuestaEmisor),

  confirmarEmisor: (): Promise<Resultado<{ readonly emisor: Emisor | null }>> =>
    pedir("/api/v1/legal-entity/confirm", { method: "POST", body: JSON.stringify({}) }, comoRespuestaEmisor),

  /**
   * El logotipo del emisor (`RC-0009`).
   *
   * `incluidoEnElPlan` lo decide LA API y llega ya resuelto: `URS §105` prohíbe
   * que el navegador compare nombres de plan para decidir qué ofrece.
   */
  estadoLogo: (): Promise<Resultado<EstadoLogo>> =>
    pedir("/api/v1/legal-entity/logo/estado", { method: "GET" }, comoEstadoLogo),

  /**
   * Sube el logotipo: los bytes tal cual, no dentro de un JSON.
   *
   * `application/octet-stream` no es casualidad ni adorno: el guard CSRF del
   * servidor sólo acepta tipos que CORS obliga a preflight, y éste lo es. El
   * nombre del fichero va en una cabecera porque es un DATO del usuario, no
   * parte de ninguna ruta.
   */
  subirLogo: (fichero: File): Promise<Resultado<{ readonly logo: Logo }>> =>
    pedir(
      "/api/v1/legal-entity/logo",
      {
        method: "PUT",
        body: fichero,
        headers: { "content-type": "application/octet-stream", "x-nombre-archivo": nombreSeguro(fichero.name) },
      },
      (cuerpo) => {
        const l = comoLogo((cuerpo as { logo?: unknown } | null)?.logo);
        return l === null ? null : { logo: l };
      },
    ),

  quitarLogo: (): Promise<Resultado<{ readonly borrado: boolean }>> =>
    pedir("/api/v1/legal-entity/logo", { method: "DELETE" }, (cuerpo) =>
      typeof cuerpo === "object" && cuerpo !== null ? { borrado: (cuerpo as { borrado?: unknown }).borrado === true } : null,
    ),

  /** `OD-0063` D-04: a qué gestoría ha dado acceso la empresa, y quién la lleva. */
  accesoAsesoria: (): Promise<Resultado<AccesoDeAsesoria>> =>
    pedir("/api/v1/asesoria/acceso", { method: "GET" }, comoAccesoAsesoria),

  /** Dar acceso por el código de la gestoría. `sustituir` solo tras avisar de que la anterior lo pierde. */
  /** D-08: «INVITACION» la primera vez, «REENVIO» la segunda; la tercera, 409. */
  invitarClienteAsesoria: (correo: string): Promise<Resultado<"INVITACION" | "REENVIO">> =>
    pedir("/api/v1/asesoria/invitar", { method: "POST", body: JSON.stringify({ correo }) }, (cuerpo) => {
      const e = objeto(cuerpo)?.enviada;
      return e === "INVITACION" || e === "REENVIO" ? e : null;
    }),

  atribucionAsesoria: (): Promise<Resultado<AtribucionDeAsesoriaWeb>> =>
    pedir("/api/v1/asesoria/atribucion", { method: "GET" }, comoAtribucionAsesoria),

  darAccesoAsesoria: (codigo: string, sustituir: boolean): Promise<Resultado<true>> =>
    pedir("/api/v1/asesoria/acceso", { method: "POST", body: JSON.stringify({ codigo, sustituir }) }, hecho),

  retirarAccesoAsesoria: (): Promise<Resultado<true>> =>
    pedir("/api/v1/asesoria/acceso/retirar", { method: "POST", body: "{}" }, hecho),

  /** D-04 (enmienda 2026-10-28): dar acceso, con un clic, a la gestoría que entró por la invitación de la empresa. */
  darAccesoPorInvitacion: (invitacion: string): Promise<Resultado<true>> =>
    pedir("/api/v1/asesoria/acceso", { method: "POST", body: JSON.stringify({ invitacion }) }, hecho),

  /** El cliente crea el enlace para su gestoría; el correo lo manda él desde el suyo. */
  invitarAMiAsesoria: (): Promise<Resultado<InvitacionCreada>> =>
    pedir("/api/v1/asesoria/acceso/invitacion", { method: "POST", body: "{}" }, (cuerpo) => {
      const o = objeto(cuerpo);
      return o !== null && typeof o.token === "string" && typeof o.caduca === "string"
        ? { token: o.token, caduca: o.caduca, empresa: typeof o.empresa === "string" ? o.empresa : null }
        : null;
    }),

  /** La gestoría entra por el enlace de su cliente. No da acceso: lo da el cliente después. */
  aceptarInvitacionDeCliente: (token: string): Promise<Resultado<InvitacionDeClienteAceptada>> =>
    pedir("/api/v1/asesoria/invitacion/aceptar", { method: "POST", body: JSON.stringify({ token }) }, (cuerpo) => {
      const o = objeto(cuerpo);
      return o !== null && o.aceptada === true
        ? { cliente: typeof o.cliente === "string" ? o.cliente : null, perfilActivo: o.perfilActivo === true }
        : null;
    }),

  /** `OD-0063` D-01: el perfil de asesoría de la empresa, si lo tiene. */
  perfilAsesoria: (): Promise<Resultado<EstadoPerfilAsesoria>> =>
    pedir("/api/v1/asesoria/perfil", { method: "GET" }, comoEstadoPerfilAsesoria),

  activarPerfilAsesoria: (condiciones: string): Promise<Resultado<true>> =>
    pedir("/api/v1/asesoria/perfil", { method: "POST", body: JSON.stringify({ aceptaCondiciones: true, condiciones }) }, hecho),

  clientesAsesoria: (): Promise<Resultado<ClientesDeAsesoria>> =>
    pedir("/api/v1/asesoria/clientes", { method: "GET" }, comoClientesAsesoria),

  asignarPersonaAsesoria: (organizationId: string, userId: string): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/asesoria/clientes/${encodeURIComponent(organizationId)}/personas`,
      { method: "POST", body: JSON.stringify({ userId }) },
      hecho,
    ),

  quitarPersonaAsesoria: (organizationId: string, userId: string): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/asesoria/clientes/${encodeURIComponent(organizationId)}/personas/${encodeURIComponent(userId)}/quitar`,
      { method: "POST", body: "{}" },
      hecho,
    ),

  renunciarClienteAsesoria: (organizationId: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/asesoria/clientes/${encodeURIComponent(organizationId)}/renunciar`, { method: "POST", body: "{}" }, hecho),

  /**
   * La exportación para la gestoría (`OD-0036`). Igual que el logotipo,
   * `incluidoEnElPlan` llega ya resuelto: el navegador no compara planes.
   */
  estadoExportacion: (): Promise<Resultado<EstadoExportacion>> =>
    pedir("/api/v1/accountant-export/estado", { method: "GET" }, comoEstadoExportacion),

  /**
   * Descarga el listado (CSV) o el paquete (ZIP) del periodo.
   *
   * No es un enlace directo, como el PDF de una factura, por los errores: un
   * periodo con demasiadas facturas para el paquete, o un plan que no lo
   * incluye, tienen que decirse en la pantalla con su mensaje, no abrir una
   * página con un JSON dentro.
   */
  descargarExportacion: async (
    formato: FormatoListado,
    conPdf: boolean,
    periodo: PeriodoExportacion,
  ): Promise<Resultado<FicheroDescargado>> => {
    const q = new URLSearchParams("trimestre" in periodo ? { trimestre: periodo.trimestre } : { desde: periodo.desde, hasta: periodo.hasta });
    if (conPdf) q.set("formato", formato);
    const ruta = conPdf ? "/api/v1/accountant-export/facturas.zip" : RUTA_DEL_LISTADO[formato];
    let res: Response;
    try {
      res = await fetch(`${ruta}?${q.toString()}`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    // `match` y no `RegExp.exec`: CHECK-1 (ADR-0001) no distingue uno del `exec` de D1.
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), conPdf ? "facturas.zip" : "facturas");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },

  /** `OD-0054-ENMIENDA-01`: el listado de facturas RECIBIDAS del periodo, solo. */
  descargarRecibidas: async (formato: FormatoRecibidas, periodo: PeriodoExportacion): Promise<Resultado<FicheroDescargado>> => {
    const q = new URLSearchParams("trimestre" in periodo ? { trimestre: periodo.trimestre } : { desde: periodo.desde, hasta: periodo.hasta });
    let res: Response;
    try {
      res = await fetch(`/api/v1/accountant-export/recibidas.${formato}?${q.toString()}`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), `facturas-recibidas.${formato}`);
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },

  /** `OD-0063` D-10e: las recibidas con el adjunto de cada compra, en un ZIP. */
  descargarRecibidasZip: async (periodo: PeriodoExportacion): Promise<Resultado<FicheroDescargado>> => {
    const q = new URLSearchParams("trimestre" in periodo ? { trimestre: periodo.trimestre } : { desde: periodo.desde, hasta: periodo.hasta });
    let res: Response;
    try {
      res = await fetch(`/api/v1/accountant-export/recibidas.zip?${q.toString()}`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), "facturas-recibidas.zip");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },

  /** `OD-0063` D-10f: IVR e IVS para ContaSOL, con su LEEME, en un ZIP. */
  descargarContasol: async (periodo: PeriodoExportacion, o: OpcionesContasol): Promise<Resultado<FicheroDescargado>> => {
    const q = new URLSearchParams("trimestre" in periodo ? { trimestre: periodo.trimestre } : { desde: periodo.desde, hasta: periodo.hasta });
    q.set("digitos", o.digitos);
    q.set("prefijoClientes", o.prefijoClientes);
    q.set("prefijoProveedores", o.prefijoProveedores);
    q.set("primerCodigo", o.primerCodigo);
    let res: Response;
    try {
      res = await fetch(`/api/v1/accountant-export/contasol.zip?${q.toString()}`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), "contasol.zip");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },

  /** Emitidas y recibidas del periodo en un solo Excel, una pestaña cada una. */
  descargarCompleto: async (periodo: PeriodoExportacion): Promise<Resultado<FicheroDescargado>> => {
    const q = new URLSearchParams("trimestre" in periodo ? { trimestre: periodo.trimestre } : { desde: periodo.desde, hasta: periodo.hasta });
    let res: Response;
    try {
      res = await fetch(`/api/v1/accountant-export/completo.xlsx?${q.toString()}`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), "ventas-y-compras.xlsx");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },

  /**
   * El PDF de un presupuesto enviado o de una factura emitida, como FICHERO:
   * el botón «Enviar» lo entrega al correo del usuario. Por `fetch` y no por
   * enlace por lo mismo que la exportación: un 409 se dice en la pantalla.
   */
  descargarPdf: async (documento: "quotes" | "invoices", id: string): Promise<Resultado<FicheroDescargado>> => {
    let res: Response;
    try {
      res = await fetch(`/api/v1/${documento}/${encodeURIComponent(id)}/pdf`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), "documento.pdf");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },

  /** TDS-CORE §4-§6. Las plazas las decide la API, en la misma sentencia del alta. */
  /**
   * TODOS los clientes, página a página. La API los da de 50 en 50 con
   * `nextCursor`, y leer solo la primera dejaba fuera del selector de
   * presupuestos, facturas, oportunidades y actividades, de la lista de
   * Clientes y del buscador a todos los que van después del 50.º por orden
   * alfabético: tras importar 150, los de la «E» en adelante no se podían elegir.
   */
  listarClientes: async (estado: "ACTIVE" | "ARCHIVED"): Promise<Resultado<readonly Cliente[]>> => {
    const todos: Cliente[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < MAX_PAGINAS_CLIENTES; i++) {
      const pagina: string = `/api/v1/customers?status=${estado}${cursor === null ? "" : `&cursor=${encodeURIComponent(cursor)}`}`;
      const r: Resultado<PaginaClientes> = await pedir(pagina, { method: "GET" }, comoPaginaClientes);
      if (!r.ok) return r;
      todos.push(...r.datos.clientes);
      if (r.datos.siguiente === null || r.datos.siguiente === cursor) break;
      cursor = r.datos.siguiente;
    }
    return { ok: true, datos: todos };
  },

  obtenerCliente: (id: string): Promise<Resultado<Cliente>> =>
    pedir(`/api/v1/customers/${encodeURIComponent(id)}`, { method: "GET" }, comoUnCliente),

  /** `claveIdempotencia`: la misma en los reintentos de un mismo formulario (TDS-000 §5.1). */
  crearCliente: (datos: DatosCliente, claveIdempotencia: string): Promise<Resultado<Cliente>> =>
    pedir(
      "/api/v1/customers",
      { method: "POST", body: JSON.stringify(datos), headers: { "idempotency-key": claveIdempotencia } },
      comoUnCliente,
    ),

  editarCliente: (id: string, datos: DatosCliente): Promise<Resultado<Cliente>> =>
    pedir(`/api/v1/customers/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(datos) }, comoUnCliente),

  archivarCliente: (id: string): Promise<Resultado<Cliente>> =>
    pedir(`/api/v1/customers/${encodeURIComponent(id)}/archive`, { method: "POST", body: JSON.stringify({}) }, comoUnCliente),

  eliminarCliente: (id: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/customers/${encodeURIComponent(id)}`, { method: "DELETE", body: JSON.stringify({}) }, (c) =>
      typeof c === "object" && c !== null && (c as Record<string, unknown>).deleted === true ? true : null,
    ),

  plazasClientes: (): Promise<Resultado<Plazas>> => pedir("/api/v1/usage/customer-slots", { method: "GET" }, comoPlazas),

  /**
   * URS §723: un cliente provisional SOLO con el nombre, para apuntar una
   * oportunidad sin pedir datos fiscales. Pasa por la ruta normal de clientes.
   */
  crearClienteProvisional: (nombre: string, claveIdempotencia: string): Promise<Resultado<Cliente>> =>
    pedir(
      "/api/v1/customers",
      { method: "POST", body: JSON.stringify({ displayName: nombre }), headers: { "idempotency-key": claveIdempotencia } },
      comoUnCliente,
    ),

  /** CRM V1 de Business (`OD-0048`). Fuera de Business la API contesta 402. */
  listarOportunidades: (estado: EstadoOportunidad | null, clienteId: string | null = null): Promise<Resultado<ListaOportunidades>> => {
    const q = new URLSearchParams();
    if (estado !== null) q.set("status", estado);
    if (clienteId !== null) q.set("customerId", clienteId);
    const qs = q.toString();
    return pedir(`/api/v1/crm/opportunities${qs === "" ? "" : `?${qs}`}`, { method: "GET" }, comoListaOportunidades);
  },

  obtenerOportunidad: (id: string): Promise<Resultado<UnaOportunidad>> =>
    pedir(`/api/v1/crm/opportunities/${encodeURIComponent(id)}`, { method: "GET" }, comoUnaOportunidad),

  crearOportunidad: (datos: DatosOportunidad): Promise<Resultado<UnaOportunidad>> =>
    pedir("/api/v1/crm/opportunities", { method: "POST", body: JSON.stringify(datos) }, comoUnaOportunidad),

  editarOportunidad: (
    id: string,
    datos: DatosOportunidad,
  ): Promise<Resultado<UnaOportunidad>> =>
    pedir(`/api/v1/crm/opportunities/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(datos) }, comoUnaOportunidad),

  /**
   * Cambia de etapa (`OD-0048-ENMIENDA-01`). Ganarla devuelve lo que la pantalla
   * ofrece (`ofertas`) y NO crea nada: ni presupuesto ni factura.
   */
  cambiarEtapaOportunidad: (
    id: string,
    etapa: EtapaOportunidad,
    // OD-0048-ENMIENDA-02 F-01: solo al perderla, y opcional.
    motivo: { readonly lostReason: MotivoPerdida | null; readonly lostNote: string | null } | null = null,
  ): Promise<Resultado<UnaOportunidad>> =>
    pedir(
      `/api/v1/crm/opportunities/${encodeURIComponent(id)}/stage`,
      { method: "POST", body: JSON.stringify(motivo === null ? { stage: etapa } : { stage: etapa, ...motivo }) },
      comoUnaOportunidad,
    ),

  /**
   * OD-0058: guarda lo que viene (una respuesta por toque, o el ajuste a mano) y
   * devuelve la oportunidad con el porcentaje ya recalculado por la API.
   */
  cualificarOportunidad: (
    id: string,
    // El porcentaje a mano va como TEXTO ("70"): la web no convierte números.
    cambio: Partial<Record<PreguntaCualificacion, RespuestaCualificacion | null>> & { readonly probabilityOverride?: string | null },
  ): Promise<Resultado<{ readonly oportunidad: Oportunidad; readonly ofertas: readonly string[] }>> =>
    pedir(
      `/api/v1/crm/opportunities/${encodeURIComponent(id)}/qualification`,
      { method: "POST", body: JSON.stringify(cambio) },
      comoUnaOportunidad,
    ),

  /** OD-0058 D-06: la oportunidad de la que salió un presupuesto, si la hay. Fuera de Business, 402. */
  oportunidadDePresupuesto: async (quoteId: string): Promise<Resultado<Oportunidad | null>> => {
    const r = await pedir(`/api/v1/crm/opportunities?quoteId=${encodeURIComponent(quoteId)}`, { method: "GET" }, comoListaOportunidades);
    return r.ok ? { ok: true, datos: r.datos.oportunidades[0] ?? null } : r;
  },

  /** D-E2: el presupuesto recién creado desde la oportunidad queda enlazado a ella. */
  enlazarPresupuestoOportunidad: (
    id: string,
    quoteId: string,
  ): Promise<Resultado<{ readonly oportunidad: Oportunidad; readonly ofertas: readonly string[] }>> =>
    pedir(
      `/api/v1/crm/opportunities/${encodeURIComponent(id)}/quote`,
      { method: "POST", body: JSON.stringify({ quoteId }) },
      comoUnaOportunidad,
    ),

  /** Registro de cobros de Business (`OD-0040`). Fuera de Business la API contesta 402. */
  /** `OD-0067` D-02: la nota para la asesoría de una factura emitida. */
  notaDeFactura: (facturaId: string): Promise<Resultado<NotaParaLaAsesoria>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(facturaId)}/nota-asesoria`, { method: "GET" }, comoNotaParaLaAsesoria),

  /** Las dos vacías la borran. */
  guardarNotaDeFactura: (facturaId: string, datos: { readonly nota: string; readonly etiqueta: string }): Promise<Resultado<NotaParaLaAsesoria>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(facturaId)}/nota-asesoria`, { method: "POST", body: JSON.stringify(datos) }, comoNotaParaLaAsesoria),

  notaDeCompra: (compraId: string): Promise<Resultado<NotaParaLaAsesoria>> =>
    pedir(`/api/v1/purchases/${encodeURIComponent(compraId)}/nota-asesoria`, { method: "GET" }, comoNotaParaLaAsesoria),

  guardarNotaDeCompra: (compraId: string, datos: { readonly nota: string; readonly etiqueta: string }): Promise<Resultado<NotaParaLaAsesoria>> =>
    pedir(`/api/v1/purchases/${encodeURIComponent(compraId)}/nota-asesoria`, { method: "POST", body: JSON.stringify(datos) }, comoNotaParaLaAsesoria),

  /** `OD-0067` D-03: el comentario de un trimestre `AAAA-TN`. */
  comentarioDelTrimestre: (trimestre: string): Promise<Resultado<ComentarioDelTrimestre>> =>
    pedir(`/api/v1/accountant-export/comentario?trimestre=${encodeURIComponent(trimestre)}`, { method: "GET" }, comoComentarioDelTrimestre),

  /** Vacío, lo borra. */
  guardarComentarioDelTrimestre: (trimestre: string, comentario: string): Promise<Resultado<ComentarioDelTrimestre>> =>
    pedir("/api/v1/accountant-export/comentario", { method: "POST", body: JSON.stringify({ trimestre, comentario }) }, comoComentarioDelTrimestre),

  cobrosDeFactura: (facturaId: string): Promise<Resultado<FichaCobros>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(facturaId)}/payments`, { method: "GET" }, comoFichaCobros),

  /** `claveIdempotencia`: la misma en los reintentos de un mismo formulario (`D-10`). */
  registrarCobro: (facturaId: string, datos: DatosCobro, claveIdempotencia: string): Promise<Resultado<CuentaCobros>> =>
    pedir(
      `/api/v1/invoices/${encodeURIComponent(facturaId)}/payments`,
      { method: "POST", body: JSON.stringify(datos), headers: { "idempotency-key": claveIdempotencia } },
      comoCuentaDeUnCobro,
    ),

  /** Un cobro no se edita ni se borra: se anula con motivo, y se queda (`D-06`). */
  anularCobro: (facturaId: string, cobroId: string, motivo: string): Promise<Resultado<CuentaCobros>> =>
    pedir(
      `/api/v1/invoices/${encodeURIComponent(facturaId)}/payments/${encodeURIComponent(cobroId)}/void`,
      { method: "POST", body: JSON.stringify({ reason: motivo }) },
      comoCuentaDeUnCobro,
    ),

  /** Cobrado del mes y pendiente, por cliente y por factura (`D-08`). */
  resumenCobros: (): Promise<Resultado<ResumenCobros>> => pedir("/api/v1/payments/summary", { method: "GET" }, comoResumenCobros),

  /** `OD-0051`: lo pendiente con su antigüedad. Los filtros van a la API, que es quien suma. */
  cobrosPendientes: (f: FiltrosPendientes): Promise<Resultado<CobrosPendientes>> =>
    pedir(`/api/v1/payments/receivables?${consultaPendientes(f)}`, { method: "GET" }, comoCobrosPendientes),

  /** `OD-0062` E-05: lo mismo, con los mismos filtros, como Excel. Lo genera la API. */
  descargarPendientes: async (f: FiltrosPendientes): Promise<Resultado<FicheroDescargado>> => {
    let res: Response;
    try {
      res = await fetch(`/api/v1/payments/receivables.xlsx?${consultaPendientes(f)}`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), "cobros-pendientes.xlsx");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },

  /** `OD-0041`: el informe de un trimestre (`2026-T3`), o del que está en curso. Fuera de Business, 402. */
  informeFiscal: (trimestre: string | null): Promise<Resultado<InformeFiscal>> =>
    pedir(
      trimestre === null ? "/api/v1/tax-insights" : `/api/v1/tax-insights?trimestre=${encodeURIComponent(trimestre)}`,
      { method: "GET" },
      comoInformeFiscal,
    ),

  eliminarOportunidad: (id: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/crm/opportunities/${encodeURIComponent(id)}`, { method: "DELETE", body: JSON.stringify({}) }, (c) =>
      typeof c === "object" && c !== null && (c as Record<string, unknown>).deleted === true ? true : null,
    ),

  /** Tareas y actividades (`OD-0050`). Fuera de Business la API contesta 402. */
  listarActividades: (
    vista: "PENDING" | "DONE",
    filtro: FiltroActividades = {},
  ): Promise<Resultado<ListaActividades>> => {
    const q = new URLSearchParams({ view: vista });
    if (filtro.clienteId) q.set("customerId", filtro.clienteId);
    if (filtro.oportunidadId) q.set("opportunityId", filtro.oportunidadId);
    if (filtro.presupuestoId) q.set("quoteId", filtro.presupuestoId);
    if (filtro.facturaId) q.set("invoiceId", filtro.facturaId);
    return pedir(`/api/v1/crm/activities?${q.toString()}`, { method: "GET" }, comoListaActividades);
  },

  /** `hecha`: apunta algo que ya pasó ("le he llamado") directamente como hecho. */
  /** `pasoJose`: la tarea la propone José (OD-0058-ENMIENDA-01 E-05), y lo cuenta E-07. */
  crearActividad: (datos: DatosActividad, hecha = false, pasoJose: string | null = null): Promise<Resultado<Actividad>> =>
    pedir(
      "/api/v1/crm/activities",
      { method: "POST", body: JSON.stringify({ ...datos, ...(hecha ? { done: true } : {}), ...(pasoJose !== null ? { joseStep: pasoJose } : {}) }) },
      comoUnaActividad,
    ),

  /**
   * E-07 (ADR-0024): lo que solo sabe la pantalla de José. Un contador sin ids;
   * si falla, nadie se entera: no se reintenta ni se avisa.
   */
  senalJose: (senal: SenalJose): Promise<Resultado<true>> =>
    pedir("/api/v1/crm/jose/signals", { method: "POST", body: JSON.stringify(senal) }, (c) =>
      typeof c === "object" && c !== null && (c as Record<string, unknown>).recorded === true ? true : null,
    ),

  editarActividad: (id: string, datos: DatosActividad): Promise<Resultado<Actividad>> =>
    pedir(`/api/v1/crm/activities/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(datos) }, comoUnaActividad),

  marcarActividad: (id: string, hecha: boolean): Promise<Resultado<ActividadMarcada>> =>
    pedir(`/api/v1/crm/activities/${encodeURIComponent(id)}/done`, { method: "POST", body: JSON.stringify({ done: hecha }) }, comoActividadMarcada),

  eliminarActividad: (id: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/crm/activities/${encodeURIComponent(id)}`, { method: "DELETE", body: JSON.stringify({}) }, (c) =>
      typeof c === "object" && c !== null && (c as Record<string, unknown>).deleted === true ? true : null,
    ),

  /** TDS-CORE Parte 2 §1. La cuota (100) la decide la API en la misma sentencia del alta. */
  listarProductos: (): Promise<Resultado<readonly Producto[]>> =>
    pedir("/api/v1/products", { method: "GET" }, comoListaProductos),

  obtenerProducto: (id: string): Promise<Resultado<Producto>> =>
    pedir(`/api/v1/products/${encodeURIComponent(id)}`, { method: "GET" }, comoUnProducto),

  catalogoIva: (): Promise<Resultado<CatalogoIva>> => pedir("/api/v1/tax-catalog", { method: "GET" }, comoCatalogo),

  /** `claveIdempotencia`: la misma en los reintentos de un mismo formulario (TDS-000 §5.1). */
  crearProducto: (datos: DatosProducto, claveIdempotencia: string): Promise<Resultado<Producto>> =>
    pedir(
      "/api/v1/products",
      { method: "POST", body: JSON.stringify(datos), headers: { "idempotency-key": claveIdempotencia } },
      comoUnProducto,
    ),

  editarProducto: (id: string, datos: DatosProducto): Promise<Resultado<Producto>> =>
    pedir(`/api/v1/products/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(datos) }, comoUnProducto),

  activarProducto: (id: string, activo: boolean): Promise<Resultado<Producto>> =>
    pedir(
      `/api/v1/products/${encodeURIComponent(id)}/${activo ? "activate" : "deactivate"}`,
      { method: "POST", body: JSON.stringify({}) },
      comoUnProducto,
    ),

  eliminarProducto: (id: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/products/${encodeURIComponent(id)}`, { method: "DELETE", body: JSON.stringify({}) }, (c) =>
      typeof c === "object" && c !== null && (c as Record<string, unknown>).deleted === true ? true : null,
    ),

  /** TDS-CORE Parte 2 §2. Los totales los calcula la API: esta web no suma importes. */
  listarPresupuestos: (estado: FiltroListaPresupuestos | null): Promise<Resultado<readonly ResumenPresupuesto[]>> =>
    pedir(`/api/v1/quotes${estado === null ? "" : `?status=${estado}`}`, { method: "GET" }, comoListaPresupuestos),

  /** `OD-0062` E-06: la lista del filtro elegido como Excel, con lo facturado. Lo genera la API. */
  descargarPresupuestos: async (filtro: FiltroListaPresupuestos | typeof PENDIENTES_FACTURAR | null): Promise<Resultado<FicheroDescargado>> => {
    let res: Response;
    try {
      res = await fetch(`/api/v1/quotes/export.xlsx${filtro === null ? "" : `?status=${filtro}`}`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), "presupuestos.xlsx");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },

  /** `OD-0062` E-07: los totales de las líneas escritas, sin guardar ni gastar cuota (`URS §129`). */
  calcularPresupuesto: (datos: Pick<DatosPresupuesto, "lines" | "withholdingRate" | "withholdingReason">): Promise<Resultado<CalculoPresupuesto>> =>
    pedir("/api/v1/quotes/calculate", { method: "POST", body: JSON.stringify(datos) }, comoCalculoPresupuesto),

  obtenerPresupuesto: (id: string): Promise<Resultado<Presupuesto>> =>
    pedir(`/api/v1/quotes/${encodeURIComponent(id)}`, { method: "GET" }, comoUnPresupuesto),

  cuotasPresupuestos: (): Promise<Resultado<CuotasPresupuestos>> => pedir("/api/v1/usage/quotas", { method: "GET" }, comoCuotas),

  resumenPresupuestos: (): Promise<Resultado<ResumenPresupuestos>> => pedir("/api/v1/quotes/summary", { method: "GET" }, comoResumen),

  crearPresupuesto: (datos: DatosPresupuesto, claveIdempotencia: string): Promise<Resultado<Presupuesto>> =>
    pedir(
      "/api/v1/quotes",
      { method: "POST", body: JSON.stringify(datos), headers: { "idempotency-key": claveIdempotencia } },
      comoUnPresupuesto,
    ),

  editarPresupuesto: (id: string, datos: DatosPresupuesto): Promise<Resultado<Presupuesto>> =>
    pedir(`/api/v1/quotes/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(datos) }, comoUnPresupuesto),

  /** `send` marca como enviado: lo entrega el usuario (no hay email de documentos todavia). */
  transicionPresupuesto: (
    id: string,
    accion: "send" | "accept" | "reject" | "cancel",
    cuerpo: Record<string, unknown> = {},
  ): Promise<Resultado<Presupuesto>> =>
    pedir(`/api/v1/quotes/${encodeURIComponent(id)}/${accion}`, { method: "POST", body: JSON.stringify(cuerpo) }, comoUnPresupuesto),

  duplicarPresupuesto: (id: string, claveIdempotencia: string): Promise<Resultado<Presupuesto>> =>
    pedir(
      `/api/v1/quotes/${encodeURIComponent(id)}/duplicate`,
      { method: "POST", body: JSON.stringify({}), headers: { "idempotency-key": claveIdempotencia } },
      comoUnPresupuesto,
    ),

  /** TDS-CORE Parte 3 §1-§2: borradores de factura. Sin emisión todavía. */
  listarFacturas: (estado: EstadoFactura | null): Promise<Resultado<readonly ResumenFactura[]>> =>
    pedir(`/api/v1/invoices${estado === null ? "" : `?status=${estado}`}`, { method: "GET" }, comoListaFacturas),

  obtenerFactura: (id: string): Promise<Resultado<Factura>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(id)}`, { method: "GET" }, comoUnaFactura),

  controlPrevioFactura: (id: string): Promise<Resultado<ControlPrevio>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(id)}/preflight`, { method: "GET" }, comoControlPrevio),

  /** Qué impide ANULAR, preguntado antes de ofrecer el botón. */
  controlPrevioAnulacion: (id: string): Promise<Resultado<ControlAnulacion>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(id)}/void-preflight`, { method: "GET" }, comoControlAnulacion),

  usoFacturas: (): Promise<Resultado<UsoFacturas>> => pedir("/api/v1/usage/invoices", { method: "GET" }, comoUsoFacturas),

  crearFactura: (datos: DatosFactura, claveIdempotencia: string): Promise<Resultado<Factura>> =>
    pedir(
      "/api/v1/invoices",
      { method: "POST", body: JSON.stringify(datos), headers: { "idempotency-key": claveIdempotencia } },
      comoUnaFactura,
    ),

  editarFactura: (id: string, datos: DatosFactura): Promise<Resultado<Factura>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(datos) }, comoUnaFactura),

  /** Emitir (TDS-CORE Parte 3 §4): irreversible. Solo si el control previo lo permite. */
  emitirFactura: (id: string): Promise<Resultado<Factura>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(id)}/issue`, { method: "POST", body: JSON.stringify({}) }, comoUnaFactura),

  /**
   * Anular una factura EMITIDA (TDS-FISCAL Parte 4 §3). No la borra ni la
   * edita: encadena un registro de anulación. El motivo lo escribe la persona.
   */
  anularFactura: (id: string, motivo: string): Promise<Resultado<Factura>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(id)}/void`, { method: "POST", body: JSON.stringify({ reason: motivo }) }, comoUnaFactura),

  /**
   * Preparar una rectificativa (`TDS-FISCAL` Parte 4 §2, `DR4-08`). Crea un
   * BORRADOR nuevo en serie R; la factura original no se toca nunca.
   *
   * `confirmarFueraDePlazo` sólo cuando la API lo ha pedido: han pasado más de
   * cuatro años desde la original (art. 15.3) y la persona lo confirma.
   */
  rectificarFactura: (
    id: string,
    motivo: MotivoRectificativa,
    modalidad: ModalidadRectificativa,
    confirmarFueraDePlazo = false,
  ): Promise<Resultado<Factura>> =>
    pedir(
      `/api/v1/invoices/${encodeURIComponent(id)}/rectify`,
      { method: "POST", body: JSON.stringify({ type: motivo, kind: modalidad, confirmOutOfTime: confirmarFueraDePlazo }) },
      comoUnaFactura,
    ),

  /** Descartar NO borra: el borrador se conserva (TDS-CORE Parte 3 §1.1). */
  descartarFactura: (id: string): Promise<Resultado<Factura>> =>
    pedir(`/api/v1/invoices/${encodeURIComponent(id)}/discard`, { method: "POST", body: JSON.stringify({}) }, comoUnaFactura),

  /** Qué se ha facturado de cada presupuesto, para la lista. */
  facturacionPresupuestos: (): Promise<Resultado<FacturacionPresupuestos>> =>
    pedir("/api/v1/quotes/invoicing", { method: "GET" }, comoFacturacionPresupuestos),

  /** Los anticipos de un presupuesto y qué se puede hacer (`OD-0053 D-07`). */
  anticiposPresupuesto: (quoteId: string): Promise<Resultado<AnticiposPresupuesto>> =>
    pedir(`/api/v1/quotes/${encodeURIComponent(quoteId)}/advances`, { method: "GET" }, comoAnticiposPresupuesto),

  /** «Facturar un anticipo»: un BORRADOR de factura por un porcentaje del presupuesto (`OD-0053 D-02`). */
  facturarAnticipo: (quoteId: string, datos: DatosAnticipo, claveIdempotencia: string): Promise<Resultado<Factura>> =>
    pedir(
      `/api/v1/quotes/${encodeURIComponent(quoteId)}/advance-invoice`,
      { method: "POST", body: JSON.stringify(datos), headers: { "idempotency-key": claveIdempotencia } },
      comoUnaFactura,
    ),

  /** «Facturar el resto»: la factura final, que descuenta los anticipos emitidos (`OD-0053 D-03`). */
  facturarResto: (quoteId: string, claveIdempotencia: string): Promise<Resultado<Factura>> =>
    pedir(
      `/api/v1/quotes/${encodeURIComponent(quoteId)}/final-invoice`,
      { method: "POST", body: JSON.stringify({}), headers: { "idempotency-key": claveIdempotencia } },
      comoUnaFactura,
    ),

  /** "Crear factura" desde un presupuesto aceptado; una sola vez (TDS-CORE Parte 3 §2). */
  facturarPresupuesto: (quoteId: string, claveIdempotencia: string): Promise<Resultado<Factura>> =>
    pedir(
      `/api/v1/quotes/${encodeURIComponent(quoteId)}/invoice`,
      { method: "POST", body: JSON.stringify({}), headers: { "idempotency-key": claveIdempotencia } },
      comoUnaFactura,
    ),

  logout: (): Promise<Resultado<true>> =>
    pedir("/api/v1/auth/logout", { method: "POST", body: JSON.stringify({}) }, () => true),
};

/*
 * Facturas de compra, proveedores y pagos a proveedores — `OD-0054` (ACCEPTED
 * 2026-10-22). Igual que los cobros: los importes llegan ya sumados y como
 * TEXTO, y esta web no hace ninguna cuenta (`URS §129`). Lo que se puede
 * escribir lo dice la API en `writable`; fuera de Business contesta 402.
 */

export type TipoCompra = "ORDINARY" | "ADVANCE";
export const TIPOS_COMPRA: readonly TipoCompra[] = ["ORDINARY", "ADVANCE"];

/** Los tipos de IVA y de retención que admite la API (`OD-0054 D-03`), como texto. */
export const TIPOS_IVA_COMPRA = ["21,00", "10,00", "4,00", "0,00"] as const;
export const TIPOS_RETENCION_COMPRA = ["15,00", "7,00", "19,00", "1,00"] as const;

export type Proveedor = {
  readonly id: string;
  readonly taxId: string;
  readonly taxIdType: string;
  readonly name: string;
  readonly email: string | null;
  readonly notes: string | null;
  readonly status: "ACTIVE" | "ARCHIVED";
  readonly createdAt: string;
  readonly updatedAt: string;
};

export type ProveedorDeLista = Proveedor & { readonly pending: string; readonly unbilledPayments: number };

export type PagoProveedor = {
  readonly id: string;
  readonly supplierId: string;
  readonly purchaseId: string | null;
  /** `OD-0055 D-04`: el presupuesto de un pago anotado sin factura. */
  readonly supplierQuoteId: string | null;
  readonly amount: string;
  readonly paidOn: string;
  readonly method: MetodoCobro;
  readonly note: string | null;
  readonly status: "POSTED" | "VOIDED";
  readonly registeredAt: string;
  readonly assignedAt: string | null;
  readonly voidedAt: string | null;
  readonly voidReason: string | null;
};

/** `D-05`: un pago sin factura, con el día en que el proveedor ya llega tarde. */
export type PagoSinFactura = PagoProveedor & {
  readonly supplierName: string;
  readonly invoiceDueBy: string;
  readonly late: boolean;
  readonly quoteNumber: string | null;
};

export type FilaCompra = {
  readonly id: string;
  readonly registryNumber: string;
  readonly kind: TipoCompra;
  readonly supplierId: string;
  readonly supplierName: string;
  readonly supplierTaxId: string;
  readonly supplierInvoiceNumber: string;
  readonly issueDate: string;
  readonly dueDate: string | null;
  readonly base: string;
  readonly vat: string;
  readonly total: string;
  readonly payable: string;
  readonly paid: string;
  readonly pending: string;
  readonly paymentStatus: EstadoPago;
  readonly status: "REGISTERED" | "VOIDED";
  readonly hasAttachment: boolean;
  /** Algo por pagar con el vencimiento pasado. Lo decide la API, con la fecha de Madrid. */
  readonly overdue: boolean;
  /** `OD-0055 D-08` y `D-09`: la obra, y de qué presupuesto viene. */
  readonly reference: string | null;
  readonly supplierQuoteId: string | null;
  readonly quoteNumber: string | null;
};

export type CompraEnlazada = {
  readonly id: string;
  readonly registryNumber: string;
  readonly supplierInvoiceNumber: string;
  readonly issueDate: string;
  readonly total: string;
};

/** `OD-0055 D-03`: los tres estados guardados y los dos que salen de lo facturado. */
export type EstadoPresupuestoProveedor = "PENDING" | "ACCEPTED" | "PARTIALLY_INVOICED" | "COMPLETED" | "CANCELLED";
export const ESTADOS_PRESUPUESTO_PROVEEDOR: readonly EstadoPresupuestoProveedor[] = ["PENDING", "ACCEPTED", "PARTIALLY_INVOICED", "COMPLETED", "CANCELLED"];

/** Un presupuesto de proveedor, para nombrarlo y elegirlo desde una compra o un pago. */
export type PresupuestoEnlazado = {
  readonly id: string;
  readonly quoteNumber: string;
  readonly supplierQuoteNumber: string | null;
  readonly reference: string | null;
  readonly issueDate: string;
  readonly total: string;
  readonly toInvoice: string;
  readonly state: EstadoPresupuestoProveedor;
};

export type AdjuntoCompra = {
  readonly fileId: string;
  readonly originalName: string;
  readonly contentType: string;
  readonly tamanoLegible: string;
  readonly uploadedAt: string;
};

export type LineaCompra = {
  readonly vatTreatment: TratamientoIva;
  readonly vatRate: string | null;
  readonly base: string;
  readonly vat: string;
};

export type Compra = {
  readonly id: string;
  readonly registryNumber: string;
  readonly kind: TipoCompra;
  readonly supplier: Proveedor;
  readonly supplierInvoiceNumber: string;
  readonly issueDate: string;
  readonly operationDate: string | null;
  readonly receivedOn: string;
  readonly dueDate: string | null;
  readonly concept: string | null;
  readonly lines: readonly LineaCompra[];
  readonly withholdingRate: string | null;
  readonly withholding: string;
  readonly base: string;
  readonly vat: string;
  readonly total: string;
  readonly payable: string;
  readonly qr: { readonly url: string; readonly verifactu: boolean } | null;
  readonly attachment: AdjuntoCompra | null;
  readonly status: "REGISTERED" | "VOIDED";
  readonly voidedAt: string | null;
  readonly voidReason: string | null;
  readonly updatedAt: string;
  readonly reference: string | null;
  readonly quote: PresupuestoEnlazado | null;
  /** Los que se le pueden poner al corregirla: aceptados, sin cerrar, y el suyo. */
  readonly acceptedQuotes: readonly PresupuestoEnlazado[];
  readonly payments: readonly PagoProveedor[];
  readonly summary: {
    readonly payable: string;
    readonly paid: string;
    readonly pending: string;
    readonly paymentStatus: EstadoPago;
    readonly blockedBy: "VOIDED" | "FULLY_PAID" | null;
  };
  readonly advances: readonly CompraEnlazada[];
  readonly operationTotal: string | null;
  readonly discountedBy: CompraEnlazada | null;
  readonly writable: boolean;
  readonly assignablePayments: readonly PagoProveedor[];
  readonly availableAdvances: readonly CompraEnlazada[];
};

/** Lo que la API avisa sin bloquear: manda la factura del proveedor (`D-03`). */
export type AvisoCompra = {
  readonly code: string;
  readonly line?: number;
  readonly proposed?: string;
  readonly qrTotal?: string;
  readonly qrNumber?: string;
  readonly qrDate?: string;
  readonly qrNif?: string;
};

export type TotalesCompra = { readonly base: string; readonly vat: string; readonly total: string; readonly withholding: string; readonly payable: string };

export type DatosLineaCompra = { readonly vatTreatment: TratamientoIva; readonly vatRate: string | null; readonly base: string; readonly vat: string | null };

export type DatosCompra = {
  readonly supplierInvoiceNumber: string;
  readonly issueDate: string;
  readonly operationDate: string | null;
  readonly dueDate: string | null;
  readonly concept: string | null;
  readonly reference: string | null;
  /** `OD-0055 D-04`: `null` la deja sin presupuesto. */
  readonly supplierQuoteId: string | null;
  readonly lines: readonly DatosLineaCompra[];
  readonly withholdingRate: string | null;
  readonly withholding: string | null;
  readonly qrUrl: string | null;
  readonly advanceIds: readonly string[];
};

/** El alta lleva además el tipo, el proveedor (uno que ya está o uno nuevo) y los pagos que se le asignan. */
export type AltaCompra = DatosCompra & {
  readonly kind: TipoCompra;
  readonly supplierId?: string;
  readonly supplier?: { readonly taxId: string; readonly name: string };
  readonly paymentIds: readonly string[];
};

export type DatosProveedor = { readonly taxId: string; readonly name: string; readonly email: string | null; readonly notes: string | null };

export type FichaProveedor = {
  readonly proveedor: Proveedor;
  readonly compras: readonly FilaCompra[];
  readonly sinFactura: readonly PagoSinFactura[];
  readonly pagos: readonly PagoProveedor[];
  readonly anticiposLibres: readonly CompraEnlazada[];
  /** `OD-0055 D-04`: a los que se puede enlazar una compra o un pago nuevo. */
  readonly presupuestosAceptados: readonly PresupuestoEnlazado[];
  readonly pendiente: string;
  readonly writable: boolean;
  readonly bloqueo: BloqueoCompras;
};

export const ESTADOS_LISTA_COMPRAS = ["ALL", "PENDING", "OVERDUE", "PAID", "VOIDED"] as const;

export type FiltrosCompras = {
  readonly estado: (typeof ESTADOS_LISTA_COMPRAS)[number];
  readonly tipo: "ALL" | TipoCompra;
  /** A qué fecha va el intervalo: la emisión o el vencimiento. */
  readonly fecha: "ISSUE" | "DUE";
  readonly desde: string | null;
  readonly hasta: string | null;
  readonly proveedor: string | null;
  /** Número de registro, número del proveedor, nombre, NIF, referencia o presupuesto. */
  readonly texto: string;
  /** `OD-0055 D-09`: solo las facturas de un presupuesto. */
  readonly presupuesto: { readonly id: string; readonly numero: string } | null;
};

/** Por qué no se puede anotar: el plan o el rol. La API lo dice; la web lo explica, no lo adivina. */
export type BloqueoCompras = "PLAN" | "ROLE" | null;

export type ListaCompras = {
  readonly filas: readonly FilaCompra[];
  readonly pendiente: string;
  readonly total: string;
  readonly writable: boolean;
  readonly bloqueo: BloqueoCompras;
};

export type PagosPendientesCompras = {
  readonly aFecha: string;
  readonly pendiente: string;
  readonly vencido: string;
  readonly filas: ReadonlyArray<FilaCompra & { readonly daysOverdue: number | null }>;
  readonly sinFactura: readonly PagoSinFactura[];
  readonly writable: boolean;
  readonly bloqueo: BloqueoCompras;
};

export type CalculoCompra =
  | { readonly ok: true; readonly totales: TotalesCompra; readonly avisos: readonly AvisoCompra[] }
  | { readonly ok: false; readonly campos: Readonly<Record<string, string>> };

const objeto = (v: unknown): Record<string, unknown> | null => (typeof v === "object" && v !== null ? (v as Record<string, unknown>) : null);
const textos = (v: Record<string, unknown>, claves: readonly string[]): boolean => claves.every((k) => typeof v[k] === "string");

function comoLista<T>(v: unknown, cada: (x: unknown) => T | null): T[] | null {
  if (!Array.isArray(v)) return null;
  const l = v.map(cada);
  return l.every((x) => x !== null) ? (l as T[]) : null;
}

function comoProveedor(valor: unknown): Proveedor | null {
  const v = objeto(valor);
  if (v === null || !textos(v, ["id", "taxId", "taxIdType", "name", "createdAt", "updatedAt"])) return null;
  if ((v.status !== "ACTIVE" && v.status !== "ARCHIVED") || !textoONulo(v.email) || !textoONulo(v.notes)) return null;
  return v as unknown as Proveedor;
}

function comoProveedorDeLista(valor: unknown): ProveedorDeLista | null {
  const v = objeto(valor);
  if (comoProveedor(valor) === null || v === null || typeof v.pending !== "string" || typeof v.unbilledPayments !== "number") return null;
  return v as unknown as ProveedorDeLista;
}

function comoPagoProveedor(valor: unknown): PagoProveedor | null {
  const v = objeto(valor);
  if (v === null || !textos(v, ["id", "supplierId", "amount", "paidOn", "registeredAt"])) return null;
  if (!(METODOS_COBRO as readonly unknown[]).includes(v.method) || (v.status !== "POSTED" && v.status !== "VOIDED")) return null;
  if (!textoONulo(v.purchaseId) || !textoONulo(v.note) || !textoONulo(v.voidReason) || !textoONulo(v.supplierQuoteId)) return null;
  return v as unknown as PagoProveedor;
}

function comoPagoSinFactura(valor: unknown): PagoSinFactura | null {
  const v = objeto(valor);
  if (comoPagoProveedor(valor) === null || v === null || typeof v.supplierName !== "string" || typeof v.invoiceDueBy !== "string" || typeof v.late !== "boolean") return null;
  if (!textoONulo(v.quoteNumber)) return null;
  return v as unknown as PagoSinFactura;
}

function comoFilaCompra(valor: unknown): FilaCompra | null {
  const v = objeto(valor);
  if (v === null || !textos(v, ["id", "registryNumber", "supplierId", "supplierName", "supplierInvoiceNumber", "issueDate", "total", "payable", "paid", "pending"])) return null;
  if (!(TIPOS_COMPRA as readonly unknown[]).includes(v.kind) || !ESTADOS_PAGO.includes(v.paymentStatus)) return null;
  if ((v.status !== "REGISTERED" && v.status !== "VOIDED") || !textoONulo(v.dueDate) || typeof v.hasAttachment !== "boolean") return null;
  if (typeof v.overdue !== "boolean" || !textoONulo(v.reference) || !textoONulo(v.supplierQuoteId) || !textoONulo(v.quoteNumber)) return null;
  return v as unknown as FilaCompra;
}

function comoPresupuestoEnlazado(valor: unknown): PresupuestoEnlazado | null {
  const v = objeto(valor);
  if (v === null || !textos(v, ["id", "quoteNumber", "issueDate", "total", "toInvoice"])) return null;
  if (!textoONulo(v.supplierQuoteNumber) || !textoONulo(v.reference) || !(ESTADOS_PRESUPUESTO_PROVEEDOR as readonly unknown[]).includes(v.state)) return null;
  return v as unknown as PresupuestoEnlazado;
}

function comoEnlazada(valor: unknown): CompraEnlazada | null {
  const v = objeto(valor);
  return v !== null && textos(v, ["id", "registryNumber", "supplierInvoiceNumber", "issueDate", "total"]) ? (v as unknown as CompraEnlazada) : null;
}

function comoCompra(valor: unknown): Compra | null {
  const v = objeto(valor);
  if (v === null || !textos(v, ["id", "registryNumber", "supplierInvoiceNumber", "issueDate", "receivedOn", "base", "vat", "total", "payable", "withholding", "updatedAt"])) return null;
  if (!(TIPOS_COMPRA as readonly unknown[]).includes(v.kind) || (v.status !== "REGISTERED" && v.status !== "VOIDED")) return null;
  if (comoProveedor(v.supplier) === null || typeof v.writable !== "boolean" || !textoONulo(v.operationTotal)) return null;
  const resumen = objeto(v.summary);
  if (resumen === null || !textos(resumen, ["payable", "paid", "pending"]) || !ESTADOS_PAGO.includes(resumen.paymentStatus)) return null;
  const lineas = comoLista(v.lines, (x) => {
    const l = objeto(x);
    return l !== null && TRATAMIENTOS_IVA.includes(String(l.vatTreatment)) && textos(l, ["base", "vat"]) && textoONulo(l.vatRate) ? (l as unknown as LineaCompra) : null;
  });
  const qr = v.qr === null ? null : objeto(v.qr);
  if (v.qr !== null && (qr === null || typeof qr.url !== "string" || typeof qr.verifactu !== "boolean")) return null;
  const adjunto = v.attachment === null ? null : objeto(v.attachment);
  if (v.attachment !== null && (adjunto === null || !textos(adjunto, ["fileId", "originalName", "contentType", "tamanoLegible", "uploadedAt"]))) return null;
  if (v.discountedBy !== null && comoEnlazada(v.discountedBy) === null) return null;
  if (!textoONulo(v.reference) || (v.quote !== null && comoPresupuestoEnlazado(v.quote) === null) || comoLista(v.acceptedQuotes, comoPresupuestoEnlazado) === null) return null;
  for (const lista of [lineas, comoLista(v.payments, comoPagoProveedor), comoLista(v.assignablePayments, comoPagoProveedor)]) if (lista === null) return null;
  for (const lista of [comoLista(v.advances, comoEnlazada), comoLista(v.availableAdvances, comoEnlazada)]) if (lista === null) return null;
  return v as unknown as Compra;
}

const comoCompraEn =
  (clave: string) =>
  (cuerpo: unknown): Compra | null =>
    comoCompra(objeto(cuerpo)?.[clave]);

function comoAvisosCompra(v: unknown): readonly AvisoCompra[] {
  return Array.isArray(v) ? v.filter((a): a is AvisoCompra => objeto(a) !== null && typeof (a as Record<string, unknown>).code === "string") : [];
}

function comoFichaProveedor(cuerpo: unknown): FichaProveedor | null {
  const b = objeto(cuerpo);
  if (b === null || typeof b.pending !== "string" || typeof b.writable !== "boolean") return null;
  const proveedor = comoProveedor(b.supplier);
  const compras = comoLista(b.purchases, comoFilaCompra);
  const sinFactura = comoLista(b.unbilledPayments, comoPagoSinFactura);
  const pagos = comoLista(b.payments, comoPagoProveedor);
  const anticiposLibres = comoLista(b.availableAdvances, comoEnlazada);
  const presupuestosAceptados = comoLista(b.acceptedQuotes, comoPresupuestoEnlazado);
  if (proveedor === null || compras === null || sinFactura === null || pagos === null || anticiposLibres === null || presupuestosAceptados === null) return null;
  return {
    proveedor,
    compras,
    sinFactura,
    pagos,
    anticiposLibres,
    presupuestosAceptados,
    pendiente: b.pending,
    writable: b.writable,
    bloqueo: comoBloqueo(b.writeBlockedBy),
  };
}

function comoListaCompras(cuerpo: unknown): ListaCompras | null {
  const b = objeto(cuerpo);
  const filas = comoLista(b?.rows, comoFilaCompra);
  if (b === null || filas === null || typeof b.pending !== "string" || typeof b.total !== "string" || typeof b.writable !== "boolean") return null;
  return { filas, pendiente: b.pending, total: b.total, writable: b.writable, bloqueo: comoBloqueo(b.writeBlockedBy) };
}

function comoPagosPendientesCompras(cuerpo: unknown): PagosPendientesCompras | null {
  const b = objeto(cuerpo);
  if (b === null || !textos(b, ["asOf", "pending", "overdue"]) || typeof b.writable !== "boolean") return null;
  const filas = comoLista(b.rows, (x) => {
    const f = comoFilaCompra(x);
    const d = objeto(x)?.daysOverdue;
    return f !== null && (d === null || typeof d === "number") ? (x as FilaCompra & { readonly daysOverdue: number | null }) : null;
  });
  const sinFactura = comoLista(b.unbilledPayments, comoPagoSinFactura);
  if (filas === null || sinFactura === null) return null;
  return {
    aFecha: b.asOf as string,
    pendiente: b.pending as string,
    vencido: b.overdue as string,
    filas,
    sinFactura,
    writable: b.writable,
    bloqueo: comoBloqueo(b.writeBlockedBy),
  };
}

function comoCalculoCompra(cuerpo: unknown): CalculoCompra | null {
  const b = objeto(cuerpo);
  if (b === null) return null;
  if (b.ok === false) {
    const campos: Record<string, string> = {};
    for (const [k, v] of Object.entries(objeto(b.fields) ?? {})) if (typeof v === "string") campos[k] = v;
    return { ok: false, campos };
  }
  const t = objeto(b.totals);
  if (b.ok !== true || t === null || !textos(t, ["base", "vat", "total", "withholding", "payable"])) return null;
  return { ok: true, totales: t as unknown as TotalesCompra, avisos: comoAvisosCompra(b.warnings) };
}

/** El nombre del fichero en una cabecera: codificado, para que viajen sus acentos sin romper la petición. */
function nombreEnCabecera(nombre: string): string {
  return encodeURIComponent(nombre.slice(0, 200));
}

function comoBloqueo(v: unknown): BloqueoCompras {
  return v === "PLAN" || v === "ROLE" ? v : null;
}

const conClave = (clave: string): Record<string, string> => ({ "idempotency-key": clave });

export const apiCompras = {
  listarProveedores: (
    estado: "ACTIVE" | "ARCHIVED",
    q: string,
  ): Promise<Resultado<{ readonly proveedores: readonly ProveedorDeLista[]; readonly writable: boolean; readonly bloqueo: BloqueoCompras }>> => {
    const p = new URLSearchParams({ estado });
    if (q.trim() !== "") p.set("q", q.trim());
    return pedir(`/api/v1/suppliers?${p.toString()}`, { method: "GET" }, (cuerpo) => {
      const b = objeto(cuerpo);
      const proveedores = comoLista(b?.suppliers, comoProveedorDeLista);
      return proveedores === null || typeof b?.writable !== "boolean" ? null : { proveedores, writable: b.writable, bloqueo: comoBloqueo(b.writeBlockedBy) };
    });
  },

  /** Con un NIF que ya está, la API devuelve ESE proveedor (`creado: false`), no un duplicado. */
  crearProveedor: (datos: DatosProveedor): Promise<Resultado<{ readonly proveedor: Proveedor; readonly creado: boolean }>> =>
    pedir("/api/v1/suppliers", { method: "POST", body: JSON.stringify(datos) }, (cuerpo) => {
      const proveedor = comoProveedor(objeto(cuerpo)?.supplier);
      return proveedor === null ? null : { proveedor, creado: objeto(cuerpo)?.created === true };
    }),

  obtenerProveedor: (id: string): Promise<Resultado<FichaProveedor>> =>
    pedir(`/api/v1/suppliers/${encodeURIComponent(id)}`, { method: "GET" }, comoFichaProveedor),

  /** El NIF no se cambia: es quien es el proveedor. */
  editarProveedor: (id: string, datos: Omit<DatosProveedor, "taxId">): Promise<Resultado<Proveedor>> =>
    pedir(`/api/v1/suppliers/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(datos) }, (cuerpo) => comoProveedor(objeto(cuerpo)?.supplier)),

  archivarProveedor: (id: string, archivar: boolean): Promise<Resultado<Proveedor>> =>
    pedir(`/api/v1/suppliers/${encodeURIComponent(id)}/${archivar ? "archive" : "restore"}`, { method: "POST", body: JSON.stringify({}) }, (cuerpo) =>
      comoProveedor(objeto(cuerpo)?.supplier),
    ),

  /** `D-05` paso 1: el anticipo que se paga antes de tener la factura, y de qué presupuesto es (`OD-0055 D-04`). */
  pagoSinFactura: (proveedorId: string, datos: DatosCobro & { readonly supplierQuoteId: string | null }, clave: string): Promise<Resultado<PagoSinFactura>> =>
    pedir(`/api/v1/suppliers/${encodeURIComponent(proveedorId)}/payments`, { method: "POST", body: JSON.stringify(datos), headers: conClave(clave) }, (cuerpo) =>
      comoPagoSinFactura(objeto(cuerpo)?.payment),
    ),

  anularPago: (pagoId: string, motivo: string): Promise<Resultado<PagoProveedor>> =>
    pedir(`/api/v1/supplier-payments/${encodeURIComponent(pagoId)}/void`, { method: "POST", body: JSON.stringify({ reason: motivo }) }, (cuerpo) =>
      comoPagoProveedor(objeto(cuerpo)?.payment),
    ),

  listar: (f: FiltrosCompras): Promise<Resultado<ListaCompras>> => {
    const p = new URLSearchParams({ estado: f.estado });
    if (f.tipo !== "ALL") p.set("tipo", f.tipo);
    if (f.fecha !== "ISSUE") p.set("fecha", f.fecha);
    if (f.texto.trim() !== "") p.set("q", f.texto.trim());
    if (f.desde !== null) p.set("desde", f.desde);
    if (f.hasta !== null) p.set("hasta", f.hasta);
    if (f.proveedor !== null) p.set("proveedor", f.proveedor);
    if (f.presupuesto !== null) p.set("presupuesto", f.presupuesto.id);
    return pedir(`/api/v1/purchases?${p.toString()}`, { method: "GET" }, comoListaCompras);
  },

  pagosPendientes: (): Promise<Resultado<PagosPendientesCompras>> => pedir("/api/v1/purchases/payables", { method: "GET" }, comoPagosPendientesCompras),

  avisos: (): Promise<Resultado<{ readonly sinFactura: number; readonly tarde: number }>> =>
    pedir("/api/v1/purchases/alerts", { method: "GET" }, (cuerpo) => {
      const b = objeto(cuerpo);
      return typeof b?.unbilledPayments === "number" && typeof b.late === "number" ? { sinFactura: b.unbilledPayments, tarde: b.late } : null;
    }),

  /** Totales y avisos mientras se escribe, sin guardar: la web no suma (`URS §129`). */
  calcular: (datos: Partial<AltaCompra>): Promise<Resultado<CalculoCompra>> =>
    pedir("/api/v1/purchases/calculate", { method: "POST", body: JSON.stringify(datos) }, comoCalculoCompra),

  anotar: (datos: AltaCompra, clave: string): Promise<Resultado<{ readonly compra: Compra; readonly avisos: readonly AvisoCompra[] }>> =>
    pedir("/api/v1/purchases", { method: "POST", body: JSON.stringify(datos), headers: conClave(clave) }, (cuerpo) => {
      const compra = comoCompra(objeto(cuerpo)?.purchase);
      return compra === null ? null : { compra, avisos: comoAvisosCompra(objeto(cuerpo)?.warnings) };
    }),

  obtener: (id: string): Promise<Resultado<Compra>> => pedir(`/api/v1/purchases/${encodeURIComponent(id)}`, { method: "GET" }, comoCompra),

  /** `updatedAt` es el que se leyó: si otro la cambió entre medias, la API no lo pisa (`STALE`). */
  corregir: (id: string, datos: DatosCompra, updatedAt: string): Promise<Resultado<{ readonly compra: Compra; readonly avisos: readonly AvisoCompra[] }>> =>
    pedir(`/api/v1/purchases/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ ...datos, updatedAt }) }, (cuerpo) => {
      const compra = comoCompra(objeto(cuerpo)?.purchase);
      return compra === null ? null : { compra, avisos: comoAvisosCompra(objeto(cuerpo)?.warnings) };
    }),

  anular: (id: string, motivo: string): Promise<Resultado<Compra>> =>
    pedir(`/api/v1/purchases/${encodeURIComponent(id)}/void`, { method: "POST", body: JSON.stringify({ reason: motivo }) }, comoCompraEn("purchase")),

  pagar: (id: string, datos: DatosCobro, clave: string): Promise<Resultado<Compra>> =>
    pedir(`/api/v1/purchases/${encodeURIComponent(id)}/payments`, { method: "POST", body: JSON.stringify(datos), headers: conClave(clave) }, comoCompraEn("purchase")),

  /** `D-05` paso 2: un pago sin factura pasa a ser de esta. */
  asignarPago: (id: string, pagoId: string): Promise<Resultado<Compra>> =>
    pedir(`/api/v1/purchases/${encodeURIComponent(id)}/assign-payment`, { method: "POST", body: JSON.stringify({ paymentId: pagoId }) }, comoCompraEn("purchase")),

  /** La foto o el PDF, tal cual, como el logotipo: `octet-stream` es lo que el guard CSRF admite. */
  subirAdjunto: (id: string, fichero: File): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/purchases/${encodeURIComponent(id)}/attachment`,
      { method: "PUT", body: fichero, headers: { "content-type": "application/octet-stream", "x-nombre-archivo": nombreEnCabecera(fichero.name) } },
      (cuerpo) => (objeto(objeto(cuerpo)?.attachment) === null ? null : true),
    ),

  quitarAdjunto: (id: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/purchases/${encodeURIComponent(id)}/attachment`, { method: "DELETE" }, (cuerpo) => (objeto(cuerpo) === null ? null : true)),

  /** Se DESCARGA siempre, nunca se abre aquí (`D-07`): la API lo sirve como `attachment`. */
  descargarAdjunto: async (id: string): Promise<Resultado<FicheroDescargado>> => {
    let res: Response;
    try {
      res = await fetch(`/api/v1/purchases/${encodeURIComponent(id)}/attachment`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), "factura");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },
};

/*
 * Presupuestos de proveedor — `OD-0055` (ACCEPTED 2026-10-22). Una ANOTACIÓN
 * del presupuesto que el proveedor envía: no es fiscal ni va al libro de
 * recibidas. Sus cifras (facturado, pagado, por facturar, por pagar) y su
 * estado llegan de la API; esta web no suma nada (`URS §129`).
 */

export const ESTADOS_LISTA_PRESUPUESTOS_PROVEEDOR = ["ALL", "PENDING", "ACCEPTED", "PARTIALLY_INVOICED", "COMPLETED", "CANCELLED"] as const;

export type FilaPresupuestoProveedor = {
  readonly id: string;
  readonly quoteNumber: string;
  readonly supplierQuoteNumber: string | null;
  readonly supplierId: string;
  readonly supplierName: string;
  readonly supplierTaxId: string;
  readonly issueDate: string;
  readonly validUntil: string | null;
  readonly reference: string | null;
  readonly total: string;
  readonly payable: string;
  readonly invoiced: string;
  readonly paid: string;
  readonly toInvoice: string;
  readonly toPay: string;
  readonly state: EstadoPresupuestoProveedor;
  readonly hasAttachment: boolean;
  readonly lateUnbilledPayments: number;
};

export type PresupuestoProveedor = FilaPresupuestoProveedor & {
  readonly supplier: Proveedor;
  readonly lines: readonly LineaCompra[];
  readonly withholdingRate: string | null;
  readonly withholding: string;
  readonly base: string;
  readonly vat: string;
  readonly status: "PENDING" | "ACCEPTED" | "CANCELLED";
  readonly closed: boolean;
  readonly cancelReason: string | null;
  readonly updatedAt: string;
  readonly overInvoiced: boolean;
  readonly purchases: readonly FilaCompra[];
  readonly payments: readonly PagoProveedor[];
  readonly unbilledPayments: readonly PagoSinFactura[];
  readonly attachment: AdjuntoCompra | null;
  readonly writable: boolean;
  readonly bloqueo: BloqueoCompras;
  readonly actions: {
    readonly accept: boolean;
    readonly cancel: boolean;
    readonly close: boolean;
    readonly reopen: boolean;
    readonly changeSupplier: boolean;
    readonly addPurchase: boolean;
  };
};

export type DatosPresupuestoProveedor = {
  readonly supplierQuoteNumber: string | null;
  readonly issueDate: string;
  readonly validUntil: string | null;
  readonly reference: string | null;
  readonly lines: readonly DatosLineaCompra[];
  readonly withholdingRate: string | null;
  readonly withholding: string | null;
};

/** El proveedor, uno que ya está (`supplierId`) o uno nuevo (`supplier`). Al corregir, solo `supplierId`. */
export type AltaPresupuestoProveedor = DatosPresupuestoProveedor & {
  readonly supplierId?: string;
  readonly supplier?: { readonly taxId: string; readonly name: string };
};

export type FiltrosPresupuestosProveedor = {
  readonly estado: (typeof ESTADOS_LISTA_PRESUPUESTOS_PROVEEDOR)[number];
  readonly desde: string | null;
  readonly hasta: string | null;
  /** Los dos números, el proveedor, su NIF y la referencia. */
  readonly texto: string;
  /** Solo los que tienen pagos sin factura fuera de plazo (`OD-0054 D-05`). */
  readonly tarde: boolean;
};

export type ListaPresupuestosProveedor = {
  readonly filas: readonly FilaPresupuestoProveedor[];
  readonly writable: boolean;
  readonly bloqueo: BloqueoCompras;
};

export type AccionPresupuestoProveedor = "accept" | "cancel" | "close" | "reopen";

function comoFilaPresupuestoProveedor(valor: unknown): FilaPresupuestoProveedor | null {
  const v = objeto(valor);
  if (v === null || !textos(v, ["id", "quoteNumber", "supplierId", "supplierName", "supplierTaxId", "issueDate", "total", "payable", "invoiced", "paid", "toInvoice", "toPay"])) {
    return null;
  }
  if (!textoONulo(v.supplierQuoteNumber) || !textoONulo(v.validUntil) || !textoONulo(v.reference)) return null;
  if (!(ESTADOS_PRESUPUESTO_PROVEEDOR as readonly unknown[]).includes(v.state) || typeof v.hasAttachment !== "boolean" || typeof v.lateUnbilledPayments !== "number") return null;
  return v as unknown as FilaPresupuestoProveedor;
}

function comoPresupuestoProveedor(valor: unknown): PresupuestoProveedor | null {
  const v = objeto(valor);
  if (v === null || comoFilaPresupuestoProveedor(v) === null || !textos(v, ["withholding", "base", "vat", "updatedAt"])) return null;
  if (comoProveedor(v.supplier) === null || typeof v.writable !== "boolean" || typeof v.closed !== "boolean" || typeof v.overInvoiced !== "boolean") return null;
  if (v.status !== "PENDING" && v.status !== "ACCEPTED" && v.status !== "CANCELLED") return null;
  const acciones = objeto(v.actions);
  if (acciones === null || !["accept", "cancel", "close", "reopen", "changeSupplier", "addPurchase"].every((k) => typeof acciones[k] === "boolean")) return null;
  const lineas = comoLista(v.lines, (x) => {
    const l = objeto(x);
    return l !== null && TRATAMIENTOS_IVA.includes(String(l.vatTreatment)) && textos(l, ["base", "vat"]) && textoONulo(l.vatRate) ? (l as unknown as LineaCompra) : null;
  });
  const adjunto = v.attachment === null ? null : objeto(v.attachment);
  if (v.attachment !== null && (adjunto === null || !textos(adjunto, ["fileId", "originalName", "contentType", "tamanoLegible", "uploadedAt"]))) return null;
  for (const lista of [lineas, comoLista(v.purchases, comoFilaCompra), comoLista(v.payments, comoPagoProveedor), comoLista(v.unbilledPayments, comoPagoSinFactura)]) {
    if (lista === null) return null;
  }
  return { ...(v as unknown as PresupuestoProveedor), bloqueo: comoBloqueo(v.writeBlockedBy) };
}

const comoPresupuestoProveedorEn =
  (clave: string) =>
  (cuerpo: unknown): PresupuestoProveedor | null =>
    comoPresupuestoProveedor(objeto(cuerpo)?.[clave]);

export const apiPresupuestosProveedor = {
  listar: (f: FiltrosPresupuestosProveedor): Promise<Resultado<ListaPresupuestosProveedor>> => {
    const p = new URLSearchParams({ estado: f.estado });
    if (f.texto.trim() !== "") p.set("q", f.texto.trim());
    if (f.desde !== null) p.set("desde", f.desde);
    if (f.hasta !== null) p.set("hasta", f.hasta);
    if (f.tarde) p.set("tarde", "1");
    return pedir(`/api/v1/supplier-quotes?${p.toString()}`, { method: "GET" }, (cuerpo) => {
      const b = objeto(cuerpo);
      const filas = comoLista(b?.rows, comoFilaPresupuestoProveedor);
      return b === null || filas === null || typeof b.writable !== "boolean" ? null : { filas, writable: b.writable, bloqueo: comoBloqueo(b.writeBlockedBy) };
    });
  },

  /** Totales y avisos mientras se escribe, sin guardar. */
  calcular: (datos: Partial<AltaPresupuestoProveedor>): Promise<Resultado<CalculoCompra>> =>
    pedir("/api/v1/supplier-quotes/calculate", { method: "POST", body: JSON.stringify(datos) }, comoCalculoCompra),

  anotar: (datos: AltaPresupuestoProveedor, clave: string): Promise<Resultado<{ readonly presupuesto: PresupuestoProveedor; readonly avisos: readonly AvisoCompra[] }>> =>
    pedir("/api/v1/supplier-quotes", { method: "POST", body: JSON.stringify(datos), headers: conClave(clave) }, (cuerpo) => {
      const presupuesto = comoPresupuestoProveedor(objeto(cuerpo)?.quote);
      return presupuesto === null ? null : { presupuesto, avisos: comoAvisosCompra(objeto(cuerpo)?.warnings) };
    }),

  obtener: (id: string): Promise<Resultado<PresupuestoProveedor>> =>
    pedir(`/api/v1/supplier-quotes/${encodeURIComponent(id)}`, { method: "GET" }, comoPresupuestoProveedor),

  /** `updatedAt` es el que se leyó (`STALE` si otro lo cambió). El proveedor cambia solo sin nada enlazado. */
  corregir: (
    id: string,
    datos: DatosPresupuestoProveedor & { readonly supplierId: string },
    updatedAt: string,
  ): Promise<Resultado<{ readonly presupuesto: PresupuestoProveedor; readonly avisos: readonly AvisoCompra[] }>> =>
    pedir(`/api/v1/supplier-quotes/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ ...datos, updatedAt }) }, (cuerpo) => {
      const presupuesto = comoPresupuestoProveedor(objeto(cuerpo)?.quote);
      return presupuesto === null ? null : { presupuesto, avisos: comoAvisosCompra(objeto(cuerpo)?.warnings) };
    }),

  /** Aceptar, cancelar (con motivo), cerrar a mano y deshacer el cierre (`OD-0055 D-03`). */
  accion: (id: string, accion: AccionPresupuestoProveedor, motivo: string | null = null): Promise<Resultado<PresupuestoProveedor>> =>
    pedir(
      `/api/v1/supplier-quotes/${encodeURIComponent(id)}/${accion}`,
      { method: "POST", body: JSON.stringify(motivo === null ? {} : { reason: motivo }) },
      comoPresupuestoProveedorEn("quote"),
    ),

  subirAdjunto: (id: string, fichero: File): Promise<Resultado<true>> =>
    pedir(
      `/api/v1/supplier-quotes/${encodeURIComponent(id)}/attachment`,
      { method: "PUT", body: fichero, headers: { "content-type": "application/octet-stream", "x-nombre-archivo": nombreEnCabecera(fichero.name) } },
      (cuerpo) => (objeto(objeto(cuerpo)?.attachment) === null ? null : true),
    ),

  quitarAdjunto: (id: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/supplier-quotes/${encodeURIComponent(id)}/attachment`, { method: "DELETE" }, (cuerpo) => (objeto(cuerpo) === null ? null : true)),

  /** Se DESCARGA, nunca se abre aquí (`OD-0055 D-07`, como el de una compra). */
  descargarAdjunto: async (id: string): Promise<Resultado<FicheroDescargado>> => {
    let res: Response;
    try {
      res = await fetch(`/api/v1/supplier-quotes/${encodeURIComponent(id)}/attachment`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), "presupuesto");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },
};

// ------------------------------------------------------------------------------
// Importar desde Excel — OD-0057 (ACCEPTED 2026-10-24)
// ------------------------------------------------------------------------------

export type TipoImportacion = "CUSTOMERS" | "PRODUCTS" | "SUPPLIERS" | "EXTERNAL_INVOICES";
const TIPOS_IMPORTACION: readonly string[] = ["CUSTOMERS", "PRODUCTS", "SUPPLIERS", "EXTERNAL_INVOICES"];

/** Una columna de la plantilla, con los nombres que la avanzada reconoce. La manda la API. */
export type ColumnaImportacion = {
  readonly key: string;
  readonly header: string;
  readonly required: boolean;
  readonly example: string | null;
  readonly line?: boolean;
  readonly aliases: readonly string[];
};

export type LimitesImportacion = {
  readonly nivel: "MINIMAL" | "ADVANCED";
  readonly tipos: readonly TipoImportacion[];
  readonly maxFilas: number;
  readonly maxBytes: number;
  readonly alMes: number;
  readonly usadasEsteMes: number;
  readonly vuelveEl: string;
  readonly puedeDeshacer: boolean;
  readonly filasPorPeticion: number;
  readonly columnas: Readonly<Partial<Record<TipoImportacion, readonly ColumnaImportacion[]>>>;
};

export type Importacion = {
  readonly id: string;
  readonly tipo: TipoImportacion;
  readonly nivel: string;
  readonly fichero: string;
  readonly filas: number;
  readonly estado: "OPEN" | "DONE" | "UNDONE";
  readonly creadas: number;
  readonly duplicadas: number;
  readonly rechazadas: number;
  readonly creadaEl: string;
  readonly deshechaEl: string | null;
};

export type ResultadoFila = {
  readonly fila: number;
  readonly resultado: "OK" | "CREATED" | "DUPLICATE" | "REJECTED";
  readonly existente: string | null;
  readonly campos: Readonly<Record<string, string>>;
};

export type FacturaImportada = {
  readonly id: string;
  readonly numero: string;
  readonly fecha: string;
  readonly fechaOperacion: string | null;
  readonly tipo: string;
  readonly clienteId: string | null;
  readonly cliente: string | null;
  readonly nifCliente: string | null;
  readonly programa: string | null;
  readonly base: string;
  readonly iva: string;
  readonly recargo: string;
  readonly retencion: string;
  readonly total: string;
  readonly importadaEl: string;
};

export type LineaFacturaImportada = {
  readonly base: string;
  readonly tipoIva: string;
  readonly iva: string;
  readonly tipoRecargo: string | null;
  readonly recargo: string;
};

function comoColumna(valor: unknown): ColumnaImportacion | null {
  const v = objeto(valor);
  if (v === null || typeof v.key !== "string" || typeof v.header !== "string" || typeof v.required !== "boolean" || !textoONulo(v.example)) return null;
  const aliases = Array.isArray(v.aliases) ? v.aliases.filter((x): x is string => typeof x === "string") : [];
  return { key: v.key, header: v.header, required: v.required, example: v.example as string | null, line: v.line === true, aliases };
}

function comoLimites(valor: unknown): LimitesImportacion | null {
  const v = objeto(valor);
  if (v === null || (v.level !== "MINIMAL" && v.level !== "ADVANCED") || !Array.isArray(v.kinds)) return null;
  const numeros = ["maxRows", "maxFileBytes", "perMonth", "usedThisMonth", "maxItemsPerRequest"];
  if (!numeros.every((k) => typeof v[k] === "number") || typeof v.resetsOn !== "string") return null;
  const columnas: Partial<Record<TipoImportacion, readonly ColumnaImportacion[]>> = {};
  const c = objeto(v.columns) ?? {};
  for (const [k, lista] of Object.entries(c)) {
    if (!TIPOS_IMPORTACION.includes(k)) continue;
    const l = comoLista(lista, comoColumna);
    if (l !== null) columnas[k as TipoImportacion] = l;
  }
  return {
    nivel: v.level,
    tipos: v.kinds.filter((x): x is TipoImportacion => typeof x === "string" && TIPOS_IMPORTACION.includes(x)),
    maxFilas: v.maxRows as number,
    maxBytes: v.maxFileBytes as number,
    alMes: v.perMonth as number,
    usadasEsteMes: v.usedThisMonth as number,
    vuelveEl: v.resetsOn,
    // Falla cerrado: si la API no lo afirma, no se ofrece deshacer.
    puedeDeshacer: v.canUndo === true,
    filasPorPeticion: v.maxItemsPerRequest as number,
    columnas,
  };
}

function comoImportacion(valor: unknown): Importacion | null {
  const v = objeto(valor);
  if (v === null || !textos(v, ["id", "kind", "level", "fileName", "status", "createdAt"]) || !TIPOS_IMPORTACION.includes(v.kind as string)) return null;
  if (!["totalRows", "createdCount", "duplicateCount", "rejectedCount"].every((k) => typeof v[k] === "number") || !textoONulo(v.undoneAt)) return null;
  if (v.status !== "OPEN" && v.status !== "DONE" && v.status !== "UNDONE") return null;
  return {
    id: v.id as string,
    tipo: v.kind as TipoImportacion,
    nivel: v.level as string,
    fichero: v.fileName as string,
    filas: v.totalRows as number,
    estado: v.status,
    creadas: v.createdCount as number,
    duplicadas: v.duplicateCount as number,
    rechazadas: v.rejectedCount as number,
    creadaEl: v.createdAt as string,
    deshechaEl: v.undoneAt as string | null,
  };
}

function comoResultadoFila(valor: unknown): ResultadoFila | null {
  const v = objeto(valor);
  if (v === null || typeof v.row !== "number" || !["OK", "CREATED", "DUPLICATE", "REJECTED"].includes(v.outcome as string)) return null;
  const campos: Record<string, string> = {};
  for (const [k, x] of Object.entries(objeto(v.fields) ?? {})) if (typeof x === "string") campos[k] = x;
  return {
    fila: v.row,
    resultado: v.outcome as ResultadoFila["resultado"],
    existente: typeof v.existingId === "string" && v.existingId !== "" ? v.existingId : null,
    campos,
  };
}

function comoFacturaImportada(valor: unknown): FacturaImportada | null {
  const v = objeto(valor);
  if (v === null || !textos(v, ["id", "number", "issueDate", "kind", "base", "vat", "surcharge", "withholding", "total", "importedAt"])) return null;
  if (![v.operationDate, v.customerId, v.customerName, v.customerTaxId, v.sourceProgram].every(textoONulo)) return null;
  return {
    id: v.id as string,
    numero: v.number as string,
    fecha: v.issueDate as string,
    fechaOperacion: v.operationDate as string | null,
    tipo: v.kind as string,
    clienteId: v.customerId as string | null,
    cliente: v.customerName as string | null,
    nifCliente: v.customerTaxId as string | null,
    programa: v.sourceProgram as string | null,
    base: v.base as string,
    iva: v.vat as string,
    recargo: v.surcharge as string,
    retencion: v.withholding as string,
    total: v.total as string,
    importadaEl: v.importedAt as string,
  };
}

function comoLineaImportada(valor: unknown): LineaFacturaImportada | null {
  const v = objeto(valor);
  if (v === null || !textos(v, ["base", "vatRate", "vat", "surcharge"]) || !textoONulo(v.surchargeRate)) return null;
  return { base: v.base as string, tipoIva: v.vatRate as string, iva: v.vat as string, tipoRecargo: v.surchargeRate as string | null, recargo: v.surcharge as string };
}

const comoResultados = (cuerpo: unknown): readonly ResultadoFila[] | null => comoLista(objeto(cuerpo)?.results, comoResultadoFila);

export const apiImportacion = {
  estado: (): Promise<Resultado<{ readonly importaciones: readonly Importacion[]; readonly limites: LimitesImportacion }>> =>
    pedir("/api/v1/imports", { method: "GET" }, (cuerpo) => {
      const b = objeto(cuerpo);
      const importaciones = comoLista(b?.imports, comoImportacion);
      const limites = comoLimites(b?.limits);
      return importaciones === null || limites === null ? null : { importaciones, limites };
    }),

  /** Vista previa: la API comprueba las filas SIN escribir nada. */
  comprobar: (tipo: TipoImportacion, items: readonly unknown[]): Promise<Resultado<{ readonly resultados: readonly ResultadoFila[]; readonly libres: number | null }>> =>
    pedir("/api/v1/imports/check", { method: "POST", body: JSON.stringify({ kind: tipo, items }) }, (cuerpo) => {
      const resultados = comoResultados(cuerpo);
      const libres = objeto(cuerpo)?.available;
      return resultados === null ? null : { resultados, libres: typeof libres === "number" ? libres : null };
    }),

  /** Con la misma clave, repetir devuelve la misma importación. */
  empezar: (datos: { readonly kind: TipoImportacion; readonly fileName: string; readonly totalRows: number; readonly fileBytes: number }, clave: string): Promise<Resultado<Importacion>> =>
    pedir("/api/v1/imports", { method: "POST", body: JSON.stringify(datos), headers: conClave(clave) }, (cuerpo) => comoImportacion(objeto(cuerpo)?.import)),

  enviarFilas: (id: string, items: readonly unknown[]): Promise<Resultado<readonly ResultadoFila[]>> =>
    pedir(`/api/v1/imports/${encodeURIComponent(id)}/rows`, { method: "POST", body: JSON.stringify({ items }) }, comoResultados),

  terminar: (id: string, cuentas: { readonly duplicates: number; readonly rejected: number }): Promise<Resultado<Importacion>> =>
    pedir(`/api/v1/imports/${encodeURIComponent(id)}/finish`, { method: "POST", body: JSON.stringify(cuentas) }, (cuerpo) => comoImportacion(objeto(cuerpo)?.import)),

  deshacer: (id: string): Promise<Resultado<{ readonly importacion: Importacion; readonly quitadas: number; readonly seQuedan: number }>> =>
    pedir(`/api/v1/imports/${encodeURIComponent(id)}/undo`, { method: "POST", body: JSON.stringify({}) }, (cuerpo) => {
      const b = objeto(cuerpo);
      const importacion = comoImportacion(b?.import);
      return importacion === null || typeof b?.removed !== "number" || typeof b?.kept !== "number" ? null : { importacion, quitadas: b.removed, seQuedan: b.kept };
    }),

  descargarPlantilla: async (tipo: TipoImportacion): Promise<Resultado<FicheroDescargado>> => {
    let res: Response;
    try {
      res = await fetch(`/api/v1/imports/template/${encodeURIComponent(tipo)}`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    const nombre = nombreDeDescarga(res.headers.get("content-disposition"), "plantilla.xlsx");
    return { ok: true, datos: { contenido: await res.blob(), nombre } };
  },

  facturas: (filtro: { readonly q: string; readonly cliente: string | null }): Promise<Resultado<{ readonly facturas: readonly FacturaImportada[]; readonly total: string }>> => {
    const p = new URLSearchParams();
    if (filtro.q.trim() !== "") p.set("q", filtro.q.trim());
    if (filtro.cliente !== null) p.set("cliente", filtro.cliente);
    return pedir(`/api/v1/external-invoices?${p.toString()}`, { method: "GET" }, (cuerpo) => {
      const b = objeto(cuerpo);
      const facturas = comoLista(b?.invoices, comoFacturaImportada);
      return facturas === null || typeof b?.total !== "string" ? null : { facturas, total: b.total };
    });
  },

  factura: (id: string): Promise<Resultado<{ readonly factura: FacturaImportada; readonly lineas: readonly LineaFacturaImportada[] }>> =>
    pedir(`/api/v1/external-invoices/${encodeURIComponent(id)}`, { method: "GET" }, (cuerpo) => {
      const b = objeto(cuerpo);
      const factura = comoFacturaImportada(b?.invoice);
      const lineas = comoLista(b?.lines, comoLineaImportada);
      return factura === null || lineas === null ? null : { factura, lineas };
    }),
};

/* ===================================================================== equipo */

/**
 * El equipo de la organización — `OD-0060` D-03, D-04, D-06 y la enmienda E-01
 * de `TDS-FREE-DOWNGRADE` §3.
 *
 * Todo lo decide la API: cuántos asientos hay, quién ocupa uno y qué puede
 * hacer quien mira con cada persona (`can`). La pantalla no cuenta asientos ni
 * repite las reglas de D-06: enseña lo que llega y la API lo vuelve a
 * comprobar en cada acción.
 */
export type RolDelEquipo = "ADMIN" | "SALES" | "BILLING" | "READ_ONLY";

export const ROLES_DEL_EQUIPO: readonly RolDelEquipo[] = ["ADMIN", "SALES", "BILLING", "READ_ONLY"];

export type AsientosDelEquipo = {
  /** Si el plan admite usuarios adicionales (Factura+ y Business). */
  readonly admiteAdicionales: boolean;
  readonly maximo: number;
  readonly ocupados: number;
  readonly libres: number;
  /** El techo de `OD-0060` D-02. */
  readonly techo: number;
};

export type MiembroDelEquipo = {
  readonly id: string;
  readonly email: string | null;
  /** El rol tal como llega; uno que la web no conoce se enseña sin traducir. */
  readonly rol: string;
  readonly desactivadoPorPlan: boolean;
  readonly eresTu: boolean;
  readonly puede: { readonly cambiarRol: boolean; readonly quitar: boolean; readonly reactivar: boolean };
};

export type InvitacionDelEquipo = {
  readonly id: string;
  readonly email: string;
  readonly rol: string;
  readonly caduca: string;
  readonly puedeReenviar: boolean;
};

export type Equipo = {
  readonly asientos: AsientosDelEquipo;
  /** `OD-0060` D-05: si quien mira puede comprar un usuario adicional ahora. Lo decide la API. */
  readonly puedeComprarAsiento: boolean;
  /** La enmienda de D-05: si puede quitar del plan un usuario LIBRE ahora. Lo decide la API. */
  readonly puedeQuitarAsiento: boolean;
  readonly miembros: readonly MiembroDelEquipo[];
  readonly invitaciones: readonly InvitacionDelEquipo[];
};

/** Lo que costará un usuario adicional, antes de pedirlo (D-05). */
export type OfertaDeAsiento = {
  /** «22,99»: el precio anual con el IVA dentro, como lo da Stripe. Texto, nunca número. */
  readonly precio: string;
  /** Hasta cuándo se prorratea hoy (ISO), si se sabe. */
  readonly renueva: string | null;
  readonly asientosDespues: number;
};

function comoAsientos(v: unknown): AsientosDelEquipo | null {
  const a = objeto(v);
  if (a === null) return null;
  const numeros = ["maximum", "occupied", "free", "ceiling"].every((k) => Number.isInteger(a[k]));
  if (!numeros || typeof a.allowsAdditional !== "boolean") return null;
  return {
    admiteAdicionales: a.allowsAdditional,
    maximo: a.maximum as number,
    ocupados: a.occupied as number,
    libres: a.free as number,
    techo: a.ceiling as number,
  };
}

function comoMiembro(v: unknown): MiembroDelEquipo | null {
  const m = objeto(v);
  if (m === null || typeof m.membershipId !== "string" || typeof m.role !== "string" || typeof m.status !== "string") return null;
  const can = objeto(m.can);
  return {
    id: m.membershipId,
    email: typeof m.email === "string" ? m.email : null,
    rol: m.role,
    desactivadoPorPlan: m.status === "DEACTIVATED_BY_PLAN",
    eresTu: m.isYou === true,
    // Falla cerrado: lo que la API no afirma, la pantalla no lo ofrece.
    puede: { cambiarRol: can?.changeRole === true, quitar: can?.remove === true, reactivar: can?.reactivate === true },
  };
}

function comoInvitacion(v: unknown): InvitacionDelEquipo | null {
  const i = objeto(v);
  if (i === null || typeof i.id !== "string" || typeof i.email !== "string" || typeof i.role !== "string" || typeof i.expiresAt !== "string") {
    return null;
  }
  return { id: i.id, email: i.email, rol: i.role, caduca: i.expiresAt, puedeReenviar: i.canResend === true };
}

function comoEquipo(cuerpo: unknown): Equipo | null {
  const b = objeto(cuerpo);
  const asientos = comoAsientos(b?.seats);
  const miembros = comoLista(b?.members, comoMiembro);
  const invitaciones = comoLista(b?.invitations, comoInvitacion);
  return asientos === null || miembros === null || invitaciones === null
    ? null
    : { asientos, puedeComprarAsiento: b?.canBuySeat === true, puedeQuitarAsiento: b?.canReduceSeat === true, miembros, invitaciones };
}

function comoOferta(cuerpo: unknown): OfertaDeAsiento | null {
  const b = objeto(cuerpo);
  if (b === null || typeof b.price !== "string" || !/^\d+,\d{2}$/.test(b.price) || !Number.isInteger(b.paidSeatsAfter)) return null;
  return {
    precio: b.price,
    renueva: typeof b.renewsAt === "string" ? b.renewsAt : null,
    asientosDespues: b.paidSeatsAfter as number,
  };
}

/** `true` si el banco tiene que confirmar el pago antes de que el usuario cuente. */
function comoCompraDeAsiento(cuerpo: unknown): { readonly pagoPendiente: boolean } | null {
  const b = objeto(cuerpo);
  if (b?.status === "PROCESSING") return { pagoPendiente: false };
  if (b?.status === "PAYMENT_PENDING") return { pagoPendiente: true };
  return null;
}

/** `false` si la invitación está creada pero el correo no salió: se puede reenviar. */
function comoInvitada(cuerpo: unknown): { readonly correoEnviado: boolean } | null {
  const b = objeto(cuerpo);
  return b !== null && (b.status === "INVITED" || b.status === "RESENT") ? { correoEnviado: b.emailSent !== false } : null;
}

const tramo = (id: string): string => encodeURIComponent(id);

/** Una de las empresas de quien tiene la sesión (`OD-0060` D-04). */
export type EmpresaDeLaSesion = { readonly id: string; readonly nombre: string; readonly activa: boolean };

function comoEmpresa(v: unknown): EmpresaDeLaSesion | null {
  const o = objeto(v);
  return o !== null && typeof o.organizationId === "string" && typeof o.name === "string"
    ? { id: o.organizationId, nombre: o.name, activa: o.current === true }
    : null;
}

/**
 * El selector de empresa. Las dos rutas van con la sesión y nada más: sirven
 * también a quien no puede entrar en la empresa activa.
 */
export const apiEmpresas = {
  listar: (): Promise<Resultado<readonly EmpresaDeLaSesion[]>> =>
    pedir("/api/v1/me/organizations", { method: "GET" }, (cuerpo) => comoLista(objeto(cuerpo)?.organizations, comoEmpresa)),

  /** Un 404 es una empresa en la que ya no se puede entrar (o que no es tuya): la API no distingue. */
  cambiar: (id: string): Promise<Resultado<{ readonly cambiada: true }>> =>
    pedir("/api/v1/me/organization", { method: "POST", body: JSON.stringify({ organizationId: id }) }, (cuerpo) =>
      objeto(cuerpo)?.status === "SWITCHED" ? { cambiada: true } : null,
    ),
};

export const apiEquipo = {
  /** Exige users.manage: un 403 es la respuesta normal para quien no lo tiene. */
  ver: (): Promise<Resultado<Equipo>> => pedir("/api/v1/team", { method: "GET" }, comoEquipo),

  invitar: (email: string, rol: RolDelEquipo): Promise<Resultado<{ readonly correoEnviado: boolean }>> =>
    pedir("/api/v1/team/invitations", { method: "POST", body: JSON.stringify({ email, role: rol }) }, comoInvitada),

  reenviar: (id: string): Promise<Resultado<{ readonly correoEnviado: boolean }>> =>
    pedir(`/api/v1/team/invitations/${tramo(id)}/resend`, { method: "POST", body: JSON.stringify({}) }, comoInvitada),

  revocar: (id: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/team/invitations/${tramo(id)}/revoke`, { method: "POST", body: JSON.stringify({}) }, estadoEs("REVOKED")),

  cambiarRol: (id: string, rol: RolDelEquipo): Promise<Resultado<true>> =>
    pedir(`/api/v1/team/members/${tramo(id)}/role`, { method: "POST", body: JSON.stringify({ role: rol }) }, (c) =>
      estadoEs("ROLE_CHANGED")(c) ?? estadoEs("UNCHANGED")(c),
    ),

  quitar: (id: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/team/members/${tramo(id)}/remove`, { method: "POST", body: JSON.stringify({}) }, estadoEs("REMOVED")),

  reactivar: (id: string): Promise<Resultado<true>> =>
    pedir(`/api/v1/team/members/${tramo(id)}/reactivate`, { method: "POST", body: JSON.stringify({}) }, estadoEs("REACTIVATED")),

  /** D-05: el precio de un usuario más. Solo lee. */
  ofertaAsiento: (): Promise<Resultado<OfertaDeAsiento>> => pedir("/api/v1/team/seats/offer", { method: "GET" }, comoOferta),

  /** D-05: un usuario más en el plan. El asiento aparece libre cuando el pago se confirma. */
  comprarAsiento: (): Promise<Resultado<{ readonly pagoPendiente: boolean }>> =>
    pedir("/api/v1/team/seats/purchase", { method: "POST", body: JSON.stringify({}) }, comoCompraDeAsiento),

  /** La enmienda de D-05: un usuario LIBRE menos en el plan, en el acto y sin abono. */
  quitarAsiento: (): Promise<Resultado<{ readonly quitado: true }>> =>
    pedir("/api/v1/team/seats/reduce", { method: "POST", body: JSON.stringify({}) }, (cuerpo) =>
      objeto(cuerpo)?.status === "REDUCED" ? { quitado: true } : null,
    ),

  /**
   * Aceptar el enlace de `E-18`. La organización la decide la invitación, no
   * la web; al aceptar, la API deja la sesión en esa empresa.
   */
  aceptar: (token: string): Promise<Resultado<true>> =>
    pedir("/api/v1/invitations/accept", { method: "POST", body: JSON.stringify({ token }) }, estadoEs("ACCEPTED")),
};

// --- El panel de la gestoría — OD-0063 D-06 y ADR-0025 ---

/** Una persona de la gestoría que lleva la empresa (D-03). Sin correo si ya no está en `GLOBAL_DB`. */
export type PersonaDeLaFila = { readonly userId: string; readonly email: string | null };

/** Una fila de `GET /api/v1/asesoria/panel`: de `GLOBAL_DB`, sin un dato de la empresa. */
export type FilaDelPanel = {
  readonly organizationId: string;
  readonly nombre: string | null;
  readonly plan: string | null;
  /** Fuera de Business la fila sale en gris, sin datos (D-06 SIN ACCESO). */
  readonly enBusiness: boolean;
  readonly personas: readonly PersonaDeLaFila[];
  /** Si quien mira la lleva: solo entonces la API le da el resumen. */
  readonly llevasTu: boolean;
};

export type PanelDeAsesoria = {
  readonly perfilActivo: boolean;
  readonly gestiona: boolean;
  /** Los que se pueden pedir, el en curso primero. Los calcula la API con el día de Madrid. */
  readonly trimestres: readonly string[];
  readonly clientes: readonly FilaDelPanel[];
};

/** `GET /api/v1/asesoria/clientes/:id/resumen`: del shard de ESA empresa, y de nada más. */
export type ResumenDeCliente = {
  readonly organizationId: string;
  readonly empresa: { readonly nombre: string; readonly nif: string } | null;
  readonly trimestre: { readonly etiqueta: string; readonly enCurso: boolean };
  readonly emitidas:
    | { readonly calculado: true; readonly facturas: number; readonly base: string; readonly repercutido: string }
    | { readonly calculado: false; readonly motivo: string };
  readonly soportado:
    | { readonly disponible: true; readonly compras: number; readonly base: string; readonly soportado: string }
    | { readonly disponible: false; readonly motivo: string };
  readonly aeat: { readonly rechazados: number; readonly pendientes: number };
  readonly ultimaDescarga: { readonly en: string; readonly userId: string | null; readonly formato: string | null } | null;
  /** `OD-0067` D-03: lo que el cliente le dice de ese trimestre. `null` si nada. */
  readonly comentario: string | null;
  /** `OD-0067` D-05: lo que la empresa ha escrito y quien mira no ha leído. `null` sin conversación. */
  readonly noLeidos: number | null;
};

/** Lo que se descarga de cada empresa desde el panel: el Excel completo o el ZIP con los PDF. */
export type DescargaDelPanel = "completo.xlsx" | "facturas.zip";

function comoPersonaDeLaFila(v: unknown): PersonaDeLaFila | null {
  const o = objeto(v);
  return o !== null && typeof o.userId === "string" && textoONulo(o.email) ? { userId: o.userId, email: (o.email as string | null) ?? null } : null;
}

function comoFilaDelPanel(v: unknown): FilaDelPanel | null {
  const o = objeto(v);
  if (o === null || typeof o.organizationId !== "string" || !textoONulo(o.nombre) || !textoONulo(o.plan)) return null;
  if (typeof o.enBusiness !== "boolean" || typeof o.llevasTu !== "boolean") return null;
  const personas = comoLista(o.personas, comoPersonaDeLaFila);
  if (personas === null) return null;
  return {
    organizationId: o.organizationId,
    nombre: (o.nombre as string | null) ?? null,
    plan: (o.plan as string | null) ?? null,
    enBusiness: o.enBusiness,
    personas,
    llevasTu: o.llevasTu,
  };
}

function comoPanelDeAsesoria(cuerpo: unknown): PanelDeAsesoria | null {
  const o = objeto(cuerpo);
  if (o === null || typeof o.perfilActivo !== "boolean" || typeof o.gestiona !== "boolean") return null;
  const trimestres = Array.isArray(o.trimestres) && o.trimestres.every((x) => typeof x === "string") ? (o.trimestres as string[]) : null;
  const clientes = comoLista(o.clientes, comoFilaDelPanel);
  return trimestres === null || clientes === null ? null : { perfilActivo: o.perfilActivo, gestiona: o.gestiona, trimestres, clientes };
}

const esCifra = (v: unknown): v is string => typeof v === "string";

function comoResumenDeCliente(cuerpo: unknown): ResumenDeCliente | null {
  const r = objeto(objeto(cuerpo)?.resumen);
  if (r === null || typeof r.organizationId !== "string") return null;
  const e = r.empresa === null ? null : objeto(r.empresa);
  const empresa = e !== null && typeof e.nombre === "string" && typeof e.nif === "string" ? { nombre: e.nombre, nif: e.nif } : null;
  if (r.empresa !== null && empresa === null) return null;
  const t = objeto(r.trimestre);
  if (t === null || typeof t.etiqueta !== "string" || typeof t.enCurso !== "boolean") return null;
  const em = objeto(r.emitidas);
  const emitidas: ResumenDeCliente["emitidas"] | null =
    em?.calculado === true && typeof em.facturas === "number" && esCifra(em.base) && esCifra(em.repercutido)
      ? { calculado: true, facturas: em.facturas, base: em.base, repercutido: em.repercutido }
      : em?.calculado === false && typeof em.motivo === "string"
        ? { calculado: false, motivo: em.motivo }
        : null;
  const so = objeto(r.soportado);
  const soportado: ResumenDeCliente["soportado"] | null =
    so?.disponible === true && typeof so.compras === "number" && esCifra(so.base) && esCifra(so.soportado)
      ? { disponible: true, compras: so.compras, base: so.base, soportado: so.soportado }
      : so?.disponible === false && typeof so.motivo === "string"
        ? { disponible: false, motivo: so.motivo }
        : null;
  const a = objeto(r.aeat);
  if (emitidas === null || soportado === null || a === null || typeof a.rechazados !== "number" || typeof a.pendientes !== "number") return null;
  const u = r.ultimaDescarga === null ? null : objeto(r.ultimaDescarga);
  if (r.ultimaDescarga !== null && (u === null || typeof u.en !== "string" || !textoONulo(u.userId) || !textoONulo(u.formato))) return null;
  return {
    organizationId: r.organizationId,
    empresa,
    trimestre: { etiqueta: t.etiqueta, enCurso: t.enCurso },
    emitidas,
    soportado,
    aeat: { rechazados: a.rechazados, pendientes: a.pendientes },
    ultimaDescarga: u === null ? null : { en: u.en as string, userId: (u.userId as string | null) ?? null, formato: (u.formato as string | null) ?? null },
    // Una API anterior no lo manda: es «sin comentario», no un error.
    comentario: typeof r.comentario === "string" ? r.comentario : null,
    noLeidos: typeof r.noLeidos === "number" ? r.noLeidos : null,
  };
}

/**
 * El panel de la gestoría. Las filas salen de la gestoría (`GLOBAL_DB`); el
 * resumen y las descargas, de UNA empresa por petición, con la empresa en la
 * ruta (`ADR-0025`). Un 404 en una de esas es lo normal si, desde que se
 * cargaron las filas, la empresa ha dejado Business o ha retirado el permiso:
 * la API no distingue un caso de otro, a propósito.
 */
export const apiPanelAsesoria = {
  filas: (): Promise<Resultado<PanelDeAsesoria>> => pedir("/api/v1/asesoria/panel", { method: "GET" }, comoPanelDeAsesoria),

  resumen: (organizationId: string, trimestre: string): Promise<Resultado<ResumenDeCliente>> =>
    pedir(
      `/api/v1/asesoria/clientes/${tramo(organizationId)}/resumen?${new URLSearchParams({ trimestre }).toString()}`,
      { method: "GET" },
      comoResumenDeCliente,
    ),

  /** La descarga de siempre, con su auditoría en el shard de la empresa. */
  descargar: async (organizationId: string, que: DescargaDelPanel, trimestre: string): Promise<Resultado<FicheroDescargado>> => {
    const q = new URLSearchParams({ trimestre });
    if (que === "facturas.zip") q.set("formato", "xlsx");
    let res: Response;
    try {
      res = await fetch(`/api/v1/asesoria/clientes/${tramo(organizationId)}/${que}?${q.toString()}`, { method: "GET", credentials: "same-origin" });
    } catch {
      return { ok: false, estado: 0, error: null };
    }
    if (!res.ok) return { ok: false, estado: res.status, error: comoError(await cuerpoJson(res)) };
    return { ok: true, datos: { contenido: await res.blob(), nombre: nombreDeDescarga(res.headers.get("content-disposition"), que) } };
  },
};
